import React, { useCallback, useEffect, useRef, useState } from "react";
import AsyncActionButton from "../components/AsyncActionButton";
import AvatarImg from "../components/AvatarImg";
import MapKitFamilyMap from "../components/MapKitFamilyMap";
import HaritaErrorBoundary from "../components/HaritaErrorBoundary";
import { autocompleteMapKitPlaces, searchMapKitPlaces } from "../services/mapKitService";
import AVATAR_IMPORTS from "../assets/avatar/index";
import ReactDOM from "react-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { appAlert, appConfirm } from "../components/AppDialog";
import { API_BASE_URL, apiRequest } from "../services/apiClient";
import {
  yerEkleService,
  yerGuncelleService,
  yerSilService,
  yerleriDinleService,
  konumlariDinleService,
  konumGuncelleService,
  paylasimDurumGuncelleService,
  geofenceEkleService,
  geofenceSilService,
  geofencelariDinleService,
  bulusmaNoktasiKaydetService,
  bulusmaNoktasiSilService,
  bulusmaNoktasiDinleService,
  sosGonderService,
  sosBitirService,
  sosAlertlariDinleService,
  konumGecmisiniDinleService,
  guvenliAlanEkleService,
  guvenliAlanSilService,
  guvenliAlanlariDinleService,
  hareketAnaliziGetirService,
} from "../services/haritaService";
import {
  getCurrentLocationOnce,
  stopNativeLocationSharing,
  setNativeBackgroundLocationSharingEnabled,
  isNativeLocationAvailable,
  hasNativeLocationPermission,
  hasAlwaysPermission,
  setBackgroundLocationContext,
  requestAlwaysLocationUpgrade,
} from "../services/nativeLocationService";
import { isAndroidPlatform, isIOSPlatform } from "../utils/platformFeatures";
import { App as CapacitorApp } from "@capacitor/app";
import { loadPersistentImage } from "../services/persistentImageCache";

/* DÜZELTME: new Date().toISOString().slice(0,10) HER ZAMAN UTC tarihini verir.
   Türkiye UTC+3 olduğu için, TR saatiyle 00:00–03:00 arası bu hâlâ "dün"ün
   tarihini döndürür — yani gece yarısından sonra birkaç saat boyunca "Bugün"
   butonu/varsayılan tarih hatalı şekilde bir önceki günü gösterirdi. Bu
   yardımcı, cihazın YEREL tarih bileşenlerini (getFullYear/getMonth/getDate)
   kullanır — TR kullanıcıların cihazı TR saatinde olduğu için doğru sonucu verir. */
const yerelTarihStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const bugununTarihi = () => yerelTarihStr(new Date());
const dunununTarihi = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return yerelTarihStr(d);
};

const haritaSpeedKmh = (speed) => {
  const n = Number(speed);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n <= 75 ? n * 3.6 : n;
};

const haritaSpeedBadgeValue = (speed) => {
  const kmh = haritaSpeedKmh(speed);
  if (kmh < 20) return null;
  return Math.min(999, Math.max(20, Math.round(kmh)));
};


/* ─── Çocuk Takibi Yer Kategorileri ─── */
const COCUK_KATEGORILER = [
  { key:"okul",      tr:"Okul",      en:"School",         emoji:"🏫", defaultR:300  },
  { key:"kres",      tr:"Kreş",      en:"Daycare",        emoji:"🧸", defaultR:200  },
  { key:"yuzme",     tr:"Yüzme",     en:"Swimming",       emoji:"🏊", defaultR:200  },
  { key:"spor",      tr:"Spor/Kurs", en:"Sports/Class",   emoji:"⚽", defaultR:200  },
  { key:"dershane",  tr:"Dershane",  en:"Tutoring",       emoji:"📚", defaultR:250  },
  { key:"kurs",      tr:"Kurs/Sanat",en:"Art/Course",     emoji:"🎨", defaultR:200  },
  { key:"park",      tr:"Park/Oyun", en:"Park/Playground",emoji:"🌳", defaultR:150  },
  { key:"diger",     tr:"Diğer",     en:"Other",          emoji:"📍", defaultR:300  },
];

const formatLocationError = (err, lang = "tr") => {
  const code = err?.code || err?.message || "";
  if (code === "PERMISSION_DENIED" || code === "LOCATION_DENIED") return mt(lang, "Konum izni reddedildi.");
  if (code === "POSITION_UNAVAILABLE") return mt(lang, "GPS / Wi-Fi konumunu açın.");
  if (code === "TIMEOUT") return mt(lang, "Zaman aşımı.");
  if (code === "GEOLOCATION_PLUGIN_NOT_FOUND") return mt(lang, "Konum desteklenmiyor.");
  return mt(lang, "Konum alınamadı.");
};


const getAppLocationOnce = async ({
  requestPermission = true,
  tryAlways         = false,
  timeout           = 10000,
  maximumAge        = 8000,
} = {}) => {
  return getCurrentLocationOnce({ requestPermission, tryAlways, timeout, maximumAge });
};

const ANDROID_LOCATION_DISCLOSURE_VERSION = "v1";
const androidLocationDisclosureKey = (uid) =>
  `fp:androidLocationDisclosure:${ANDROID_LOCATION_DISCLOSURE_VERSION}:${String(uid || "").trim()}`;

const androidLocationDisclosureAccepted = (uid) => {
  if (!uid) return false;
  try {
    return localStorage.getItem(androidLocationDisclosureKey(uid)) === "accepted";
  } catch (_) {
    return false;
  }
};

const rememberAndroidLocationDisclosure = (uid) => {
  if (!uid) return;
  try {
    localStorage.setItem(androidLocationDisclosureKey(uid), "accepted");
  } catch (_) {}
};

const MAP_LABELS = {
  "Arka Planda Konum Paylaşımı":"Background Location Sharing",
  "Aile Yönetimi, canlı konum paylaşımını ve güvenli alan giriş/çıkış bildirimlerini çalıştırabilmek için konum verinizi uygulama kapalıyken veya kullanılmıyorken de toplar ve sunucuya aktarır. Konumunuz yalnızca seçtiğiniz aile alanındaki üyelerle paylaşılır. Bu özelliği istediğiniz zaman kapatabilirsiniz.":"Aile Yönetimi collects and transmits your location data to enable live location sharing and safe-zone entry/exit alerts, even when the app is closed or not in use. Your location is shared only with members of your selected family space. You can turn this feature off at any time.",
  "Kabul Et ve Devam Et":"Accept and Continue",
  "Her Zaman Konum İzni":"Allow Location All the Time",
  "Canlı konum paylaşımının ve güvenli alan bildirimlerinin uygulama arka plandayken de çalışması için Android konum ayarlarında İzinler > Konum bölümünden \"Her zaman izin ver\" seçeneğini seçin. Ayarları Aç'a dokunduğunuzda Aile Yönetimi'nin uygulama ayarları açılacaktır.":"To keep live location sharing and safe-zone alerts working while the app is in the background, select \"Allow all the time\" under Permissions > Location in Android settings. Tapping Open Settings will open Aile Yönetimi's app settings.",
  "Ayarları Aç":"Open Settings",
  "Aile üyeleriniz uygulama kapalıyken de konumunuzu görebilsin ve güvenli alan bildirimleri kesintisiz çalışsın diye bir sonraki ekranda \"Her Zaman İzin Ver\" seçmenizi öneririz.":"We recommend choosing \"Always Allow\" on the next screen so your family can see your location even when the app is closed, and safe zone alerts keep working without interruption.",
  "Devam Et":"Continue",
  "Ev":"Home", "Okul":"School", "İş":"Work", "Hastane":"Hospital", "Market":"Market", "Özel":"Custom", "Park":"Park", "Diğer":"Other",
  "Az önce":"Just now", "Bir aile üyesi":"A family member", "Tüm Aile":"Whole Family", "Güvenli Alan":"Safe Zone", "Coğrafi Sınır":"Geofence", "Buluşma Noktası":"Meeting Point",
  "Haritadan Konum Seç":"Select Location on Map", "Haritada istediğiniz noktaya dokunun.":"Tap the point you want on the map.", "Seçilen konum":"Selected location",
  "Konum alınamadı. Haritadan dokunarak seçebilirsiniz.":"Location could not be retrieved. You can tap on the map to select it.",
  "Aradığınız yer bulunamadı. Daha açık bir adres yazın veya haritadan seçin.":"The place you searched for was not found. Type a clearer address or select it on the map.",
  "Arama yapılamadı. İnternet bağlantınızı kontrol edin.":"Search could not be performed. Check your internet connection.",
  "Ara":"Search", "Harita Ortasını Seç":"Select Map Center", "Seçilen konum:":"Selected location:",
  "Yer adı boş olamaz.":"Place name cannot be empty.", "Yeri Düzenle":"Edit Place", "Yeni Yer Ekle":"Add New Place", "Kategori":"Category", "Yer Adı":"Place Name",
  "Örn: Ev, Okul…":"E.g. Home, School…", "Mevcut Konumumu Kullan":"Use My Current Location", "Haritadan Seç":"Select from Map", "Kaydediliyor…":"Saving…", "Güncelle":"Update", "Ekle":"Add",
  "Sınır Merkezi Seç":"Select Geofence Center", "Coğrafi Sınır Ekle":"Add Geofence", "Sınır Adı":"Geofence Name", "Örn: Okul Bölgesi…":"E.g. School Zone…", "Yarıçap":"Radius",
  "Bildirim Türü":"Notification Type", "🚪 Giriş":"🚪 Entry", "🚶 Çıkış":"🚶 Exit", "🔄 Her İkisi":"🔄 Both", "Takip Edilecek Üyeler":"Members to Track",
  "Seçim yapmazsan tüm aile üyeleri takip edilir.":"If you do not select anyone, all family members will be tracked.", "Mevcut Konumu Kullan":"Use Current Location", "Ad ve konum zorunludur.":"Name and location are required.",
  "Güvenli Alan Merkezi Seç":"Select Safe Zone Center", "Güvenli alanın merkezini haritada arayın veya dokunarak seçin.":"Search for the safe zone center on the map or tap to select it.", "Güvenli Alan Ekle":"Add Safe Zone", "Alan Adı":"Zone Name", "Örn: Ev Çevresi…":"E.g. Around Home…",
  "Buluşma Noktası Seç":"Select Meeting Point", "Buluşma noktasını haritada arayın veya dokunarak seçin.":"Search for the meeting point on the map or tap to select it.", "Kayıtlı Yerden Seç":"Select Saved Place", "Buluşma noktası adı…":"Meeting point name…", "Şu Anki Konumu Kullan":"Use Current Location", "Buluşma Noktası Belirle":"Set Meeting Point",
  "Konumumu Paylaş":"Share My Location", "Konum alınıyor…":"Getting location…", "Paylaşılıyor":"Sharing", "Gizli":"Private", "Süreli Paylaş:":"Timed Share:",
  "Sokak":"Street", "Uydu":"Satellite", "Arazi":"Terrain", "Ortak Yerler":"Shared Places", "Yeni Yer":"New Place", "Daha Fazla Göster":"Show More", "Daha Az Göster":"Show Less", "Yol Tarifi":"Directions", "Düzenle":"Edit", "Sil":"Delete",
  "Odaklan":"Focus", "Geçmiş":"History", "Analiz":"Analysis", "Yakındaki Üyeler":"Nearby Members", "Bölgeler":"Zones", "Güvenli Alanlar":"Safe Zones", "Buluşma":"Meeting", "Acil Yardım":"Emergency Help",
  "Konum bulunamadı.":"Location not found.", "Aradığınız cadde/sokak için sonuç bulunamadı.":"No result was found for the street/address you searched.", "Harita araması yapılamadı. İnternet bağlantınızı kontrol edin.":"Map search could not be performed. Check your internet connection.",
  "Konum desteklenmiyor.":"Location is not supported.", "Konum alınamadı.":"Location could not be retrieved.", "Aile Üyesi Yakında":"Family Member Nearby", "Yakınlık Algılandı":"Proximity Detected",
  "Yeni Coğrafi Sınır":"New Geofence", "Coğrafi Sınır Eklendi":"Geofence Added", "Güvenli Alan Eklendi":"Safe Zone Added", "Buluşma Noktası Belirlendi":"Meeting Point Set",
  "ACİL YARDIM!":"EMERGENCY HELP!", "yardım istiyor! Konumu paylaşıldı.":"is asking for help! Location has been shared.",
  "Üye":"Member",
  "Yer Konumu Seç":"Select Place Location",
  "Eklemek istediğiniz ortak yeri haritada arayın veya doğrudan haritaya dokunun.":"Search for the shared place you want to add or tap directly on the map.",
  "Konum izni reddedildi.":"Location permission denied.",
  "Zaman aşımı.":"Timed out.",
  "Hata.":"Error.",
  "Mevcut konum alınıyor. Konum izni kapalıysa arama yapabilir veya haritadan dokunarak seçebilirsiniz.":"Getting current location. If location permission is off, you can search or tap on the map to select.",
  "Bu Konumu Kullan":"Use This Location",
  "Güvenli Alan Oluştur":"Create Safe Zone",
  "Coğrafi Sınır Oluştur":"Create Geofence",
  "Belirli bölgelere girip çıkınca bildirim al":"Get notified when entering or leaving specific areas",
  "Aile üyelerinin konum geçmişini görüntüle":"View family members’ location history",
  "Canlı konum paylaşımı":"Live location sharing",
  "Coğrafi sınır bildirimleri":"Geofence notifications",
  "Konum geçmişi":"Location history",
  "Buluşma noktası belirleme":"Set meeting points",
  "Belirlenen bölgeye giriş/çıkışta bildirim alın. Sınırlar haritada daire olarak görünür.":"Get notified when entering/leaving the defined area. Boundaries appear as circles on the map.",
  "Acil durumda tüm aile üyelerine anlık konum bildirimi gönderir. Haritada konumun yanıp söner.":"In an emergency, sends your live location alert to all family members. Your location flashes on the map.",
  "Belirlenen bölge dışına çıkınca anlık bildirim alın. Tüm aile üyelerini izler.":"Get instant notifications when someone leaves the defined zone. Tracks all family members.",
  "Konum Paylaşımı Açıldı":"Location Sharing Enabled",
  "Arka Plan Konum İzni":"Background Location Permission",
  "Kesintisiz konum takibi için Ayarlar'dan konum iznini 'Her Zaman' olarak seçin.": "For uninterrupted location tracking, set location access to 'Always' in Settings.",
  "Ayarlar'ı Aç":"Open Settings",
  "Şimdi Değil":"Not Now",
  "Süreli Konum Paylaşımı":"Timed Location Sharing",
  "konumunu paylaşmaya başladı":"started sharing location",
  "boyunca konumunu paylaşıyor":"is sharing location for",
  "dakika":"minutes",
  "saat":"hours",
  "için":"for",
  "bildirimi eklendi":"notification was added",
  "oluşturuldu":"created",
  "tanımlandı":"defined",
  "buluşma noktası olarak belirlendi":"was set as the meeting point",
  "Aile yöneticisi":"Family manager",
  "Adres":"Address", "Mahalle, cadde…":"Neighborhood, street…", "Enlem":"Latitude", "Boylam":"Longitude", "veya manuel gir":"or enter manually", "Coğrafi sınırın merkezini haritada arayın veya dokunarak seçin.":"Search for the geofence center on the map or tap to select it.", "çok yakın!":"very close!", "m mesafede":"m away", "Coğrafi Sınırlar":"Geofences", "Henüz coğrafi sınır eklenmemiş.":"No geofences added yet.", "Giriş":"Entry", "Çıkış":"Exit", "Her İkisi":"Both", "Haritada göster":"Show on map", "Buluşma noktası belirlenmemiş.":"No meeting point set.", "Yakınlık Durumu":"Proximity Status", "Konumunuzu paylaşarak başlatın.":"Start by sharing your location.", "SOS İptal Et — Güvendeyim ✓":"Cancel SOS — I am safe ✓", "ACİL YARDIM GÖNDER":"SEND EMERGENCY HELP", "İlk Yeri Ekle":"Add First Place", "Renk":"Color", "Aile üyelerinin konumunu gerçek zamanlı takip et":"Track family members’ locations in real time", "Coğrafi sınır (Geofence)":"Geofence", "alanına":"area", "girdi":"entered", "çıktı":"left", "Güvenli Alan:":"Safe Zone:", "Coğrafi Sınır:":"Geofence:",
  "Paylaşıyor":"Sharing", "Kendiniz":"You", "Konum paylaşımı kapalı":"Location sharing is off", "Üyeler":"Members", "Sınır":"Boundary", "Yakınlık":"Proximity", "Güvenli":"Safe", "İptal":"Cancel",
  "Tarayıcınız konum özelliğini desteklemiyor.":"Your browser does not support location.", "Konum özelliği sadece HTTPS'de çalışır.":"Location works only over HTTPS.", "Konum kaydedilemedi.":"Location could not be saved.", "Konum izni reddedildi. 🔒 simgesine tıklayın.":"Location permission was denied. Tap the 🔒 icon.", "GPS / Wi-Fi konumunu açın.":"Turn on GPS / Wi‑Fi location.",
  "Konum izni gerekli":"Location Permission Required",
  "Konum paylaşımını açmak için iPhone Ayarları > Aile Yönetimi > Konum bölümünden izin verin.":"To turn on location sharing, allow location access from iPhone Settings > Family Management > Location.",
  "Canlı konum için konum izni vermeniz gerekir.":"You need to allow location permission for live location.",
  "Konum alınırken beklenmeyen bir sorun oluştu.":"An unexpected problem occurred while getting location.",
  "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin.":"Location sharing could not be updated. Check your internet connection and try again.",
  "Harita geçici olarak görüntülenemiyor.":"The map is temporarily unavailable.",
  "Kayıtlı yerler":"Saved places", "Sınırsız ortak yer":"Unlimited shared places", "Aile konumları ve adres yönetimi":"Family locations and address management", "Haritada kayıtlı yerleri hızlı bulma":"Quickly find saved places on the map", "Bu yeri silmek istediğinizden emin misiniz?":"Are you sure you want to delete this place?",
  "Hedef":"Destination", "giriş":"entry", "çıkış":"exit", "giriş/çıkış":"entry/exit", "Konumumu Göster":"Show My Location", "Tüm aile üyelerine mevcut navigasyon uygulamanız üzerinden yol tarifi verir.":"Gives all family members directions through your available navigation application.",
  "Konumunu paylaşan aile üyelerine olan mesafen gösterilir.":"Shows your distance to family members who share their location.", "Konum paylaşan başka üye yok.":"No other member is sharing location.", "Çok yakın! 🎉":"Very close! 🎉", "ACİL YARDIM İSTİYOR!":"IS ASKING FOR EMERGENCY HELP!",
  "Konum Geçmişi":"Location History",
  "Kimin nerede olduğunu görün. Haritada renkli rota olarak çizilir.":"See where everyone was. Drawn as a colored route on the map.",
  "Yükleniyor":"Loading",
  "Bu tarih için konum kaydı yok. Konum paylaşımı açıkken ve uygulama arka planda iken hareket olduğunda otomatik kaydedilir. Son 15 günlük hareketleriniz kaydedilir.":"No location records were found for this date. Locations are automatically saved when location sharing is enabled and movement is detected while the app is in the background. Your last 15 days of movement history is stored.",
  "kayıt":"records",
  "Bugün":"Today",
  "Dün":"Yesterday",
  "Rota haritada gösterildi":"Route shown on map",
  "Noktaya git":"Go to point",
  "Rota temizlendi":"Route cleared",
  "Takip Et":"Track",
  "Takip Ediliyor":"Tracking",
  "Takibi Durdur":"Stop Tracking",
  "Navigasyon Uygulaması Seç":"Choose Navigation App",
  "Harita":"Maps",
  "Google Maps":"Google Maps",
  "Yol Tarifi Al":"Get Directions",
  "Konum paylaşımı aktif":"Location sharing active",
  "Kesintisiz takip için uygulamayı kapatmayın":"Keep the app open for continuous tracking",
  "Anladım":"Got it",
  "Konum güncellemesi durdu":"Location update stopped",
  "Uygulama kapatıldığı için konum gecikmeli çalıştı. Kesintisiz takip için uygulamayı arka planda açık tutun.":"Location was delayed because the app was closed. Keep the app running in the background for continuous tracking.",
  "6 sa":"6h",
  "12 sa":"12h",
  "24 sa":"24h",
  "kayıt — rota haritada gösterildi":"records — route shown on map",
  "Henüz güvenli alan eklenmemiş.":"No safe zones added yet.", "yarıçap":"radius", "Hareket Analizi":"Movement Analysis", "Son 7 günlük hareket verisi. Üyeyi seçerek analizi görüntüleyin.":"Movement data from the last 7 days. Select a member to view the analysis.", "Analiz yükleniyor…":"Loading analysis…", "km toplam":"km total", "aktif gün":"active days", "konum kaydı":"location records",
  "Henüz ortak yer eklenmemiş.":"No shared places added yet.", "HARİTADA GÖR":"SHOW ON MAP", "Tüm Yerleri Gör":"Show All Places", "Ekleyen:":"Added by:", "Cadde, sokak veya yer ara...":"Search street, road or place...", "Arama önerileri":"Search suggestions", "Son aramalar":"Recent searches", "Yakındaki sonuçlar":"Nearby results", "Sonuç bulunamadı":"No results found", "Daha fazla yazın":"Type more", "Harita görünümü":"Map view", "Standart":"Standard", "Koyu":"Dark",
  "Süreli":"Timed", "dk":"min", "sa":"hr", "m uzakta":"m away", "km uzakta":"km away", "Konum alınamadı":"Location could not be retrieved", "Haritada Göster":"Show on Map",
};
const mt = (lang, text) => (lang === "en" ? (MAP_LABELS[text] || text) : text);
const mtime = (lang, f) => {
  if (f < 1) return lang === "en" ? "Just now" : "Az önce";
  if (f < 60) return lang === "en" ? `${f} min ago` : `${f} dk önce`;
  if (f < 1440) { const h = Math.floor(f / 60); return lang === "en" ? `${h} hr ago` : `${h} sa önce`; }
  const d = Math.floor(f / 1440);
  return lang === "en" ? `${d} days ago` : `${d} gün önce`;
};

const bekleyenKonumKapatmaAnahtari = (familyId, uid) => (
  familyId && uid ? `fp:map:pending-location-stop:${familyId}:${uid}` : null
);

const bekleyenKonumKapatmaOku = (familyId, uid) => {
  const key = bekleyenKonumKapatmaAnahtari(familyId, uid);
  if (!key) return false;
  try { return localStorage.getItem(key) === "1"; } catch (_) { return false; }
};

const bekleyenKonumKapatmaYaz = (familyId, uid, bekliyor) => {
  const key = bekleyenKonumKapatmaAnahtari(familyId, uid);
  if (!key) return;
  try {
    if (bekliyor) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch (_) {}
};

/* ─── Sabitler ─── */
const KATEGORI_LISTESI = [
  { key: "ev",      ikon: "🏠", renk: "#3b82f6", label: "Ev",      bg: "rgba(59,130,246,0.15)"  },
  { key: "okul",    ikon: "🏫", renk: "#f59e0b", label: "Okul",    bg: "rgba(245,158,11,0.15)"  },
  { key: "is",      ikon: "🏢", renk: "#8b5cf6", label: "İş",      bg: "rgba(139,92,246,0.15)"  },
  { key: "hastane", ikon: "🏥", renk: "#ef4444", label: "Hastane", bg: "rgba(239,68,68,0.15)"   },
  { key: "market",  ikon: "🛒", renk: "#10b981", label: "Market",  bg: "rgba(16,185,129,0.15)"  },
  { key: "ozel",    ikon: "📍", renk: "#6366f1", label: "Özel",    bg: "rgba(99,102,241,0.15)"  },
  { key: "park",    ikon: "🌳", renk: "#22c55e", label: "Park",    bg: "rgba(34,197,94,0.15)"   },
  { key: "diger",   ikon: "📌", renk: "#94a3b8", label: "Diğer",   bg: "rgba(148,163,184,0.15)" },
];

const UYE_RENKLERI = ["#6366f1","#ec4899","#f59e0b","#10b981","#06b6d4","#8b5cf6","#ef4444","#3b82f6"];
const ROL_SIRA     = ["Kişisel","Baba","Anne","Çocuk","Amca","Hala","Teyze","Dayı","Dede","Nene","Diğer"];
const GEOFENCE_YARICAPLAR = [100, 200, 500, 1000, 2000, 5000];


const HARITA_TURU_STORAGE_KEY = "aileHaritaGorunumTuru";
const GECERLI_HARITA_TURLERI = new Set(["sokak", "koyu", "uydu", "terrain"]);

function kayitliHaritaTuruGetir() {
  try {
    const saved = localStorage.getItem(HARITA_TURU_STORAGE_KEY);
    return GECERLI_HARITA_TURLERI.has(saved) ? saved : "sokak";
  } catch (_) {
    return "sokak";
  }
}

function kayitliHaritaTuruVarMi() {
  try {
    return GECERLI_HARITA_TURLERI.has(localStorage.getItem(HARITA_TURU_STORAGE_KEY));
  } catch (_) {
    return false;
  }
}

function haritaTuruKaydet(tur) {
  try {
    if (GECERLI_HARITA_TURLERI.has(tur)) localStorage.setItem(HARITA_TURU_STORAGE_KEY, tur);
  } catch (_) {}
}

const TILE_LAYERS = {
  // Carto'nun anonim tile uçları artık bazı isteklerde API anahtarı görseli
  // döndürüyor. Sokak ve koyu görünüm aynı anahtarsız OSM kaynağını kullanır;
  // koyu görünüm yalnızca istemci tarafında görsel filtre uygular.
  sokak:   { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "© OpenStreetMap contributors", overlay: null, className: "" },
  koyu:    { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "© OpenStreetMap contributors", overlay: null, className: "haritaDarkBaseTile" },
  uydu:    {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attr: "© Esri",
    // World Imagery anahtarsız kullanılabilir. Esri'nin eski Reference servisleri
    // bazı bölgelerde tile yerine "API key required" görseli döndürdüğü için
    // uydu görünümüne ayrıca overlay bindirmiyoruz.
    overlay: null,
  },
  terrain: { url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",                                                   attr: "© OpenTopoMap",   overlay: null },
};

/* ─── Yardımcılar ─── */
function uyelerSirala(liste) {
  return [...liste].sort((a, b) => {
    const ai = ROL_SIRA.indexOf(a.rol), bi = ROL_SIRA.indexOf(b.rol);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}
function initials(u) { return `${u.ad?.[0] || ""}${u.soyad?.[0] || ""}`.toUpperCase(); }
function uyeRenk(u, i) { return u.calendarColor || u.renk || UYE_RENKLERI[i % UYE_RENKLERI.length]; }
function guvenliHaritaRengi(value) {
  const renk = String(value || "").trim();
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(renk) ? renk : "#6366f1";
}
function haritaHtmlKacis(value) {
  return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[ch]));
}
function haritaAvatarBilgisi(avatar) {
  if (!avatar) return null;
  const presetSrc = AVATAR_IMPORTS[avatar] || null;
  if (presetSrc) return { src: presetSrc, custom: false };
  if (typeof window === "undefined") return null;
  try {
    const parsed = new URL(String(avatar), window.location.origin);
    const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
    const allowedOrigin = parsed.origin === apiOrigin || parsed.origin === window.location.origin;
    return allowedOrigin && parsed.pathname.startsWith("/files/view/")
      ? { src: parsed.href, custom: true }
      : null;
  } catch (_) {
    return null;
  }
}
function haritaMapKitAvatarSrc(member) {
  if (member?._mapAvatarPending) return "";
  return member?._resolvedMapAvatar || haritaAvatarBilgisi(member?.avatar)?.src || "";
}
function kategoriData(k) { return KATEGORI_LISTESI.find((x) => x.key === k) || KATEGORI_LISTESI[7]; }
function haritaMapKitYerStili(place) { return kategoriData(place?.kategori); }

function HaritaUiIcon({ name, size = 22 }) {
  const ortak = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: "false",
  };

  if (name === "share-location") {
    return (
      <svg {...ortak}>
        <path d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z" />
        <circle cx="12" cy="10" r="2.15" />
        <path d="M19.2 7.1a4.1 4.1 0 0 1 0 5.8" />
        <path d="M21.1 5.2a6.8 6.8 0 0 1 0 9.6" />
      </svg>
    );
  }

  return (
    <svg {...ortak}>
      <circle cx="12" cy="12" r="7.25" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}

function zaman(ts, lang = "tr") {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const f = Math.floor((Date.now() - d.getTime()) / 60000);
  return mtime(lang, f);
}
function mesafe(lat1, lng1, lat2, lng2) {
  const R = 6371, dLat = ((lat2-lat1)*Math.PI)/180, dLon = ((lng2-lng1)*Math.PI)/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}


const temizMetin = (v = "") => String(v || "").trim();
const placeKey = (lat, lng, name = "") => `${Number(lat).toFixed(5)}:${Number(lng).toFixed(5)}:${temizMetin(name).toLocaleLowerCase("tr-TR")}`;
const placeSubtitle = (parts = []) => parts.map(temizMetin).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(" · ");

function normalizePhotonFeature(feature, q = "") {
  const coords = feature?.geometry?.coordinates;
  const props = feature?.properties || {};
  if (!coords || coords.length < 2) return null;
  const name = temizMetin(props.name || props.street || props.city || props.county || props.state || q);
  const subtitle = placeSubtitle([props.street, props.district, props.city, props.county, props.state, props.country]);
  return {
    source: "photon",
    name: name || temizMetin(q),
    subtitle,
    lat: Number(coords[1]),
    lng: Number(coords[0]),
    raw: feature,
  };
}

function normalizeNominatimResult(item, q = "") {
  if (!item?.lat || !item?.lon) return null;
  const a = item.address || {};
  const name = temizMetin(item.name || a.amenity || a.shop || a.road || a.neighbourhood || a.suburb || a.city || a.town || a.province || q);
  const subtitle = placeSubtitle([a.road, a.neighbourhood || a.suburb, a.city || a.town || a.county, a.state, a.country]);
  return {
    source: "nominatim",
    name: name || temizMetin(q),
    subtitle: subtitle || temizMetin(item.display_name),
    lat: Number(item.lat),
    lng: Number(item.lon),
    raw: item,
  };
}

async function haritaYerAraCoklu(q, { lang = "tr", lat, lng, limit = 10 } = {}) {
  const query = temizMetin(q);
  if (query.length < 2) return [];
  const encoded = encodeURIComponent(query);
  const acceptLanguage = lang === "en" ? "en" : "tr";
  const photonUrl = `https://photon.komoot.io/api/?q=${encoded}&limit=${Math.min(limit, 12)}&lang=${acceptLanguage}${lat && lng ? `&lat=${lat}&lon=${lng}` : ""}`;
  const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&limit=${Math.min(limit, 12)}&addressdetails=1&accept-language=${acceptLanguage}&q=${encoded}${lat && lng ? `&viewbox=${lng-0.8},${lat+0.8},${lng+0.8},${lat-0.8}&bounded=0` : ""}`;

  const [photonRes, nominatimRes] = await Promise.allSettled([
    fetch(photonUrl, { headers: { Accept: "application/json" } }).then((r) => r.ok ? r.json() : null),
    fetch(nominatimUrl, { headers: { Accept: "application/json" } }).then((r) => r.ok ? r.json() : null),
  ]);

  const photonItems = photonRes.status === "fulfilled" && Array.isArray(photonRes.value?.features)
    ? photonRes.value.features.map((f) => normalizePhotonFeature(f, query)).filter(Boolean)
    : [];
  const nominatimItems = nominatimRes.status === "fulfilled" && Array.isArray(nominatimRes.value)
    ? nominatimRes.value.map((r) => normalizeNominatimResult(r, query)).filter(Boolean)
    : [];

  const merged = [];
  const seen = new Set();
  [...photonItems, ...nominatimItems].forEach((item) => {
    if (!Number.isFinite(item.lat) || !Number.isFinite(item.lng)) return;
    const key = placeKey(item.lat, item.lng, item.name);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });

  const qLower = query.toLocaleLowerCase("tr-TR");
  return merged
    .map((item) => {
      let score = 0;
      const name = `${item.name} ${item.subtitle}`.toLocaleLowerCase("tr-TR");
      if (item.source === "photon") score += 8;
      if (name.startsWith(qLower)) score += 12;
      if (name.includes(qLower)) score += 6;
      if (lat && lng) score += Math.max(0, 8 - mesafe(lat, lng, item.lat, item.lng));
      return { ...item, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

function takipEdilecekUidler(bolge, uyeler = []) {
  if (Array.isArray(bolge?.uyeUids) && bolge.uyeUids.length > 0) {
    return [...new Set(bolge.uyeUids.filter(Boolean))];
  }
  if (bolge?.uyeUid) return [bolge.uyeUid];
  return uyeler.map((u) => u.uid || u.id).filter(Boolean);
}
function uyeTamAdBul(uyeler = [], uid, lang = "tr") {
  const uye = uyeler.find((u) => (u.uid || u.id) === uid);
  if (!uye) return mt(lang, "Bir aile üyesi");
  return `${uye.ad || ""} ${uye.soyad || ""}`.trim() || uye.ad || mt(lang, "Bir aile üyesi");
}
function bolgeHedefMetni(bolge, uyeler = [], lang = "tr") {
  const hedefler = takipEdilecekUidler(bolge, uyeler);
  if (hedefler.length === 0 || hedefler.length === uyeler.length) return mt(lang, "Tüm Aile");
  if (hedefler.length === 1) return uyeTamAdBul(uyeler, hedefler[0], lang);
  return lang === "en" ? `${hedefler.length} members` : `${hedefler.length} üye`;
}

/* ═══════════════════════════════════════════════════
   LEAFLET HARİTA
═══════════════════════════════════════════════════ */
function LeafletMap({ konumlar, yerler, uyeler, seciliUyeUid, takipModu=false, haritaTuru="sokak", lang="tr", onZoomChange,
                      geofencelar=[], guvenliAlanlar=[], bulusmaNoktasi=null, sosAlertler=[], onYerTikla, mapRef: extMapRef }) {
  const divRef          = useRef(null);
  const internalMapRef  = useRef(null);
  const mapRef          = extMapRef || internalMapRef;
  const tileRef         = useRef(null);
  const overlayRef      = useRef([]); // Uydu görünümündeki etiket katmanları
  const leafRef         = useRef(null);
  const haritaTuruRef   = useRef(GECERLI_HARITA_TURLERI.has(haritaTuru) ? haritaTuru : "sokak");
  const markerUyeRef    = useRef([]);
  const markerYerRef    = useRef([]);
  const geofenceRef     = useRef([]);
  const guvenliAlanRef  = useRef([]);
  const bulusmaRef      = useRef(null);

  /* Leaflet index.html'den yüklü geliyor */
  useEffect(() => {
    if (window.L) { leafRef.current = window.L; initMap(); return; }
    // Leaflet henüz yüklenmediyse bekle
    const interval = setInterval(() => {
      if (window.L) { clearInterval(interval); leafRef.current = window.L; initMap(); }
    }, 50);
    return () => { clearInterval(interval); if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [lang]); // eslint-disable-line

  const initMap = useCallback(() => {
    if (!divRef.current || mapRef.current) return;
    const L = leafRef.current;
    mapRef.current = L.map(divRef.current, { center:[41.0082,28.9784], zoom:11, zoomControl:false });
    const ilkTur = TILE_LAYERS[haritaTuruRef.current] || TILE_LAYERS.sokak;
    tileRef.current = L.tileLayer(ilkTur.url, { attribution: ilkTur.attr, maxZoom:18, className: ilkTur.className || "" }).addTo(mapRef.current);
    const ilkOverlays = Array.isArray(ilkTur.overlay) ? ilkTur.overlay : (ilkTur.overlay ? [ilkTur.overlay] : []);
    overlayRef.current = ilkOverlays.map((url) => L.tileLayer(url, { maxZoom: 18, opacity: 0.92 }).addTo(mapRef.current));
    L.control.zoom({ position:"bottomright" }).addTo(mapRef.current);
    // Kullanıcı zoom değiştirince bildir
    mapRef.current.on("zoomend", () => {
      const z = mapRef.current?.getZoom();
      if (z) onZoomChange?.(z);
    });
    updateMarkers();
  }, []); // eslint-disable-line

  /* Tile değiştir */
  useEffect(() => {
    const L=leafRef.current, map=mapRef.current; if (!L||!map) return;
    const t = TILE_LAYERS[haritaTuru]||TILE_LAYERS.sokak;
    if (tileRef.current) tileRef.current.remove();
    overlayRef.current.forEach((layer) => layer?.remove?.());
    overlayRef.current = [];
    tileRef.current = L.tileLayer(t.url,{attribution:t.attr,maxZoom:18,className:t.className || ""}).addTo(map);
    // Uydu görünümünde sokak/yol/yer isimlerini ayrı referans katmanı olarak bas.
    const overlays = Array.isArray(t.overlay) ? t.overlay : (t.overlay ? [t.overlay] : []);
    overlayRef.current = overlays.map((url) => L.tileLayer(url,{maxZoom:18,opacity:0.92}).addTo(map));
    haritaTuruRef.current = haritaTuru;
  }, [haritaTuru]); // eslint-disable-line

  /* Marker güncelle */
  const updateMarkers = useCallback(() => {
    const L=leafRef.current, map=mapRef.current; if (!L||!map) return;
    markerUyeRef.current.forEach(m=>m.remove()); markerUyeRef.current=[];
    markerYerRef.current.forEach(m=>m.remove()); markerYerRef.current=[];
    uyelerSirala(uyeler).forEach((uye,i) => {
      const uid=uye.uid||uye.id, lok=konumlar[uid];
      if (!lok||!lok.paylasimAcik||!lok.lat) return;
      const renk=guvenliHaritaRengi(uyeRenk(uye,i)), ins=haritaHtmlKacis(initials(uye));
      const avatarBilgisi=uye._mapAvatarPending
        ? null
        : uye._resolvedMapAvatar
        ? { src: uye._resolvedMapAvatar, custom: true }
        : haritaAvatarBilgisi(uye.avatar);
      const avatarIcerik=avatarBilgisi
        ? `<img src="${haritaHtmlKacis(avatarBilgisi.src)}" alt="" draggable="false" />`
        : `<span>${ins}</span>`;
      const icon=L.divIcon({ className:"", iconSize:[48,54], iconAnchor:[24,52], popupAnchor:[0,-52],
        html:`<div class="haritaUyeMarker" style="--harita-uye-renk:${renk}"><div class="haritaUyeMarkerAvatar${avatarBilgisi?.custom ? " custom" : ""}">${avatarIcerik}</div></div>` });
      const m = L.marker([lok.lat,lok.lng],{icon}).addTo(map)
        .bindPopup(`<b>${uye.ad} ${uye.soyad}</b><br>${uye.rol}<br><small style="color:#94a3b8">${zaman(lok.sonGuncelleme, lang)}</small>`);
      markerUyeRef.current.push(m);
    });
    yerler.forEach((yer) => {
      if (!yer.lat||!yer.lng) return;
      const kat=kategoriData(yer.kategori);
      const icon=L.divIcon({ className:"", iconSize:[34,34], iconAnchor:[17,34], popupAnchor:[0,-36],
        html:`<div style="background:${kat.renk};border:2.5px solid #fff;border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 3px 10px rgba(0,0,0,0.3);cursor:pointer">${kat.ikon}</div>` });
      const m = L.marker([yer.lat,yer.lng],{icon}).addTo(map)
        .bindPopup(`<b>${yer.ad}</b><br>${yer.adres||""}<br><small>${lang === "en" ? "Added by" : "Ekleyen"}: ${yer.ekleyenAd||""}</small>`);
      if (onYerTikla) m.on("click",()=>onYerTikla(yer));
      markerYerRef.current.push(m);
    });
  }, [konumlar, yerler, uyeler, onYerTikla, lang, mapRef]);

  useEffect(()=>{updateMarkers();},[updateMarkers]);

  /* Geofence daireleri */
  useEffect(() => {
    const L=leafRef.current,map=mapRef.current; if(!L||!map) return;
    geofenceRef.current.forEach(l=>l.remove()); geofenceRef.current=[];
    geofencelar.forEach((gf) => {
      if (!gf.lat||!gf.lng) return;
      const c=L.circle([gf.lat,gf.lng],{ radius:gf.yaricap, color:gf.renk||"#6366f1",
        fillColor:gf.renk||"#6366f1", fillOpacity:0.08, weight:2, dashArray:"6 4" })
        .addTo(map).bindPopup(`<b>${gf.ad}</b><br>${gf.yaricap>=1000?(gf.yaricap/1000).toFixed(1)+"km":gf.yaricap+"m"} ${lang === "en" ? "radius" : "yarıçap"}`);
      geofenceRef.current.push(c);
    });
  },[geofencelar]); // eslint-disable-line

  /* Güvenli alan daireleri */
  useEffect(() => {
    const L=leafRef.current,map=mapRef.current; if(!L||!map) return;
    guvenliAlanRef.current.forEach(l=>l.remove()); guvenliAlanRef.current=[];
    guvenliAlanlar.forEach((alan) => {
      if (!alan.lat||!alan.lng) return;
      const c=L.circle([alan.lat,alan.lng],{
        radius:alan.yaricap||300,
        color:"#10b981",
        fillColor:"#10b981",
        fillOpacity:0.12,
        weight:2.5,
        dashArray:"8 5"
      })
        .addTo(map)
        .bindPopup(`<b>🟢 ${alan.ad||mt(lang, "Güvenli Alan")}</b><br>${alan.yaricap>=1000?(alan.yaricap/1000).toFixed(1)+"km":(alan.yaricap||300)+"m"} ${lang === "en" ? "radius" : "yarıçap"}`);
      guvenliAlanRef.current.push(c);
    });
  },[guvenliAlanlar]); // eslint-disable-line

  const sosMarkerRef = useRef([]);
  /* SOS marker - yanıp söner */
  useEffect(() => {
    const L=leafRef.current,map=mapRef.current; if(!L||!map) return;
    sosMarkerRef.current.forEach(m=>m.remove()); sosMarkerRef.current=[];
    sosAlertler.forEach((sos) => {
      if (!sos.lat||!sos.lng) return;
      const icon=L.divIcon({className:"",iconSize:[50,50],iconAnchor:[25,25],
        html:`<div style="background:#ef4444;border:3px solid #fff;border-radius:50%;width:50px;height:50px;display:flex;align-items:center;justify-content:center;font-size:24px;box-shadow:0 0 0 rgba(239,68,68,0.7);animation:sos-pulse 1s infinite">🆘</div>`});
      const m=L.marker([sos.lat,sos.lng],{icon}).addTo(map)
        .bindPopup(`<b style="color:#ef4444">🆘 ${sos.tamAd}</b><br>${mt(lang, "ACİL YARDIM!")}`).openPopup();
      sosMarkerRef.current.push(m);
    });
  },[sosAlertler]); // eslint-disable-line

  /* Buluşma noktası */
  useEffect(() => {
    const L=leafRef.current,map=mapRef.current; if(!L||!map) return;

    const oncekiMarkerlar = Array.isArray(bulusmaRef.current)
      ? bulusmaRef.current
      : (bulusmaRef.current ? [bulusmaRef.current] : []);
    oncekiMarkerlar.forEach(marker => { try { marker.remove(); } catch (_) {} });
    bulusmaRef.current = [];

    const liste = Array.isArray(bulusmaNoktasi)
      ? bulusmaNoktasi
      : (bulusmaNoktasi ? [bulusmaNoktasi] : []);

    const gecerliListe = liste.filter(bn => Number.isFinite(Number(bn?.lat)) && Number.isFinite(Number(bn?.lng)));
    if (gecerliListe.length === 0) return;

    const icon=L.divIcon({ className:"", iconSize:[42,42], iconAnchor:[21,21], popupAnchor:[0,-24],
      html:`<div style="background:#f59e0b;border:3px solid #fff;border-radius:50%;width:42px;height:42px;display:flex;align-items:center;justify-content:center;font-size:20px;box-shadow:0 4px 14px rgba(245,158,11,0.5)">🤝</div>` });

    bulusmaRef.current = gecerliListe.map((bn) =>
      L.marker([Number(bn.lat), Number(bn.lng)], { icon })
        .addTo(map)
        .bindPopup(`<b>${mt(lang, "Buluşma Noktası")}</b><br>${bn.ad||""}`)
    );
  },[bulusmaNoktasi]); // eslint-disable-line

  /* Odaklanma — seçili üye değişince */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !seciliUyeUid) return;
    const lok = konumlar[seciliUyeUid];
    if (lok?.lat) {
      // İlk seçimde makul bir zoom kullan, sonraki güncellemelerde mevcut zomu koru
      const mevcutZoom = map.getZoom();
      const z = mevcutZoom && mevcutZoom > 10 ? mevcutZoom : 15;
      map.flyTo([lok.lat, lok.lng], z, { duration: 1.0 });
    }
  }, [seciliUyeUid]); // eslint-disable-line

  /* Takip modu: seçili üyenin konumu güncellenince haritayı o konuma götür */
  useEffect(() => {
    if (!takipModu || !seciliUyeUid) return;
    const map = mapRef.current;
    if (!map) return;
    const lok = konumlar[seciliUyeUid];
    // Kullanıcının ayarladığı zoom seviyesini koru, yoksa mevcut zoom
    if (lok?.lat) {
      const z = map.getZoom() || 17;
      map.flyTo([lok.lat, lok.lng], z, { duration: 0.8, animate: true });
    }
  }, [konumlar, takipModu, seciliUyeUid]); // eslint-disable-line

  return <div ref={divRef} className="haritaLeafletWrap" />;
}

function ResilientFamilyMap(props) {
  // Fallback yalnızca mevcut map mount'u için geçerlidir. Geçici CDN/token
  // hatası sonraki Harita açılışlarını bütün oturum boyunca Leaflet'e kilitlemez.
  const [useLeaflet, setUseLeaflet] = useState(false);
  const fallbackViewRef = useRef(null);
  const fallbackMapRef = props.mapRef;
  const onLeafletFallback = props.onLeafletFallback;

  const activateLeaflet = useCallback(() => {
    const currentMap = fallbackMapRef?.current;
    try {
      const center = currentMap?.getCenter?.();
      if (Number.isFinite(Number(center?.lat)) && Number.isFinite(Number(center?.lng))) {
        fallbackViewRef.current = { center, zoom: currentMap?.getZoom?.() || 11 };
      }
    } catch (_) {}
    onLeafletFallback?.();
    setUseLeaflet(true);
  }, [fallbackMapRef, onLeafletFallback]);

  useEffect(() => {
    if (!useLeaflet || !fallbackViewRef.current) return undefined;
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      const map = props.mapRef?.current;
      if (map && map._renderer !== "mapkit") {
        const { center, zoom } = fallbackViewRef.current;
        map.flyTo?.([center.lat, center.lng], zoom, { animate: false });
        fallbackViewRef.current = null;
        window.clearInterval(timer);
      } else if (attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [useLeaflet, props.mapRef]);

  const rendererKey = useLeaflet ? "leaflet" : "mapkit";
  const fallback = useLeaflet
    ? (
      <div className="haritaLeafletWrap" role="status" style={{display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        {mt(props.lang, "Harita geçici olarak görüntülenemiyor.")}
      </div>
    )
    : <div className="haritaLeafletWrap" aria-hidden="true" />;

  return (
    <HaritaErrorBoundary
      key={rendererKey}
      resetKey={rendererKey}
      onError={useLeaflet ? undefined : activateLeaflet}
      fallback={fallback}
    >
      {useLeaflet
        ? <LeafletMap {...props} />
        : <MapKitFamilyMap {...props} onFallback={activateLeaflet} />}
    </HaritaErrorBoundary>
  );
}


/* ═══════════════════════════════════════════════════
   HARİTADAN KONUM SEÇME MODALI
═══════════════════════════════════════════════════ */
function gecerliKonumSec(...adaylar) {
  for (const aday of adaylar) {
    if (!aday) continue;

    // Boş input değerleri Number("") ile 0'a döner.
    // Yeni Yer Ekle ekranında enlem/boylam boşken harita 0,0'a gidip
    // uydu katmanında "Map data not yet available" gösterebiliyordu.
    if (aday.lat === "" || aday.lng === "" || aday.lat === null || aday.lng === null || aday.lat === undefined || aday.lng === undefined) continue;

    const lat = Number(aday.lat);
    const lng = Number(aday.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  return null;
}

function HaritadanKonumSecModal({ baslik="Haritadan Konum Seç", aciklama="Haritada istediğiniz noktaya dokunun.", ilkKonum, onSec, onKapat, lang="tr" }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const overlayRef = useRef([]);
  const markerRef = useRef(null);
  const [secili, setSecili] = useState(() => gecerliKonumSec(ilkKonum));
  const [arama, setArama] = useState("");
  const [aramaOdak, setAramaOdak] = useState(false);
  const [oneriler, setOneriler] = useState([]);
  const [oneriYukleniyor, setOneriYukleniyor] = useState(false);
  const [sonAramalar, setSonAramalar] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("aileHaritaSonAramalar") || "[]");
      return Array.isArray(raw) ? raw.filter((x) => x?.lat && x?.lng).slice(0, 8) : [];
    } catch (_) { return []; }
  });
  const [secimHaritaTuru, setSecimHaritaTuru] = useState(() => kayitliHaritaTuruGetir());
  const [araniyor, setAraniyor] = useState(false);
  const [hata, setHata] = useState("");

  const haritaKatmaniniUygula = useCallback((tur) => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    const guvenliTur = GECERLI_HARITA_TURLERI.has(tur) ? tur : "sokak";
    const layerData = TILE_LAYERS[guvenliTur] || TILE_LAYERS.sokak;

    try { tileRef.current?.remove?.(); } catch (_) {}
    overlayRef.current.forEach((layer) => { try { layer?.remove?.(); } catch (_) {} });
    overlayRef.current = [];

    tileRef.current = L.tileLayer(layerData.url, {
      attribution: layerData.attr,
      maxZoom: 18,
      maxNativeZoom: guvenliTur === "uydu" ? 16 : 18,
      className: layerData.className || "",
    }).addTo(map);

    const overlays = Array.isArray(layerData.overlay) ? layerData.overlay : (layerData.overlay ? [layerData.overlay] : []);
    overlayRef.current = overlays.map((url) => L.tileLayer(url, { maxZoom: 18, opacity: 0.92 }).addTo(map));

    let fallbackEklendi = false;
    tileRef.current.on?.("tileerror", () => {
      if (fallbackEklendi || !mapRef.current || guvenliTur !== "uydu") return;
      fallbackEklendi = true;
      try { tileRef.current?.remove?.(); } catch (_) {}
      tileRef.current = L.tileLayer(TILE_LAYERS.sokak.url, {
        attribution: TILE_LAYERS.sokak.attr,
        maxZoom: 18,
      }).addTo(mapRef.current);
    });
  }, []);

  const markerGuncelle = useCallback((lat, lng, zoom=16) => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;

    const nokta = { lat: Number(lat), lng: Number(lng) };
    setSecili(nokta);

    if (markerRef.current) markerRef.current.remove();

    const icon = L.divIcon({
      className: "",
      iconSize: [42, 42],
      iconAnchor: [21, 42],
      popupAnchor: [0, -38],
      html: `<div style="width:42px;height:42px;border-radius:50% 50% 50% 0;background:#6366f1;border:3px solid #fff;box-shadow:0 8px 24px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;transform:rotate(-45deg)"><span style="transform:rotate(45deg)">📍</span></div>`
    });

    markerRef.current = L.marker([nokta.lat, nokta.lng], { icon }).addTo(map)
      .bindPopup(mt(lang, "Seçilen konum"))
      .openPopup();
    map.flyTo([nokta.lat, nokta.lng], zoom, { duration: 0.6 });
  }, [lang]);

  useEffect(() => {
    let temizle = false;

    const baslat = () => {
      if (temizle || !divRef.current || mapRef.current || !window.L) return;
      const L = window.L;
      const merkez = secili ? [secili.lat, secili.lng] : [39.9208, 32.8541];
      mapRef.current = L.map(divRef.current, { center: merkez, zoom: secili ? 15 : 6, zoomControl: false });

      // Seçim haritası ana haritadaki son seçilen görünümle açılsın.
      // Uydu modunda yol/yer etiketleri de eklenir; uydu yüklenemezse sokak katmanına düşer.
      haritaKatmaniniUygula(secimHaritaTuru);

      L.control.zoom({ position: "bottomright" }).addTo(mapRef.current);
      mapRef.current.on("click", (e) => markerGuncelle(e.latlng.lat, e.latlng.lng, mapRef.current.getZoom() || 16));

      // Modal animasyonu bittikten sonra Leaflet ölçüsünü birkaç kez yenile.
      // Mobilde boş/açık mavi ekran kalmasının ana sebebi çoğu zaman ilk ölçümün 0 gelmesi.
      [120, 350, 700].forEach((ms) => {
        setTimeout(() => {
          try { mapRef.current?.invalidateSize?.(); } catch (_) {}
        }, ms);
      });

      if (secili) {
        setTimeout(() => markerGuncelle(secili.lat, secili.lng, 15), 300);
      } else {
        // Native uygulamada web geolocation popup'ı açmadan mevcut konuma yaklaş.
        // DÜZELTME 5: Sessiz — yeni izin popup'ı açmadan cache'den dene
        getAppLocationOnce({ requestPermission: false, tryAlways: false, timeout: 6000, maximumAge: 60000 })
          .then((p) => {
            if (temizle || !p?.lat || !p?.lng) return;
            markerGuncelle(p.lat, p.lng, 15);
          })
          .catch(() => {});
      }
    };

    if (window.L) baslat();
    else {
      const id = setInterval(() => {
        if (window.L) { clearInterval(id); baslat(); }
      }, 50);
      return () => { temizle = true; clearInterval(id); if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    }

    return () => { temizle = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!mapRef.current) return;
    haritaKatmaniniUygula(secimHaritaTuru);
  }, [secimHaritaTuru, haritaKatmaniniUygula]);

  const sonAramayiKaydetModal = useCallback((yer) => {
    if (!yer?.lat || !yer?.lng) return;
    const kayit = { name: yer.name, subtitle: yer.subtitle, lat: yer.lat, lng: yer.lng, source: yer.source || "recent" };
    setSonAramalar((prev) => {
      const next = [kayit, ...prev.filter((x) => placeKey(x.lat, x.lng, x.name) !== placeKey(kayit.lat, kayit.lng, kayit.name))].slice(0, 8);
      try { localStorage.setItem("aileHaritaSonAramalar", JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const oneriSec = useCallback((yer) => {
    if (!yer?.lat || !yer?.lng) return;
    setArama(yer.name || "");
    setOneriler([]);
    setAramaOdak(false);
    sonAramayiKaydetModal(yer);
    markerGuncelle(Number(yer.lat), Number(yer.lng), 16);
  }, [markerGuncelle, sonAramayiKaydetModal]);

  useEffect(() => {
    const q = arama.trim();
    let iptal = false;
    if (q.length < 2) {
      setOneriler([]);
      setOneriYukleniyor(false);
      return;
    }
    setOneriYukleniyor(true);
    const timer = setTimeout(async () => {
      try {
        const c = mapRef.current?.getCenter?.();
        const liste = await haritaYerAraCoklu(q, { lang, lat: c?.lat, lng: c?.lng, limit: 6 });
        if (!iptal) setOneriler(liste);
      } catch (_) {
        if (!iptal) setOneriler([]);
      } finally {
        if (!iptal) setOneriYukleniyor(false);
      }
    }, 300);
    return () => { iptal = true; clearTimeout(timer); };
  }, [arama, lang]);

  const mevcutKonumuKullan = async () => {
    setHata("");
    try {
      const p = await getAppLocationOnce({ requestPermission: true, timeout: 10000, maximumAge: 5000 });
      if (!p?.lat || !p?.lng) throw new Error("LOCATION_EMPTY");
      markerGuncelle(p.lat, p.lng, 16);
    } catch (err) {
      setHata(formatLocationError(err, lang) || mt(lang, "Konum alınamadı. Haritadan dokunarak seçebilirsiniz."));
    }
  };

  const adresAra = async (e) => {
    e?.preventDefault?.();
    const q = arama.trim();
    if (!q) return;
    setHata("");
    try {
      setAraniyor(true);
      const c = mapRef.current?.getCenter?.();
      const sonuc = await haritaYerAraCoklu(q, { lang, lat: c?.lat, lng: c?.lng, limit: 6 });
      const ilk = sonuc[0];
      if (!ilk?.lat || !ilk?.lng) {
        setHata(mt(lang, "Aradığınız yer bulunamadı. Daha açık bir adres yazın veya haritadan seçin."));
        return;
      }
      sonAramayiKaydetModal(ilk);
      setOneriler([]);
      setAramaOdak(false);
      markerGuncelle(Number(ilk.lat), Number(ilk.lng), 16);
    } catch {
      setHata(mt(lang, "Arama yapılamadı. İnternet bağlantınızı kontrol edin."));
    } finally {
      setAraniyor(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onKapat}>
      <div className="haritaModalIcerik" onClick={e=>e.stopPropagation()} style={{maxHeight:"94vh",overflowY:"auto",width:"100%",maxWidth:520}}>
        <div className="haritaModalBaslik"><span>{mt(lang, baslik)}</span><button className="haritaModalKapat" onClick={onKapat}>×</button></div>
        <p style={{fontSize:12,color:"var(--text-secondary)",margin:"0 0 10px",lineHeight:1.5}}>{mt(lang, aciklama)}</p>
        {!secili&&<div style={{fontSize:11,color:"var(--text-secondary)",padding:"8px 10px",background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.16)",borderRadius:10,marginBottom:10}}>📡 {mt(lang, "Mevcut konum alınıyor. Konum izni kapalıysa arama yapabilir veya haritadan dokunarak seçebilirsiniz.")}</div>}
        <div className="haritaSecimAramaSatiri noSwipeBack" data-no-swipe-back="true">
          <form className="haritaAramaForm" onSubmit={adresAra}>
            <span className="haritaAramaIkon">🔎</span>
            <input
              className="haritaAramaInput"
              value={arama}
              onChange={(e)=>setArama(e.target.value)}
              onFocus={()=>setAramaOdak(true)}
              placeholder={lang === "en" ? "Search address, street, school, hospital..." : "Adres, cadde, okul, hastane ara..."}
            />
            <button className="haritaAramaBtn" type="submit" disabled={araniyor}>{araniyor ? "..." : mt(lang, "Ara")}</button>
          </form>
          <select
            className="haritaKartBtn haritaSecimTurSelect"
            value={secimHaritaTuru}
            onChange={(e)=>{ const tur=e.target.value; setSecimHaritaTuru(tur); haritaTuruKaydet(tur); }}
            aria-label={mt(lang, "Harita görünümü")}
          >
            <option value="sokak">🗺 {mt(lang, "Standart")}</option>
            <option value="koyu">🌙 {mt(lang, "Koyu")}</option>
            <option value="uydu">🛰 {mt(lang, "Uydu")}</option>
            <option value="terrain">🏔 {mt(lang, "Arazi")}</option>
          </select>
          {(aramaOdak && (arama.trim().length >= 2 || sonAramalar.length > 0)) && (
            <div className="haritaAramaOneriKutusu haritaSecimOneriKutusu noSwipeBack" data-no-swipe-back="true">
              <div className="haritaAramaOneriBaslik">
                <span>{arama.trim().length >= 2 ? mt(lang, "Arama önerileri") : mt(lang, "Son aramalar")}</span>
                {oneriYukleniyor && <small>…</small>}
              </div>
              {(arama.trim().length >= 2 ? oneriler : sonAramalar).slice(0, 2).map((yer, idx) => (
                <button
                  key={`${yer.source || "recent"}-${idx}-${yer.lat}-${yer.lng}`}
                  type="button"
                  className="haritaAramaOneriSatir"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => oneriSec(yer)}
                >
                  <span className="haritaAramaOneriIkon">📍</span>
                  <span className="haritaAramaOneriMetin">
                    <b>{yer.name}</b>
                    {yer.subtitle && <small>{yer.subtitle}</small>}
                  </span>
                </button>
              ))}
              {arama.trim().length >= 2 && !oneriYukleniyor && oneriler.length === 0 && (
                <div className="haritaAramaOneriBos">{mt(lang, "Sonuç bulunamadı")}</div>
              )}
            </div>
          )}
        </div>
        <div ref={divRef} className="haritaLeafletWrap noSwipeBack" data-no-swipe-back="true" style={{height:360,width:"100%",borderRadius:16,overflow:"hidden",border:"1px solid var(--border)",marginBottom:10}} />
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
          <button className="secondaryButton" type="button" onClick={mevcutKonumuKullan} style={{flex:1}}>{`📍 ${mt(lang, "Mevcut Konumumu Kullan")}`}</button>
          <button className="secondaryButton" type="button" onClick={()=>{ if (mapRef.current) { const c=mapRef.current.getCenter(); markerGuncelle(c.lat, c.lng, mapRef.current.getZoom() || 16); } }} style={{flex:1}}>{`🎯 ${mt(lang, "Harita Ortasını Seç")}`}</button>
        </div>
        {secili&&(
          <div style={{fontSize:11,color:"var(--text-secondary)",padding:"8px 10px",background:"rgba(99,102,241,0.10)",border:"1px solid rgba(99,102,241,0.18)",borderRadius:10,marginBottom:10}}>
            {mt(lang, "Seçilen konum:")} <b>{secili.lat.toFixed(6)}</b>, <b>{secili.lng.toFixed(6)}</b>
          </div>
        )}
        {hata&&<div style={{fontSize:12,color:"#ef4444",padding:"8px 10px",background:"rgba(239,68,68,0.08)",borderRadius:8,marginBottom:10}}>⚠️ {hata}</div>}
        <button className="primaryButton" type="button" disabled={!secili} onClick={()=>{ if(secili){ onSec?.(secili); onKapat?.(); } }}>✅ {mt(lang, "Bu Konumu Kullan")}</button>
      </div>
    </div>, document.body
  );
}

/* ═══════════════════════════════════════════════════
   YER MODALI
═══════════════════════════════════════════════════ */
function YerModal({ mevcut, onKaydet, onKapat, onHaritadanSec, mevcutKonum, lang="tr", uyeler=[] }) {
  const [ad,setAd]=useState(mevcut?.ad||""); const [kategori,setKategori]=useState(mevcut?.kategori||"ev");
  const [adres,setAdres]=useState(mevcut?.adres||""); const [lat,setLat]=useState(mevcut?.lat||""); const [lng,setLng]=useState(mevcut?.lng||"");
  const [yukleniyor,setYukleniyor]=useState(false); const [hata,setHata]=useState("");
  // Bildirim ayarları
  const [bildirimAktif,setBildirimAktif]=useState(false);
  const [bildirimYaricap,setBildirimYaricap]=useState(300);
  const [bildirimUyeUids,setBildirimUyeUids]=useState([]);
  const toggleBildirimUye=(uid)=>setBildirimUyeUids(prev=>prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]);
  const konumAl=async()=>{
    setHata("");
    try {
      const p = await getAppLocationOnce({ requestPermission:true, timeout:10000, maximumAge:5000 });
      if (!p?.lat || !p?.lng) throw new Error("LOCATION_EMPTY");
      setLat(p.lat.toFixed(6)); setLng(p.lng.toFixed(6));
    } catch (err) {
      setHata(formatLocationError(err, lang));
    }
  };
  const haritadanSec=()=>{
    const baslangicKonum = gecerliKonumSec(
      { lat, lng },
      mevcutKonum
    );

    const modalAc = (ilkKonum) => onHaritadanSec?.({
      baslik:`📍 ${mt(lang, "Yer Konumu Seç")}`,
      aciklama:mt(lang, "Eklemek istediğiniz ortak yeri haritada arayın veya doğrudan haritaya dokunun."),
      ilkKonum,
      onSec:({lat:secLat,lng:secLng})=>{setLat(secLat.toFixed(6));setLng(secLng.toFixed(6));},
    });

    if (baslangicKonum) {
      modalAc(baslangicKonum);
      return;
    }

    // Yeni Yer Ekle modalında kayıtlı koordinat olmayabilir. Bu durumda seçim haritasını
    // web konum izni açmadan native servis ile mevcut konuma yakın açıyoruz.
    setHata(mt(lang, "Konum alınıyor…"));
    getAppLocationOnce({ requestPermission:false, timeout:6000, maximumAge:60000 })
      .then((p)=>{
        setHata("");
        modalAc(p?.lat && p?.lng ? { lat:p.lat, lng:p.lng } : null);
      })
      .catch(()=>{
        setHata("");
        modalAc(null);
      });
    return;
  };
  const kaydet=async()=>{ 
    if(yukleniyor) return; // double-submit önle
    if(!ad.trim())return appAlert(mt(lang, "Yer adı boş olamaz.")); 
    setYukleniyor(true);
    try {
      await onKaydet({ad:ad.trim(),kategori,adres:adres.trim(),lat:lat?Number(lat):null,lng:lng?Number(lng):null,
        bildirimAktif,bildirimYaricap,bildirimUyeUids});
      // onKaydet içinde setModalAcik(false) çağrılır, ek kapanma gerekmez
    } catch(e) {
      console.error("Yer kayıt hatası:", e);
      setHata(lang === "en" ? "Couldn’t save. Please try again." : "Kaydedilemedi. Lütfen tekrar deneyin.");
      throw e;
    } finally {
      setYukleniyor(false);
    }
  };
  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onKapat}>
      <div className="haritaModalIcerik" onClick={e=>e.stopPropagation()} style={{maxHeight:"90vh",overflowY:"auto",width:"100%",maxWidth:480}}>
        <div className="haritaModalBaslik"><span>{mevcut ? mt(lang, "Yeri Düzenle") : mt(lang, "Yeni Yer Ekle")}</span><button className="haritaModalKapat" onClick={onKapat}>×</button></div>
        <div className="darkLabel">{mt(lang, "Kategori")}</div>
        <div className="haritaKategoriGrid">{KATEGORI_LISTESI.map(k=>(
          <button key={k.key} className={`haritaKategoriBtn${kategori===k.key?" secili":""}`} onClick={()=>setKategori(k.key)}><span className="ikon">{k.ikon}</span>{mt(lang, k.label)}</button>
        ))}</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Yer Adı")}</label><input className="darkInput" placeholder={mt(lang, "Örn: Ev, Okul…")} value={ad} onChange={e=>setAd(e.target.value)}/></div>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Adres")}</label><input className="darkInput" placeholder={mt(lang, "Mahalle, cadde…")} value={adres} onChange={e=>setAdres(e.target.value)}/></div>
          <div className="formRow">
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Enlem")}</label><input className="darkInput" placeholder="41.0082" value={lat} onChange={e=>setLat(e.target.value)} type="number" step="any"/></div>
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Boylam")}</label><input className="darkInput" placeholder="28.9784" value={lng} onChange={e=>setLng(e.target.value)} type="number" step="any"/></div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="secondaryButton" type="button" onClick={konumAl} style={{flex:1}}>{`📍 ${mt(lang, "Mevcut Konumumu Kullan")}`}</button><button className="secondaryButton" type="button" onClick={haritadanSec} style={{flex:1}}>🗺️ {mt(lang, "Haritadan Seç")}</button></div>
          {hata&&<div style={{fontSize:12,color:"#ef4444",padding:"8px 10px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>⚠️ {hata}</div>}

          {/* Bildirim seçeneği */}
          {!mevcut&&(
            <div style={{background:bildirimAktif?"rgba(99,102,241,0.08)":"rgba(255,255,255,0.04)",border:`1px solid ${bildirimAktif?"rgba(99,102,241,0.4)":"var(--border)"}`,borderRadius:"var(--r-md)",padding:"12px 14px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>setBildirimAktif(v=>!v)}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:bildirimAktif?"var(--text-accent)":"var(--text-primary)"}}>
                    🔔 {lang==="en"?"Enable Notifications":"Bildirimleri Aktif Et"}
                  </div>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
                    {lang==="en"?"Get notified when family members arrive or leave":"Aile üyeleri bu yere gelip gidince haber al"}
                  </div>
                </div>
                <div style={{width:42,height:24,borderRadius:12,background:bildirimAktif?"var(--brand-1)":"rgba(255,255,255,0.12)",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:bildirimAktif?18:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
                </div>
              </div>

              {bildirimAktif&&(
                <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:12}}>
                  {/* Yarıçap slider — tek satır */}
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <span style={{fontSize:11,fontWeight:700,color:"var(--text-secondary)"}}>📏 {lang==="en"?"Radius":"Yarıçap"}</span>
                      <span style={{fontSize:12,fontWeight:800,color:"var(--text-accent)"}}>{bildirimYaricap>=1000?`${bildirimYaricap/1000} km`:`${bildirimYaricap} m`}</span>
                    </div>
                    <input type="range" min={50} max={2000} step={50} value={bildirimYaricap}
                      onChange={e=>setBildirimYaricap(Number(e.target.value))}
                      style={{width:"100%",accentColor:"var(--brand-1)"}}/>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text-muted)",marginTop:2}}>
                      <span>50 m</span><span>2 km</span>
                    </div>
                  </div>

                  {/* Üye seçimi slider — tek satır yatay scroll */}
                  {uyeler.length>0&&(
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--text-secondary)",marginBottom:6}}>
                        👥 {lang==="en"?"Who to track":"Takip Edilecek Üyeler"}
                        <span style={{fontWeight:400,marginLeft:6,color:"var(--text-muted)"}}>({bildirimUyeUids.length===0?(lang==="en"?"Everyone":"Tüm Aile"):bildirimUyeUids.length+" "+(lang==="en"?"selected":"seçili")})</span>
                      </div>
                      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,scrollbarWidth:"none"}}>
                        <button onClick={()=>setBildirimUyeUids([])}
                          style={{flexShrink:0,padding:"5px 12px",borderRadius:"var(--r-full)",border:`1px solid ${bildirimUyeUids.length===0?"var(--brand-1)":"var(--border)"}`,background:bildirimUyeUids.length===0?"rgba(99,102,241,0.15)":"transparent",color:bildirimUyeUids.length===0?"var(--text-accent)":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                          👨‍👩‍👧 {lang==="en"?"All":"Tümü"}
                        </button>
                        {uyeler.map(u=>{const uid=u.uid||u.id;const secili=bildirimUyeUids.includes(uid);return(
                          <button key={uid} onClick={()=>toggleBildirimUye(uid)}
                            style={{flexShrink:0,padding:"5px 12px",borderRadius:"var(--r-full)",border:`1px solid ${secili?"var(--brand-1)":"var(--border)"}`,background:secili?"rgba(99,102,241,0.15)":"transparent",color:secili?"var(--text-accent)":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                            {secili?"✓ ":""}{u.ad||mt(lang,"Üye")}
                          </button>
                        );})}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <AsyncActionButton className="primaryButton" onAction={kaydet} externalBusy={yukleniyor} busyText={mt(lang, "Kaydediliyor…")} disabled={yukleniyor}>
            {mevcut ? mt(lang, "Güncelle") : mt(lang, "Ekle")}
          </AsyncActionButton>
        </div>
      </div>
    </div>, document.body);
}

/* ═══════════════════════════════════════════════════
   GEOFENCE MODALI
═══════════════════════════════════════════════════ */
function GeofenceModal({ onKaydet, onKapat, mevcutKonum, uyeler = [], onHaritadanSec, lang="tr" }) {
  const [ad,setAd]=useState(""); const [yaricap,setYaricap]=useState(500);
  const [lat,setLat]=useState(mevcutKonum?.lat||""); const [lng,setLng]=useState(mevcutKonum?.lng||"");
  const [renk,setRenk]=useState("#6366f1");
  // DÜZELTME: Varsayılan "ikisi" (her ikisi de) yapıldı. Eskiden "giris" (sadece
  // giriş) varsayılandı — kullanıcı "Her İkisi"yi açıkça seçmezse, sınır
  // sessizce "sadece giriş" olarak kaydediliyor ve çıkış bildirimi hiç
  // gelmiyordu (kullanıcı bunu fark etmiyordu, çünkü buton zaten "seçili"
  // görünüyordu — sadece yanlış seçenek seçiliydi).
  const [tur,setTur]=useState("ikisi");
  const [seciliUidler,setSeciliUidler]=useState([]);
  const toggleUye=(uid)=>setSeciliUidler(prev=>prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]);
  const [isChildTracking,setIsChildTracking]=useState(false);
  const [childCategory,setChildCategory]=useState(null);
  const RENKLER=["#6366f1","#10b981","#ef4444","#f59e0b","#06b6d4","#ec4899"];
  const konumAl=async()=>{try{const p=await getAppLocationOnce({requestPermission:true,timeout:10000,maximumAge:5000});if(p?.lat&&p?.lng){setLat(p.lat.toFixed(6));setLng(p.lng.toFixed(6));}}catch(_){}};
  const haritadanSec=()=>onHaritadanSec?.({
    baslik:`🔔 ${mt(lang, "Sınır Merkezi Seç")}`,
    aciklama:mt(lang, "Coğrafi sınırın merkezini haritada arayın veya dokunarak seçin."),
    ilkKonum:{lat,lng},
    onSec:({lat:secLat,lng:secLng})=>{setLat(secLat.toFixed(6));setLng(secLng.toFixed(6));},
  });
  // Çocuk kategorisi seçince adı ve yarıçapı otomatik doldur
  const selectChildCat = (cat) => {
    setChildCategory(cat.key);
    if(!ad.trim()) setAd(cat.emoji + " " + (lang==="en" ? cat.en : cat.tr));
    setYaricap(cat.defaultR);
  };
  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onKapat}>
      <div className="haritaModalIcerik" onClick={e=>e.stopPropagation()} style={{maxHeight:"90vh",overflowY:"auto",width:"100%",maxWidth:480}}>
        <div className="haritaModalBaslik"><span>🔔 {mt(lang, "Coğrafi Sınır Ekle")}</span><button className="haritaModalKapat" onClick={onKapat}>×</button></div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Sınır Adı")}</label><input className="darkInput" placeholder={mt(lang, "Örn: Okul Bölgesi…")} value={ad} onChange={e=>setAd(e.target.value)}/></div>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Yarıçap")}</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {GEOFENCE_YARICAPLAR.map(r=>(
                <button key={r} onClick={()=>setYaricap(r)} style={{padding:"5px 12px",borderRadius:"var(--r-full)",border:`1px solid ${yaricap===r?"var(--brand-1)":"var(--border)"}`,background:yaricap===r?"rgba(99,102,241,0.15)":"transparent",color:yaricap===r?"var(--text-accent)":"var(--text-secondary)",fontSize:12,cursor:"pointer"}}>
                  {r>=1000?`${r/1000} km`:`${r} m`}
                </button>
              ))}
            </div>
          </div>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Bildirim Türü")}</label>
            <div style={{display:"flex",gap:8}}>
              {[["giris","🚪 Giriş"],["cikis","🚶 Çıkış"],["ikisi","🔄 Her İkisi"]].map(([v,l])=>(
                <button key={v} onClick={()=>setTur(v)} style={{flex:1,padding:"7px 6px",borderRadius:"var(--r-sm)",border:`1px solid ${tur===v?"var(--brand-1)":"var(--border)"}`,background:tur===v?"rgba(99,102,241,0.15)":"transparent",color:tur===v?"var(--text-accent)":"var(--text-secondary)",fontSize:11,cursor:"pointer"}}>{mt(lang, l)}</button>
              ))}
            </div>
          </div>
          {/* Çocuk Takibi Toggle */}
          <div style={{background:isChildTracking?"rgba(251,191,36,0.08)":"rgba(255,255,255,0.04)",border:`1px solid ${isChildTracking?"rgba(251,191,36,0.35)":"var(--border)"}`,borderRadius:"var(--r-md)",padding:"12px 14px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>setIsChildTracking(v=>!v)}>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:isChildTracking?"#fbbf24":"var(--text-primary)"}}>
                  👶 {lang==="en"?"Child Tracking Mode":"Çocuk Takibi Modu"}
                </div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
                  {lang==="en"?"Warm notifications when child arrives/leaves":"Çocuk alana girip çıkınca sıcak bildirimler"}
                </div>
              </div>
              <div style={{width:42,height:24,borderRadius:12,background:isChildTracking?"#fbbf24":"rgba(255,255,255,0.12)",position:"relative",transition:"background .2s",flexShrink:0}}>
                <div style={{position:"absolute",top:3,left:isChildTracking?18:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
              </div>
            </div>
            {isChildTracking && (
              <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
                {/* Yer Kategorisi */}
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:"#fbbf24",marginBottom:6}}>
                    {lang==="en"?"Location Type":"Yer Türü"}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {COCUK_KATEGORILER.map(cat=>(
                      <button key={cat.key} onClick={()=>selectChildCat(cat)}
                        style={{padding:"6px 10px",borderRadius:"var(--r-full)",
                          border:`1px solid ${childCategory===cat.key?"#fbbf24":"var(--border)"}`,
                          background:childCategory===cat.key?"rgba(251,191,36,0.15)":"transparent",
                          color:childCategory===cat.key?"#fbbf24":"var(--text-secondary)",
                          fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                        {cat.emoji} {lang==="en"?cat.en:cat.tr}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Takip Edilecek Üyeler")}</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>setSeciliUidler([])} style={{padding:"6px 10px",borderRadius:"var(--r-full)",border:`1px solid ${seciliUidler.length===0?"var(--brand-1)":"var(--border)"}`,background:seciliUidler.length===0?"rgba(99,102,241,0.15)":"transparent",color:seciliUidler.length===0?"var(--text-accent)":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer"}}>{mt(lang, "Tüm Aile")}</button>
              {uyeler.map(u=>{const uid=u.uid||u.id;const secili=seciliUidler.includes(uid);const isCocuk=u.rol==="Çocuk"||u.rol==="Bebek";return(
                <button key={uid} onClick={()=>toggleUye(uid)} style={{padding:"6px 10px",borderRadius:"var(--r-full)",border:`1px solid ${secili?"var(--brand-1)":"var(--border)"}`,background:secili?"rgba(99,102,241,0.15)":"transparent",color:secili?"var(--text-accent)":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {isCocuk?"👶 ":""}{secili?"✓ ":""}{u.ad || mt(lang, "Üye")}
                </button>
              );})}
            </div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:5}}>{mt(lang, "Seçim yapmazsan tüm aile üyeleri takip edilir.")}</div>
          </div>
          <div className="inputLabelGroup"><label className="darkLabel">{lang === "en" ? "Color" : "Renk"}</label>
            <div style={{display:"flex",gap:8}}>{RENKLER.map(r=>(
              <button key={r} onClick={()=>setRenk(r)} style={{width:26,height:26,borderRadius:"50%",background:r,border:`2.5px solid ${renk===r?"#fff":"transparent"}`,cursor:"pointer"}}/>
            ))}</div>
          </div>
          <div className="formRow">
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Enlem")}</label><input className="darkInput" placeholder="41.0082" value={lat} onChange={e=>setLat(e.target.value)} type="number" step="any"/></div>
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Boylam")}</label><input className="darkInput" placeholder="28.9784" value={lng} onChange={e=>setLng(e.target.value)} type="number" step="any"/></div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="secondaryButton" type="button" onClick={konumAl} style={{flex:1}}>📍 {mt(lang, "Mevcut Konumu Kullan")}</button><button className="secondaryButton" type="button" onClick={haritadanSec} style={{flex:1}}>🗺️ {mt(lang, "Haritadan Seç")}</button></div>
          <AsyncActionButton className="primaryButton" busyText={mt(lang, "Kaydediliyor…")} onAction={()=>{if(!ad.trim()||!lat||!lng)return appAlert(mt(lang, "Ad ve konum zorunludur."));return onKaydet({ad:ad.trim(),yaricap,lat:Number(lat),lng:Number(lng),renk,tur,uyeUids:seciliUidler,uyeUid:seciliUidler.length===1?seciliUidler[0]:null,isChildTracking,childCategory:isChildTracking?childCategory:null,childCategoryEmoji:isChildTracking?(COCUK_KATEGORILER.find(c=>c.key===childCategory)?.emoji||"👶"):null});}}>{mt(lang, "Coğrafi Sınır Oluştur")}</AsyncActionButton>
        </div>
      </div>
    </div>, document.body);
}

/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═/* ═
   GÜVENLİ ALAN MODALI
═══════════════════════════════════════════════════ */
function GuvenliAlanModal({ onKaydet, onKapat, mevcutKonum, uyeler, onHaritadanSec, lang="tr" }) {
  const [ad,setAd]=useState(""); const [yaricap,setYaricap]=useState(300);
  const [lat,setLat]=useState(mevcutKonum?.lat||""); const [lng,setLng]=useState(mevcutKonum?.lng||"");
  const [seciliUidler,setSeciliUidler]=useState([]);
  const toggleUye=(uid)=>setSeciliUidler(prev=>prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]);
  const [isChildTracking,setIsChildTracking]=useState(false);
  const [childCategory,setChildCategory]=useState(null);
  const YARICAPLAR=[100,200,300,500,1000,2000];
  const konumAl=async()=>{try{const p=await getAppLocationOnce({requestPermission:true,timeout:10000,maximumAge:5000});if(p?.lat&&p?.lng){setLat(p.lat.toFixed(6));setLng(p.lng.toFixed(6));}}catch(_){}};
  const haritadanSec=()=>onHaritadanSec?.({
    baslik:`🟢 ${mt(lang, "Güvenli Alan Merkezi Seç")}`,
    aciklama:mt(lang, "Güvenli alanın merkezini haritada arayın veya dokunarak seçin."),
    ilkKonum:{lat,lng},
    onSec:({lat:secLat,lng:secLng})=>{setLat(secLat.toFixed(6));setLng(secLng.toFixed(6));},
  });
  const selectChildCat = (cat) => {
    setChildCategory(cat.key);
    if(!ad.trim()) setAd(cat.emoji + " " + (lang==="en" ? cat.en : cat.tr));
    setYaricap(cat.defaultR);
  };
  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onKapat}>
      <div className="haritaModalIcerik" onClick={e=>e.stopPropagation()} style={{maxHeight:"90vh",overflowY:"auto",width:"100%",maxWidth:480}}>
        <div className="haritaModalBaslik"><span>🟢 {mt(lang, "Güvenli Alan Ekle")}</span><button className="haritaModalKapat" onClick={onKapat}>×</button></div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Alan Adı")}</label><input className="darkInput" placeholder={mt(lang, "Örn: Ev Çevresi…")} value={ad} onChange={e=>setAd(e.target.value)}/></div>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Yarıçap")}</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {YARICAPLAR.map(r=>(
                <button key={r} onClick={()=>setYaricap(r)} style={{padding:"5px 12px",borderRadius:"var(--r-full)",border:`1px solid ${yaricap===r?"var(--brand-1)":"var(--border)"}`,background:yaricap===r?"rgba(16,185,129,0.15)":"transparent",color:yaricap===r?"#10b981":"var(--text-secondary)",fontSize:12,cursor:"pointer"}}>
                  {r>=1000?`${r/1000} km`:`${r} m`}
                </button>
              ))}
            </div>
          </div>
          {/* Çocuk Takibi Toggle */}
          <div style={{background:isChildTracking?"rgba(251,191,36,0.08)":"rgba(255,255,255,0.04)",border:`1px solid ${isChildTracking?"rgba(251,191,36,0.35)":"var(--border)"}`,borderRadius:"var(--r-md)",padding:"12px 14px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>setIsChildTracking(v=>!v)}>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:isChildTracking?"#fbbf24":"var(--text-primary)"}}>
                  👶 {lang==="en"?"Child Tracking Mode":"Çocuk Takibi Modu"}
                </div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
                  {lang==="en"?"Warm notifications when child arrives/leaves":"Çocuk alana girip çıkınca sıcak bildirimler"}
                </div>
              </div>
              <div style={{width:42,height:24,borderRadius:12,background:isChildTracking?"#fbbf24":"rgba(255,255,255,0.12)",position:"relative",transition:"background .2s",flexShrink:0}}>
                <div style={{position:"absolute",top:3,left:isChildTracking?18:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
              </div>
            </div>
            {isChildTracking && (
              <div style={{marginTop:12}}>
                <div style={{fontSize:11,fontWeight:700,color:"#fbbf24",marginBottom:6}}>
                  {lang==="en"?"Location Type":"Yer Türü"}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {COCUK_KATEGORILER.map(cat=>(
                    <button key={cat.key} onClick={()=>selectChildCat(cat)}
                      style={{padding:"6px 10px",borderRadius:"var(--r-full)",
                        border:`1px solid ${childCategory===cat.key?"#fbbf24":"var(--border)"}`,
                        background:childCategory===cat.key?"rgba(251,191,36,0.15)":"transparent",
                        color:childCategory===cat.key?"#fbbf24":"var(--text-secondary)",
                        fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                      {cat.emoji} {lang==="en"?cat.en:cat.tr}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Takip Edilecek Üyeler")}</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>setSeciliUidler([])} style={{padding:"6px 10px",borderRadius:"var(--r-full)",border:`1px solid ${seciliUidler.length===0?"#10b981":"var(--border)"}`,background:seciliUidler.length===0?"rgba(16,185,129,0.15)":"transparent",color:seciliUidler.length===0?"#10b981":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer"}}>{mt(lang, "Tüm Aile")}</button>
              {uyeler&&uyeler.map(u=>{const uid=u.uid||u.id;const secili=seciliUidler.includes(uid);const isCocuk=u.rol==="Çocuk"||u.rol==="Bebek";return(
                <button key={uid} onClick={()=>toggleUye(uid)} style={{padding:"6px 10px",borderRadius:"var(--r-full)",border:`1px solid ${secili?"#10b981":"var(--border)"}`,background:secili?"rgba(16,185,129,0.15)":"transparent",color:secili?"#10b981":"var(--text-secondary)",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {isCocuk?"👶 ":""}{secili?"✓ ":""}{u.ad || mt(lang, "Üye")}
                </button>
              );})}
            </div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:5}}>{mt(lang, "Seçim yapmazsan tüm aile üyeleri takip edilir.")}</div>
          </div>
          <div className="formRow">
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Enlem")}</label><input className="darkInput" placeholder="41.0082" value={lat} onChange={e=>setLat(e.target.value)} type="number" step="any"/></div>
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Boylam")}</label><input className="darkInput" placeholder="28.9784" value={lng} onChange={e=>setLng(e.target.value)} type="number" step="any"/></div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="secondaryButton" type="button" onClick={konumAl} style={{flex:1}}>📍 {mt(lang, "Mevcut Konumu Kullan")}</button><button className="secondaryButton" type="button" onClick={haritadanSec} style={{flex:1}}>🗺️ {mt(lang, "Haritadan Seç")}</button></div>
          <AsyncActionButton className="primaryButton" busyText={mt(lang, "Kaydediliyor…")} onAction={()=>{if(!ad.trim()||!lat||!lng)return appAlert(mt(lang, "Ad ve konum zorunludur."));return onKaydet({ad:ad.trim(),yaricap,lat:Number(lat),lng:Number(lng),uyeUids:seciliUidler,uyeUid:seciliUidler.length===1?seciliUidler[0]:null});}}>🟢 {mt(lang, "Güvenli Alan Oluştur")}</AsyncActionButton>
        </div>
      </div>
    </div>, document.body);
}

/* ═══════════════════════════════════════════════════
   BULUŞMA MODALI
═══════════════════════════════════════════════════ */
function BulusmaModal({ onKaydet, onKapat, yerler, onHaritadanSec, mevcutKonum, lang="tr" }) {
  const [ad,setAd]=useState(""); const [lat,setLat]=useState(""); const [lng,setLng]=useState("");
  const konumAl=async()=>{try{const p=await getAppLocationOnce({requestPermission:true,timeout:10000,maximumAge:5000});if(p?.lat&&p?.lng){setLat(p.lat.toFixed(6));setLng(p.lng.toFixed(6));}}catch(_){}};
  const haritadanSec=()=>onHaritadanSec?.({
    baslik:`🤝 ${mt(lang, "Buluşma Noktası Seç")}`,
    aciklama:mt(lang, "Buluşma noktasını haritada arayın veya dokunarak seçin."),
    ilkKonum:{lat,lng},
    onSec:({lat:secLat,lng:secLng})=>{setLat(secLat.toFixed(6));setLng(secLng.toFixed(6));},
  });
  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"flex-end",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onKapat}>
      <div className="haritaModalIcerik" onClick={e=>e.stopPropagation()} style={{maxHeight:"85vh",overflowY:"auto",width:"100%",maxWidth:480}}>
        <div className="haritaModalBaslik"><span>🤝 {mt(lang, "Buluşma Noktası")}</span><button className="haritaModalKapat" onClick={onKapat}>×</button></div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {yerler.length>0&&(
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Kayıtlı Yerden Seç")}</label>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:"220px",overflowY:"auto"}}>
                {yerler.map(yer=>{ const kat=kategoriData(yer.kategori); return(
                  <button key={yer.id} onClick={()=>{setAd(yer.ad);setLat(yer.lat);setLng(yer.lng);}}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:ad===yer.ad?"rgba(99,102,241,0.12)":"rgba(255,255,255,0.04)",border:`1px solid ${ad===yer.ad?"var(--brand-1)":"var(--border)"}`,borderRadius:"var(--r-sm)",cursor:"pointer",textAlign:"left"}}>
                    <span style={{fontSize:20}}>{kat.ikon}</span>
                    <div><div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{yer.ad}</div><div style={{fontSize:11,color:"var(--text-secondary)"}}>{yer.adres||mt(lang, kat.label)}</div></div>
                  </button>);
                })}
              </div>
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:8,color:"var(--text-muted)",fontSize:11}}>
            <div style={{flex:1,height:1,background:"var(--border)"}}/>{mt(lang, "veya manuel gir")}<div style={{flex:1,height:1,background:"var(--border)"}}/>
          </div>
          <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Yer Adı")}</label><input className="darkInput" placeholder={mt(lang, "Buluşma noktası adı…")} value={ad} onChange={e=>setAd(e.target.value)}/></div>
          <div className="formRow">
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Enlem")}</label><input className="darkInput" placeholder="41.0082" value={lat} onChange={e=>setLat(e.target.value)} type="number" step="any"/></div>
            <div className="inputLabelGroup"><label className="darkLabel">{mt(lang, "Boylam")}</label><input className="darkInput" placeholder="28.9784" value={lng} onChange={e=>setLng(e.target.value)} type="number" step="any"/></div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="secondaryButton" type="button" onClick={konumAl} style={{flex:1}}>📍 {mt(lang, "Şu Anki Konumu Kullan")}</button><button className="secondaryButton" type="button" onClick={haritadanSec} style={{flex:1}}>🗺️ {mt(lang, "Haritadan Seç")}</button></div>
          <AsyncActionButton className="primaryButton" busyText={mt(lang, "Kaydediliyor…")} onAction={()=>{if(!ad.trim()||!lat||!lng)return appAlert(mt(lang, "Ad ve konum zorunludur."));return onKaydet({ad:ad.trim(),lat:Number(lat),lng:Number(lng)});}}>📍 {mt(lang, "Buluşma Noktası Belirle")}</AsyncActionButton>
        </div>
      </div>
    </div>, document.body);
}

/* ═══════════════════════════════════════════════════
   ANA COMPONENT
═══════════════════════════════════════════════════ */
function HaritaPage({ familyId, user, tamAd, uyeler, yoneticiMi, permissions,
  bildirimEkleService, bildirimHerkeseYazService, bildirimGoster, uyelerRef,
  isPremium = false, premiumConfig = {}, onPremiumLock, onPremiumLimitHit }) {
  const { lang } = useLanguage();
  const [konumlar, setKonumlar]         = useState({});
  const [resolvedMapAvatars, setResolvedMapAvatars] = useState({});
  const [yerler, setYerler]             = useState([]);
  const [paylasimAcik, setPaylasimAcik] = useState(false);
  const [seciliUyeUid, setSeciliUyeUid] = useState(null);
  const [takipModu, setTakipModu]         = useState(false); // seçili üyeyi canlı takip et
  const [konumBgBanner, setKonumBgBanner] = useState(false);
  const [navModal, setNavModal]           = useState(null); // {lat,lng,label,uygulamalar} // arka plan konum uyarısı
  const [modalAcik, setModalAcik]       = useState(false);
  const [duzenleYer, setDuzenleYer]     = useState(null);
  const [manuelKonumAliniyor, setManuelKonumAliniyor] = useState(false);
  const [paylasimBaslatiliyor, setPaylasimBaslatiliyor] = useState(false);
  const [paylasimIslemiSuruyor, setPaylasimIslemiSuruyor] = useState(false);
  const [konumHata, setKonumHata]       = useState("");
  const [haritaTuru, setHaritaTuru]     = useState(() => kayitliHaritaTuruGetir());
  const haritaTuruDegistir = useCallback((tur) => {
    const guvenliTur = GECERLI_HARITA_TURLERI.has(tur) ? tur : "sokak";
    setHaritaTuru(guvenliTur);
    haritaTuruKaydet(guvenliTur);
  }, []);
  const leafletFallbackVarsayilaniUygula = useCallback(() => {
    if (!kayitliHaritaTuruVarMi()) setHaritaTuru("uydu");
  }, []);

  const [aktifPanel, setAktifPanel]     = useState(null);
  const [geofencelar, setGeofencelar]   = useState([]);
  const [geofenceModal, setGeofenceModal] = useState(false);
  const [bulusmaNoktasi, setBulusmaNoktasi] = useState(null);
  const [bulusmaModal, setBulusmaModal] = useState(false);
  const [yakinlar, setYakinlar]         = useState([]);
  // DÜZELTME: Banner'ın görünürlüğü artık `yakinlar` verisinden BAĞIMSIZ.
  // `yakinlar` arka planda periyodik olarak yenilendiği için (konum polling),
  // banner'ı görünürlüğe doğrudan bağlamak demek her yenilemede geri gelmesi
  // demekti — X ile kapatmak sadece bir sonraki yenilemeye kadar geçerliydi.
  const [yakinlikBannerAcik, setYakinlikBannerAcik] = useState(false);
  const yakinlikBannerGosterildiRef = useRef(false); // bu sayfa ziyaretinde bir kez gösterildi mi?
  const haritaMountedRef = useRef(true);
  useEffect(() => {
    haritaMountedRef.current = true;
    return () => { haritaMountedRef.current = false; };
  }, []); // sadece mount/unmount'ta çalışır — banner auto-dismiss timer'ının unmount sonrası state güncellemesi yapmasını önler

  useEffect(() => {
    let active = true;
    const customMembers = (uyeler || []).map((member) => {
      const uid = String(member?.uid || member?.id || "").trim();
      const avatarInfo = haritaAvatarBilgisi(member?.avatar);
      if (!uid || !avatarInfo?.custom) return null;
      const fileKey = member?.profilePictureFileKey || member?.profile_picture_file_key || "";
      return {
        uid,
        src: avatarInfo.src,
        cacheKey: fileKey ? `profile-avatar:${uid}:${fileKey}` : "",
      };
    }).filter(Boolean);

    if (!customMembers.length) {
      setResolvedMapAvatars({});
      return () => { active = false; };
    }

    Promise.all(customMembers.map(async ({ uid, src, cacheKey }) => {
      try {
        return [uid, await loadPersistentImage(src, cacheKey)];
      } catch {
        return [uid, src];
      }
    })).then((entries) => {
      if (active) setResolvedMapAvatars(Object.fromEntries(entries));
    });
    return () => { active = false; };
  }, [uyeler]);

  const mapUyeler = (uyeler || []).map((member) => {
    const uid = String(member?.uid || member?.id || "");
    const resolvedAvatar = resolvedMapAvatars[uid] || "";
    const customAvatar = haritaAvatarBilgisi(member?.avatar)?.custom === true;
    return {
      ...member,
      _resolvedMapAvatar: resolvedAvatar,
      _mapAvatarPending: customAvatar && !resolvedAvatar,
    };
  });

  useEffect(() => {
    if (!konumBgBanner) return undefined;
    const timer = setTimeout(() => setKonumBgBanner(false), 5000);
    return () => clearTimeout(timer);
  }, [konumBgBanner]);

  const [konumSuresiDk, setKonumSuresiDk] = useState(0);
  const sureBitimRef = useRef(null);
  const sureBitimVersionRef = useRef(0);
  const nativeLocationStopPromiseRef = useRef(null);
  const paylasimGecisPromiseRef = useRef(null);
  const paylasimYazimiAktifRef = useRef(false);
  const paylasimKapatmaBekliyorRef = useRef(bekleyenKonumKapatmaOku(familyId, user?.uid));
  const konumAlCalisiyorRef = useRef(false);
  const userZoomRef = useRef(null); // Kullanıcının son ayarladığı zoom seviyesi
  const mapRef       = useRef(null);
  const haritaKartRef = useRef(null);
  useEffect(() => {
    paylasimKapatmaBekliyorRef.current = bekleyenKonumKapatmaOku(familyId, user?.uid);
  }, [familyId, user?.uid]);
  // Mevcut kullanıcının kartı her zaman ilk sırada, geri kalan sıralama korunur
  const siraUyeler = (() => {
    const sirali = uyelerSirala(uyeler);
    const benimUid = user?.uid;
    if (!benimUid) return sirali;
    const benimKartim = sirali.find(u => (u.uid || u.id) === benimUid);
    const diğerleri   = sirali.filter(u => (u.uid || u.id) !== benimUid);
    return benimKartim ? [benimKartim, ...diğerleri] : sirali;
  })();
  // Backend yalnız kullanıcının kendisine ve o anda aktif paylaşımı bulunan
  // aile üyelerine geçmiş/analiz erişimi verir. Seçiciler aynı kuralı izler.
  const gecmisGorunurUyeler = siraUyeler.filter((uye) => {
    const uid = String(uye.uid || uye.id || "");
    return uid === String(user?.uid || "") || konumlar[uid]?.paylasimAcik === true;
  });
  const gecmisGorunurUyeKimlikleri = gecmisGorunurUyeler.map((uye) => String(uye.uid || uye.id || "")).join("|");
  const [sosAktif, setSosAktif]             = useState(false);
  const [sosAlertler, setSosAlertler]       = useState([]);
  const [gecmisUyeUid, setGecmisUyeUid]       = useState(null);
  const [konumGecmisi, setKonumGecmisi]       = useState([]);
  const [gecmisYukleniyor, setGecmisYukleniyor] = useState(false);
  const [gecmisTarih, setGecmisTarih]         = useState(() => bugununTarihi());
  const [seciliRotaIdx, setSeciliRotaIdx]     = useState(null); // tıklanan nokta indeksi
  const [guvenliAlanlar, setGuvenliAlanlar] = useState([]);
  const [guvenliAlanModal, setGuvenliAlanModal] = useState(false);
  const [hareketAnalizi, setHareketAnalizi] = useState([]);
  const [analizUyeUid, setAnalizUyeUid]   = useState(null);
  const [analizYukleniyor, setAnalizYukleniyor] = useState(false);
  const [haritaArama, setHaritaArama] = useState("");
  const [haritaAraniyor, setHaritaAraniyor] = useState(false);
  const [haritaOnerileri, setHaritaOnerileri] = useState([]);
  const [haritaOneriYukleniyor, setHaritaOneriYukleniyor] = useState(false);
  const [haritaAramaOdak, setHaritaAramaOdak] = useState(false);
  const haritaAramaAlanRef = useRef(null);
  const [sonHaritaAramalari, setSonHaritaAramalari] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aileHaritaSonAramalar") || "[]").slice(0, 3); } catch (_) { return []; }
  });
  const gecmisUnsubRef = useRef(null);
  const hariciHaritaAcilisRef = useRef(false);
  const [haritaSecimModal, setHaritaSecimModal] = useState(null);
  const [paylasimDurumuHazir, setPaylasimDurumuHazir] = useState(false);
  // DÜZELTME 2 & 3: Konum izin durumu state'leri
  const [konumIzinBanner, setKonumIzinBanner]  = useState(false);
  // DÜZELTME 9: gecmisLayer ref (window global kaldırıldı)
  const gecmisLayerRef                         = useRef(null);

  useEffect(() => {
    const gorunurKimlikler = new Set(gecmisGorunurUyeKimlikleri.split("|").filter(Boolean));
    if (gecmisUyeUid && !gorunurKimlikler.has(String(gecmisUyeUid))) {
      setGecmisUyeUid(null);
      setKonumGecmisi([]);
      setSeciliRotaIdx(null);
    }
    if (analizUyeUid && !gorunurKimlikler.has(String(analizUyeUid))) {
      setAnalizUyeUid(null);
      setHareketAnalizi([]);
    }
  }, [analizUyeUid, gecmisGorunurUyeKimlikleri, gecmisUyeUid]);

  /* Google Maps/harici uygulamadan geri dönünce PWA beyaz ekranda kalmasın */
  useEffect(() => {
    const haritaSayfasiniCanlandir = () => {
      if (!hariciHaritaAcilisRef.current) return;

      // Harici uygulamadan dönüşte bazı mobil tarayıcı/PWA'larda Leaflet ölçüsü bozulabiliyor.
      // Mevcut route korunur, harita yeniden ölçülür ve ekran beyaz kalmaz.
      setTimeout(() => {
        try {
          mapRef.current?.invalidateSize?.();
        } catch (_) {}
        hariciHaritaAcilisRef.current = false;
      }, 250);
    };

    const visibilityHandler = () => {
      if (document.visibilityState === "visible") haritaSayfasiniCanlandir();
    };

    window.addEventListener("focus", haritaSayfasiniCanlandir);
    window.addEventListener("pageshow", haritaSayfasiniCanlandir);
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      window.removeEventListener("focus", haritaSayfasiniCanlandir);
      window.removeEventListener("pageshow", haritaSayfasiniCanlandir);
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  /*
    Harita sayfası ilk açıldığında SADECE sessiz durum kontrolü yapılır —
    hiçbir izin sistemi promptu burada tetiklenmez.

    DÜZELTME (App Store uyumu): Eskiden burada notDetermined durumunda
    otomatik olarak WhenInUse isteniyor, bu da AppDelegate'teki kör bir
    timer üzerinden hemen ardından "Her Zaman İzin Ver" promptunu
    tetikliyordu — kullanıcı daha canlı paylaşım/arka plan takip gibi bir
    şey istediğini belirtmeden. Artık her iki izin de SADECE kullanıcı
    "Canlı Konum Paylaşımı"nı açtığında (togglePaylasim → nativePaylasimiBaslat)
    isteniyor; bu sayfa sadece mevcut durumu okuyup banner'ı buna göre ayarlıyor.
  */
  useEffect(() => {
    if (!isNativeLocationAvailable() || !paylasimAcik) {
      setKonumIzinBanner(false);
      return undefined;
    }
    let iptal = false;

    const kontrol = async () => {
      try {
        const izinVar = await hasNativeLocationPermission();
        const alwaysVar = izinVar ? await hasAlwaysPermission() : false;
        if (!iptal) {
          // Capacitor iOS'ta WhenInUse ve Always için aynı "granted" değerini
          // döndürür. Ayrımı AppDelegate'in gerçek authorization event'i yapar.
          setKonumIzinBanner(isIOSPlatform() && izinVar && !alwaysVar);
        }
      } catch (_) {
        // İzin reddedildi ya da hata — sessiz
      }
    };

    const izinDegisti = () => { void kontrol(); };
    window.addEventListener("locationAuthorizationChanged", izinDegisti);
    // Sayfa animasyonu bittikten sonra kontrol et (render'ı bloklamasın).
    const t = setTimeout(kontrol, 900);
    return () => {
      iptal = true;
      clearTimeout(t);
      window.removeEventListener("locationAuthorizationChanged", izinDegisti);
    };
  }, [paylasimAcik]); // eslint-disable-line

  /* Acilista bir kez konuma uc */
  const ilkFlyToYapildiRef = useRef(false);
  useEffect(() => {
    if (ilkFlyToYapildiRef.current) return;
    if (!user?.uid || !mapRef.current) return;
    const lok = konumlar[user.uid];
    if (!lok?.lat) return;
    ilkFlyToYapildiRef.current = true;
    mapRef.current.flyTo([lok.lat, lok.lng], 15, { duration: 1.0 });
  }, [konumlar, user?.uid]); // eslint-disable-line

  /* Firebase */
  useEffect(() => {
    if (!familyId) return;
    // u1: SSE bağlantısı — konum güncellemelerini VE diğer veri değişikliklerini dinler.
    // Yerler/geofence/güvenli alan/buluşma/SOS için polling yok;
    // backend değişiklik olduğunda SSE üzerinden push eder.
    const u1=konumlariDinleService(familyId, setKonumlar, {
      onPlacesUpdated    : setYerler,
      onGeofencesUpdated : setGeofencelar,
      onSafeZonesUpdated : setGuvenliAlanlar,
      onMeetingUpdated   : setBulusmaNoktasi,
      onSosUpdated       : setSosAlertler,
    });
    // u2-u6: sadece ilk yüklemede bir kez fetch yapar, polling yok
    const u2=yerleriDinleService(familyId,setYerler);
    const u3=geofencelariDinleService(familyId,setGeofencelar);
    const u4=bulusmaNoktasiDinleService(familyId,setBulusmaNoktasi);
    const u5=sosAlertlariDinleService(familyId,setSosAlertler);
    const u6=guvenliAlanlariDinleService(familyId,setGuvenliAlanlar);
    return ()=>{u1();u2();u3();u4();u5();u6();};
  }, [familyId]);

  /* Güvenli alan / geofence listesi değişince background context'i güncelle.
     Bu sayede yeni alan eklense bile watcher anında haberdar olur. */
  useEffect(() => {
    if (!user?.uid || !familyId || !paylasimAcik) return;
    setBackgroundLocationContext({
      familyId,
      uid            : user.uid,
      tamAd          : tamAd || "",
      lang,
      zones          : [
        ...(guvenliAlanlar || []).map(b => ({ ...b, zoneType: "safeZone" })),
        ...(geofencelar    || []).map(b => ({ ...b, zoneType: "geofence" })),
      ],
      uyeler         : uyelerRef?.current?.length ? uyelerRef.current : (uyeler || []),
      bildirimHerkese: bildirimHerkeseYazService || null,
      bildirimGoster : bildirimGoster || null,
    });
  }, [guvenliAlanlar, geofencelar, uyeler, familyId, user?.uid, lang, paylasimAcik, tamAd]); // eslint-disable-line

  /* Kendi paylaşım durumunu senkronize et
     Not: İlk backend lokasyonu gelmeden native watcher durdurulmaz.
     Kill/reopen sonrası kullanıcı paylaşımı açıksa takip sessizce restore edilir.

     MADDE 9: Paylaşım durumu sunucuda KULLANICI bazlı tutulur (cihaz bazlı değil).
     Kullanıcı başka bir cihazında paylaşımı açtıysa, bu YENİ cihaza da
     "paylaşılıyor: true" gelir — ancak yeni cihazda konum izni / aktif takip
     servisi yoksa bu yanıltıcıdır. Bu yüzden native platformda, sunucu "açık"
     dese bile BU CİHAZDA konum izni yoksa butonu görsel olarak "kapalı"
     gösteriyoruz. Bu tamamen İSTEMCİ TARAFI görseldir: sunucu değeri
     DEĞİŞTİRİLMEZ, diğer cihazların paylaşımı durmaz, aile üyeleri konumu
     görmeye devam eder. Kullanıcı bu cihazda butona basınca normal izin akışı
     çalışır ve bu cihaz da paylaşıma katılır. Web ve iOS aynı mantıkla korunur;
     haritanın mevcut çalışma yapısına dokunulmaz. */
  useEffect(() => {
    if (!user?.uid) return;
    // Yerel acma/kapama islemi surerken POST/PATCH'ten gelen ara SSE durumunun
    // toggle state'ini geri cevirmesine izin verme. Islem sonunda kesin durum
    // zaten yerelde ve sunucuda birlikte uygulanir.
    if (paylasimGecisPromiseRef.current) return;
    const k = konumlar[user.uid];
    if (k === undefined) return;

    const sunucudaAcik = k.paylasimAcik !== false;

    // Cevrimdisi kapatma istegi henuz sunucuya ulasmadiysa eski SSE kaydi bu
    // cihazdaki kesin kapatma tercihini geri ceviremez. Sunucu kapali durumu
    // dogruladiginda bekleyen isaret temizlenir.
    if (paylasimKapatmaBekliyorRef.current) {
      if (!sunucudaAcik) {
        paylasimKapatmaBekliyorRef.current = false;
        bekleyenKonumKapatmaYaz(familyId, user.uid, false);
      }
      setPaylasimDurumuHazir(true);
      setPaylasimAcik(false);
      return;
    }

    if (sunucudaAcik && isNativeLocationAvailable()) {
      // Native cihazda: sunucu açık diyorsa, bu cihazın gerçekten izni var mı bak.
      let iptal = false;
      (async () => {
        const izinVar = await hasNativeLocationPermission();
        if (iptal) return;
        setPaylasimDurumuHazir(true);
        // İzin yoksa bu cihazda görsel olarak kapalı; izin varsa sunucu değeriyle aç.
        setPaylasimAcik(izinVar ? true : false);
      })();
      return () => { iptal = true; };
    }

    // Web veya sunucu kapalı → mevcut davranış (sunucu değeri).
    setPaylasimDurumuHazir(true);
    setPaylasimAcik(sunucudaAcik);
  }, [konumlar, user?.uid]); // eslint-disable-line

  /* Geofence'leri yükle */
  /* "Yakınımdaki üyeler" listesi — sadece UI için (haritada banner/liste gösterir).
     DÜZELTME: Bildirim gönderme kısmı buradan kaldırıldı. Eskiden burada
     sessionStorage tabanlı bir tekrar-engelleme ile bildirim gönderiliyordu;
     ama sessionStorage uygulama arka plana alınıp geri geldiğinde veya sayfa
     yeniden mount olduğunda sıfırlanabiliyordu — bu da konum hiç değişmediği
     halde "sana yakın" bildiriminin sayfaya her girişte tekrar gelmesine
     sebep oluyordu. Bildirim artık backend'de (map.routes.js →
     checkProximityAndNotify), her konum güncellemesinde kalıcı bir durum
     tablosuyla, SADECE gerçek "yakın değil → yakın" geçişinde gönderiliyor —
     sayfanın kaç kez açılıp kapandığının önemi yok. */
  useEffect(() => {
    if (!user?.uid||Object.keys(konumlar).length<2) return;
    const ben=konumlar[user.uid];
    if (!ben?.lat||!ben?.paylasimAcik) return;
    const sonuc=[];
    siraUyeler.forEach((uye)=>{
      const uid=uye.uid||uye.id; if(uid===user.uid) return;
      const lok=konumlar[uid]; if(!lok?.lat||!lok?.paylasimAcik) return;
      const km=mesafe(ben.lat,ben.lng,lok.lat,lok.lng);
      if(km<=0.5) sonuc.push({uid,ad:uye.ad,km});
    });
    setYakinlar(sonuc);
  }, [konumlar]); // eslint-disable-line

  /* DÜZELTME: Yakınlık banner'ı — sayfaya girince, yakın biri varsa SADECE BİR
     KEZ gösterilir, 10 saniye sonra kendiliğinden kapanır. yakinlikBannerGosterildiRef
     bu sayfa ziyareti (component mount) boyunca true kalır — yani konum verisi
     arka planda kaç kez yenilenirse yenilensin, banner bir daha açılmaz. Sayfadan
     çıkıp tekrar girerseniz (yeni mount), ref sıfırlanır ve yakın biri hâlâ varsa
     banner yine bir kez gösterilir.

     DÜZELTME 2 (kapanmama hatası): Önceki sürümde setTimeout, bu effect'in
     cleanup fonksiyonu olarak DÖNDÜRÜLÜYORDU. `yakinlar` arka planda sık sık
     (polling ile) yenilendiği için, React her yeniden çalıştırmadan önce bir
     ÖNCEKİ cleanup'ı çağırıyor — yani 10 saniye dolmadan, `yakinlar` bir kez
     daha değiştiği anda timer SESSİZCE iptal ediliyordu ve ref guard yüzünden
     YENİSİ de kurulmuyordu. Sonuç: banner hiç kapanmıyordu. Artık timer bu
     effect'in cleanup'ından TAMAMEN bağımsız — sadece component gerçekten
     unmount olursa (mountedRef ile) iptal ediliyor, `yakinlar` değişimleriyle
     değil. */
  useEffect(() => {
    if (yakinlar.length === 0) return;
    if (yakinlikBannerGosterildiRef.current) return;
    yakinlikBannerGosterildiRef.current = true;
    setYakinlikBannerAcik(true);
    setTimeout(() => {
      if (haritaMountedRef.current) setYakinlikBannerAcik(false);
    }, 10000);
  }, [yakinlar]);

  /* DÜZELTME (kritik — asıl duplicate bildirim kaynağı bulundu): Burada eskiden
     TAMAMEN BAĞIMSIZ, üçüncü bir bölge giriş/çıkış kontrol sistemi vardı —
     kendi POST /map/{familyId}/zone-state çağrısıyla durum takip ediyor,
     kendi bildirimini POST /notifications/{familyId}/broadcast üzerinden
     gönderiyordu. Bu, backend'in (map.routes.js → checkZonesAndNotify, her
     /location yazımında OTOMATİK çalışan, platformdan bağımsız) yaptığı
     kontrolden tamamen habersiz, PARALEL bir sistemdi. İkisi birlikte =
     aynı giriş/çıkış olayı için 2 ayrı bildirim (loglarda "[map-zone]"
     önekli olanlar backend'den, öneksiz "[fcm-push]"/"[web-push]" olanlar
     BURADAN geliyordu). Daha önce nativeLocationService.js'teki bgZoneCheck()
     kaldırılmıştı ama BU ikinci kopya gözden kaçmıştı. Artık kontrol SADECE
     backend'de yapılıyor — burada hiçbir şey yapılmıyor. */

  const konumHataBaslik = konumHata === mt(lang, "Canlı konum için konum izni vermeniz gerekir.") || konumHata === mt(lang, "Konum paylaşımını açmak için iPhone Ayarları > Aile Yönetimi > Konum bölümünden izin verin.")
    ? mt(lang, "Konum izni gerekli")
    : mt(lang, "Konum alınamadı");

  const konumHatasiYaz = (mesaj) => {
    if (!mesaj) {
      setKonumHata("");
      return;
    }
    setKonumHata(mesaj);
  };

  // Kullanici hizli dokunsa, sureli paylasim ayni anda tamamlansa veya SSE
  // state'i degistirse bile tek bir acma/kapama gecisi calissin. Ikinci cagri
  // mevcut Promise'i bekler; eski state ile yeni bir islem baslatmaz.
  const paylasimGecisiniCalistir = (islem) => {
    if (paylasimGecisPromiseRef.current) return paylasimGecisPromiseRef.current;

    if (haritaMountedRef.current) setPaylasimIslemiSuruyor(true);
    const promise = Promise.resolve()
      .then(islem)
      .finally(() => {
        if (paylasimGecisPromiseRef.current === promise) {
          paylasimGecisPromiseRef.current = null;
        }
        if (haritaMountedRef.current) setPaylasimIslemiSuruyor(false);
      });
    paylasimGecisPromiseRef.current = promise;
    return promise;
  };

  const konumAl = async ({ sessiz=false, zorunluPaylasim=false, izinIste=false, flyYap=false }={}) => {
    if (!familyId || !user?.uid) return null;

    // 10 sn otomatik takip ile manuel butonun çakışmasını engelle.
    // Sessiz arka plan denemesi zaten çalışan konum isteği varsa atlanır; ekstra backend yükü oluşmaz.
    if (konumAlCalisiyorRef.current && sessiz) return null;

    konumAlCalisiyorRef.current = true;
    if (!sessiz) {
      setKonumHata("");
      setManuelKonumAliniyor(true);
    }

    try {
      // NOT: tryAlways burada KASTEN false — bu fonksiyon tek seferlik konum
      // almak için kullanılıyor (ör. "Konumumu Göster" butonu). Always izni
      // sadece canlı paylaşım başlatılırken (nativePaylasimiBaslat içinde,
      // kullanıcı onayından sonra) istenir — aksi halde ihtiyaçtan fazla izin
      // istemiş oluruz (App Review bunu olumsuz değerlendirir).
      const pos = await getCurrentLocationOnce({
        requestPermission: izinIste,
        tryAlways        : false,
        timeout          : 12000,
        maximumAge       : 8000,
      });

      if (!pos?.lat || !pos?.lng) throw new Error("LOCATION_EMPTY");
      if (zorunluPaylasim && !paylasimYazimiAktifRef.current) return null;

      await konumGuncelleService({
        familyId,
        uid: user.uid,
        lat: pos.lat,
        lng: pos.lng,
        accuracy: pos.accuracy ?? null,
        speed: pos.speed ?? null,
        heading: pos.heading ?? null,
        source: isNativeLocationAvailable() ? "native-current" : "web-current",
        paylasimAcik: zorunluPaylasim ? true : paylasimAcik,
        // Paylaşım başlatılırken alınan ilk nokta canlı konum ve geçmişe tek
        // atomik backend isteğiyle yazılır. Yalnızca haritada kendini gösterme
        // işlemi, paylaşım kapalıyken geçmiş noktası oluşturmaz.
        savePoint: zorunluPaylasim === true,
      });

      if (flyYap) setSeciliUyeUid(user.uid);
      setKonumHata("");

      // flyYap=true ise haritayı konuma götür (sadece buton basınca)
      if (flyYap && mapRef.current) {
        const z = userZoomRef.current ?? 16;
        mapRef.current.flyTo([pos.lat, pos.lng], z, { duration: 1.2 });
      }

      return pos;
    } catch (err) {
      if (!sessiz) {
        const code = err?.code || err?.message || "";
        if (code === "PERMISSION_DENIED" || code === "LOCATION_DENIED") {
          konumHatasiYaz(mt(lang, "Canlı konum için konum izni vermeniz gerekir."));
        } else if (code === "WEB_GEOLOCATION_UNSUPPORTED") {
          konumHatasiYaz(mt(lang, "Tarayıcınız konum özelliğini desteklemiyor."));
        } else if (code === "WEB_GEOLOCATION_INSECURE") {
          konumHatasiYaz(mt(lang, "Konum özelliği sadece HTTPS'de çalışır."));
        } else if (code === "POSITION_UNAVAILABLE") {
          konumHatasiYaz(mt(lang, "GPS / Wi-Fi konumunu açın."));
        } else if (code === "TIMEOUT") {
          konumHatasiYaz(mt(lang, "Zaman aşımı."));
        } else {
          konumHatasiYaz(mt(lang, "Konum alınırken beklenmeyen bir sorun oluştu."));
        }
      }
      return null;
    } finally {
      konumAlCalisiyorRef.current = false;
      if (!sessiz) setManuelKonumAliniyor(false);
    }
  };

  const nativePaylasimiBaslat = async ({ izinIste=false }={}) => {
    if (!familyId || !user?.uid) return false;

    // Google Play belirgin açıklama şartı: Android'de kullanıcıya konum
    // izni gösterilmeden, konum okunmadan ve sunucuya aktarılmadan önce
    // uygulama-içi açıklama gösterilir. Daha önce kabul etmiş kullanıcıda
    // ön plan izni hâlâ varsa pencere tekrar edilmez; izin yoksa sistem
    // penceresinin hemen öncesinde yeniden gösterilir.
    if (izinIste && isAndroidPlatform()) {
      const onPlanIzniVar = await hasNativeLocationPermission();
      const aciklamaKabulEdildi = androidLocationDisclosureAccepted(user.uid);
      if (!onPlanIzniVar || !aciklamaKabulEdildi) {
        const kabulEdildi = await appConfirm(
          mt(lang, "Aile Yönetimi, canlı konum paylaşımını ve güvenli alan giriş/çıkış bildirimlerini çalıştırabilmek için konum verinizi uygulama kapalıyken veya kullanılmıyorken de toplar ve sunucuya aktarır. Konumunuz yalnızca seçtiğiniz aile alanındaki üyelerle paylaşılır. Bu özelliği istediğiniz zaman kapatabilirsiniz."),
          {
            title: mt(lang, "Arka Planda Konum Paylaşımı"),
            confirmText: mt(lang, "Kabul Et ve Devam Et"),
            cancelText: mt(lang, "Şimdi Değil"),
          }
        );
        if (!kabulEdildi) return false;
        rememberAndroidLocationDisclosure(user.uid);
      }
    }

    // Kimlik/token bağlamını güncelle. Sürekli watcher'ın ve konum yazma
    // callback'inin tek sahibi App.js'teki global oturum yöneticisidir.
    setBackgroundLocationContext({
      familyId,
      uid     : user.uid,
      tamAd   : tamAd || "",
    });

    // Önce anlık konumu yaz (kullanıcı hemen görür), sonra watcher kur
    const ilkKonum = await konumAl({ sessiz: false, zorunluPaylasim: true, izinIste, flyYap: false });
    if (!ilkKonum) return false;

    // "Her Zaman İzin Ver" yalnızca kullanıcı canlı paylaşımı bilinçli olarak
    // açtığında istenir. Android akışı Play politikasına uygun ayrı metinle
    // uygulama ayarını açar; iOS'un mevcut App Store akışı aynen korunur.
    if (izinIste && isNativeLocationAvailable()) {
      try {
        const alwaysVarMi = await hasAlwaysPermission();
        if (!alwaysVarMi) {
          if (isAndroidPlatform()) {
            const ayarlariAc = await appConfirm(
              mt(lang, "Canlı konum paylaşımının ve güvenli alan bildirimlerinin uygulama arka plandayken de çalışması için Android konum ayarlarında İzinler > Konum bölümünden \"Her zaman izin ver\" seçeneğini seçin. Ayarları Aç'a dokunduğunuzda Aile Yönetimi'nin uygulama ayarları açılacaktır."),
              {
                title: mt(lang, "Her Zaman Konum İzni"),
                confirmText: mt(lang, "Ayarları Aç"),
                cancelText: mt(lang, "Şimdi Değil"),
              }
            );
            if (ayarlariAc) requestAlwaysLocationUpgrade();
          } else {
            const devamEt = await appConfirm(
              mt(lang, "Aile üyeleriniz uygulama kapalıyken de konumunuzu görebilsin ve güvenli alan bildirimleri kesintisiz çalışsın diye bir sonraki ekranda \"Her Zaman İzin Ver\" seçmenizi öneririz."),
              { title: mt(lang, "Arka Planda Konum Paylaşımı"), confirmText: mt(lang, "Devam Et"), cancelText: mt(lang, "Şimdi Değil") }
            );
            if (devamEt) requestAlwaysLocationUpgrade();
          }
        }
      } catch (e) {
        // Always durumu okunamadıysa sessizce geç — paylaşım WhenInUse ile de çalışır.
        // console.warn ile loglanıyor ki ileride benzer bir sorun sessizce kaybolmasın.
        console.warn("[Harita] Always izin akışı atlandı:", e?.message || e);
      }
    }

    if (isNativeLocationAvailable()) {
      // AppDelegate'in uygulama kapaliyken calisan bagimsiz CLLocationManager'i
      // ancak kullanici canli paylasimi gercekten baslattiginda etkinlestirilir.
      setNativeBackgroundLocationSharingEnabled(true);

      // Arka plan bildirim banner'ı göster
      setTimeout(() => {
        if (haritaMountedRef.current && paylasimYazimiAktifRef.current) {
          setKonumBgBanner(true);
        }
      }, 1500);
    }

    return true;
  };

  const nativePaylasimiDurdur = () => {
    // Native bayrak watcher kaldirilmadan once kapanir. Boylece ayni anda gelen
    // AppDelegate callback'i veya kuyruktaki upload yeni bir konum yazamaz.
    setNativeBackgroundLocationSharingEnabled(false);
    if (nativeLocationStopPromiseRef.current) return nativeLocationStopPromiseRef.current;

    const promise = stopNativeLocationSharing()
      .catch(() => {})
      .finally(() => {
        if (nativeLocationStopPromiseRef.current === promise) {
          nativeLocationStopPromiseRef.current = null;
        }
      });
    nativeLocationStopPromiseRef.current = promise;
    return promise;
  };

  const paylasimiKapat = async ({ sunucuyuGuncelle=true }={}) => {
    // Gizlilik tercihi once yerelde uygulanir. Boylece PATCH gecikse veya ag
    // kesilse bile yeni native/foreground konum yazimi baslamaz.
    paylasimYazimiAktifRef.current = false;
    if (sunucuyuGuncelle) {
      paylasimKapatmaBekliyorRef.current = true;
      bekleyenKonumKapatmaYaz(familyId, user?.uid, true);
    }
    sureBitimVersionRef.current += 1;
    if (sureBitimRef.current) {
      clearTimeout(sureBitimRef.current);
      sureBitimRef.current = null;
    }
    if (haritaMountedRef.current) {
      setKonumSuresiDk(0);
      setPaylasimAcik(false);
    }
    await nativePaylasimiDurdur();

    if (!sunucuyuGuncelle) return true;

    let sonHata = null;
    for (let deneme = 0; deneme < 2; deneme += 1) {
      try {
        await paylasimDurumGuncelleService({ familyId, uid: user.uid, paylasimAcik: false });
        paylasimKapatmaBekliyorRef.current = false;
        bekleyenKonumKapatmaYaz(familyId, user.uid, false);
        return true;
      } catch (error) {
        sonHata = error;
      }
    }
    throw sonHata || new Error("LOCATION_SHARE_STOP_FAILED");
  };

  // Kapatma istegi ag kesintisinde sunucuya ulasamadiysa uygulama acikken ve
  // baglanti geri geldiginde sessizce tamamla. Kullanici tercihi bu sirada
  // yerelde kapali kalir; eski sunucu/SSE durumu watcher'i yeniden baslatamaz.
  useEffect(() => {
    if (!familyId || !user?.uid) return undefined;
    let iptal = false;
    let calisiyor = false;

    const tekrarDene = async () => {
      if (iptal || calisiyor || !paylasimKapatmaBekliyorRef.current) return;
      if (paylasimGecisPromiseRef.current) return;
      calisiyor = true;
      paylasimYazimiAktifRef.current = false;
      if (haritaMountedRef.current) setPaylasimAcik(false);
      await nativePaylasimiDurdur();
      try {
        await paylasimDurumGuncelleService({ familyId, uid: user.uid, paylasimAcik: false });
        if (!iptal) {
          paylasimKapatmaBekliyorRef.current = false;
          bekleyenKonumKapatmaYaz(familyId, user.uid, false);
        }
      } catch (_) {
        // Bir sonraki online eventi veya sayfa acilisi tekrar dener.
      } finally {
        calisiyor = false;
      }
    };

    tekrarDene();
    window.addEventListener("online", tekrarDene);
    return () => {
      iptal = true;
      window.removeEventListener("online", tekrarDene);
    };
  }, [familyId, user?.uid]); // eslint-disable-line

  /* Sürekli konum takibinin sahibi App.js'teki global oturum yöneticisidir.
     Harita sayfası yalnızca paylaşım durumunu yansıtır; burada ikinci watcher
     veya 10 saniyelik JS polling kurulmaz. */
  useEffect(() => {
    if (!familyId || !user?.uid) return;
    if (!paylasimDurumuHazir) return;

    if (!paylasimAcik) {
      paylasimYazimiAktifRef.current = false;
      nativePaylasimiDurdur();
      return;
    }

    paylasimYazimiAktifRef.current = true;
    return undefined;
  }, [paylasimAcik,paylasimDurumuHazir,familyId,user?.uid]); // eslint-disable-line

  /* Toggle */
  const togglePaylasim = async () => {
    if (!isPremium && premiumConfig?.lockedFeatures?.canliKonum) {
      onPremiumLock && onPremiumLock(mt(lang, "Canlı konum paylaşımı"), mt(lang, "Aile üyelerinin konumunu gerçek zamanlı takip et"), [mt(lang, "Canlı konum paylaşımı"), mt(lang, "Coğrafi sınır (Geofence)"), mt(lang, "Konum geçmişi"), mt(lang, "Buluşma noktası belirleme")]);
      return;
    }

    return paylasimGecisiniCalistir(async () => {
      const yeni = !paylasimAcik;
      setKonumHata("");

      if (!yeni) {
        try {
          await paylasimiKapat();
        } catch (_) {
          konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
        }
        return;
      }

      // Kullanici bekleyen bir kapatma varken acmayi bilincli olarak sectiyse
      // eski cevrimdisi niyet artik uygulanmamalidir.
      paylasimKapatmaBekliyorRef.current = false;
      bekleyenKonumKapatmaYaz(familyId, user.uid, false);
      paylasimYazimiAktifRef.current = true;
      setPaylasimBaslatiliyor(true);
      let basladi = false;
      try {
        basladi = await nativePaylasimiBaslat({ izinIste:true });
      } finally {
        if (haritaMountedRef.current) setPaylasimBaslatiliyor(false);
      }

      if (!basladi) {
        await paylasimiKapat().catch(() => {});
        return;
      }

      try {
        await paylasimDurumGuncelleService({familyId,uid:user.uid,paylasimAcik:true});
        setPaylasimAcik(true);
      } catch (_) {
        await paylasimiKapat().catch(() => {});
        konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
        return;
      }

      // Tüm aileye bildirim gönder
      if (bildirimHerkeseYazService && uyelerRef) {
        bildirimHerkeseYazService({
          familyId, uyeler:uyelerRef.current, ekleyenUid:user.uid,
          baslik:`📍 ${mt(lang, "Konum Paylaşımı Açıldı")}`,
          aciklama: `${tamAd||mt(lang, "Bir aile üyesi")} ${mt(lang, "konumunu paylaşmaya başladı")}`,
          baslikKey:"auto.push.location.shareOnTitle",
          aciklamaKey:"auto.push.location.shareOnBody",
          aciklamaVars:{ member: tamAd||mt(lang, "Bir aile üyesi") },
          type:"location", kaynak:"harita", kaynakId:`konum_acik_${user.uid}`,
          gonderenUid:user.uid,
        }).catch(()=>{});
      }
    });
  };

  /* Süreli paylaşım */
  const sureligBaslat = async (dk) => {
    if (!isPremium && premiumConfig?.lockedFeatures?.canliKonum) {
      onPremiumLock && onPremiumLock(mt(lang, "Canlı konum paylaşımı"), mt(lang, "Aile üyelerinin konumunu gerçek zamanlı takip et"), [mt(lang, "Canlı konum paylaşımı"), mt(lang, "Coğrafi sınır (Geofence)"), mt(lang, "Konum geçmişi"), mt(lang, "Buluşma noktası belirleme")]);
      return;
    }

    return paylasimGecisiniCalistir(async () => {
      setKonumHata("");

      const shareUntil = new Date(Date.now() + dk * 60000);

      // Paylaşım zaten açıksa native'i durdurma/başlatma — sadece timer güncelle
      if (paylasimAcik) {
        try {
          await paylasimDurumGuncelleService({familyId,uid:user.uid,paylasimAcik:true,shareUntil,timedShare:true});
          setKonumSuresiDk(dk);
        } catch (_) {
          konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
          return;
        }
      } else {
        // Paylaşım kapalıysa normal akış: native başlat
        paylasimKapatmaBekliyorRef.current = false;
        bekleyenKonumKapatmaYaz(familyId, user.uid, false);
        paylasimYazimiAktifRef.current = true;
        setPaylasimBaslatiliyor(true);
        let basladi = false;
        try {
          basladi = await nativePaylasimiBaslat({ izinIste:true });
        } finally {
          if (haritaMountedRef.current) setPaylasimBaslatiliyor(false);
        }

        if (!basladi) {
          await paylasimiKapat().catch(() => {});
          return;
        }

        try {
          await paylasimDurumGuncelleService({familyId,uid:user.uid,paylasimAcik:true,shareUntil,timedShare:true});
          setKonumSuresiDk(dk);
          setPaylasimAcik(true);
        } catch (_) {
          await paylasimiKapat().catch(() => {});
          konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
          return;
        }

        // Tüm aileye bildirim gönder
        if (bildirimHerkeseYazService && uyelerRef) {
          const isHours = dk >= 60;
          const durationValue = isHours ? dk / 60 : dk;
          bildirimHerkeseYazService({
            familyId, uyeler:uyelerRef.current, ekleyenUid:user.uid,
            baslik:`⏱ ${mt(lang, "Süreli Konum Paylaşımı")}`,
            aciklama:`${tamAd || mt(lang, "Bir aile üyesi")} ${dk < 60 ? `${dk} ${mt(lang, "dakika")}` : `${dk / 60} ${mt(lang, "saat")}`} ${mt(lang, "boyunca konumunu paylaşıyor")}`,
            baslikKey:"auto.push.location.timedTitle",
            aciklamaKey: isHours ? "auto.push.location.timedBodyHours" : "auto.push.location.timedBodyMinutes",
            aciklamaVars:{ member: tamAd || mt(lang, "Bir aile üyesi"), value: durationValue },
            type:"location", kaynak:"harita", kaynakId:`sureli_${user.uid}_${Date.now()}`,
            gonderenUid:user.uid,
          }).catch(()=>{});
        }
      }

      // Sunucu yeni sureyi kabul ettikten sonra eski timer'i degistir. API
      // basarisizsa mevcut sureli paylasimin yerel kapanisi kaybolmaz.
      if (sureBitimRef.current) clearTimeout(sureBitimRef.current);
      const sureVersion = ++sureBitimVersionRef.current;
      sureBitimRef.current = setTimeout(() => {
        const suresiDolanPaylasimiKapat = async () => {
          if (sureBitimVersionRef.current !== sureVersion) return;
          // Tam bu anda baska bir gecis suruyorsa eski state ile yarismak
          // yerine onun tamamlanmasini bekle, sonra kesin kapatma uygula.
          const devamEdenGecis = paylasimGecisPromiseRef.current;
          if (devamEdenGecis) await devamEdenGecis.catch(() => {});
          if (sureBitimVersionRef.current !== sureVersion) return;
          await paylasimGecisiniCalistir(() => paylasimiKapat());
        };
        suresiDolanPaylasimiKapat().catch(() => {
          if (haritaMountedRef.current) {
            konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
          }
        });
      }, dk*60000);
    });
  };

  const ortakYerLimitKontrol = () => {
    if (isPremium) return true;
    const maxYer = Number(premiumConfig?.limits?.kayitliYer ?? 3);
    if (maxYer > 0 && yerler.length >= maxYer) {
      onPremiumLimitHit?.(
        mt(lang, "Kayıtlı yerler"),
        yerler.length,
        maxYer,
        [mt(lang, "Sınırsız ortak yer"), mt(lang, "Aile konumları ve adres yönetimi"), mt(lang, "Haritada kayıtlı yerleri hızlı bulma")],
        lang === "en" ? `In the free plan, you can add up to ${maxYer} shared places.` : `Ücretsiz planda en fazla ${maxYer} ortak yer ekleyebilirsin.`
      );
      return false;
    }
    return true;
  };

  const yeniYerModalAc = () => {
    if (!ortakYerLimitKontrol()) return;
    setDuzenleYer(null);
    setModalAcik(true);
  };

  /* Yer işlemleri */
  const yerKaydet = async (data) => {
    const tamAd=uyeler.find(u=>u.uid===user.uid)?.ad || (lang === "en" ? "User" : "Kullanıcı");
    if (duzenleYer) {
      const guncellendi = await yerGuncelleService({familyId,yerId:duzenleYer.id,guncel:data});
      if (guncellendi) setYerler(prev => prev.map(y => String(y.id)===String(duzenleYer.id) ? guncellendi : y));
    } else {
      if (!ortakYerLimitKontrol()) return;
      const sonuc = await yerEkleService({familyId,yer:data,ekleyenAd:tamAd,ekleyenUid:user.uid});
      // Optimistic ekleme kaldırıldı: SSE (places_updated) tam listeyi push ettiğinden
      // SSE REST response'dan önce gelirse kayıt çift görünüyordu.
      // bildirim aktifse backend bir geofence da oluşturdu → haritada hemen çiz
      if (sonuc?.bildirimGeofence) {
        setGeofencelar(prev => [...prev, sonuc.bildirimGeofence]);
      }
    }
    setModalAcik(false); setDuzenleYer(null);
    return true;
  };
  const yerSil = async (yerId) => {
    if(!await appConfirm(mt(lang, "Bu yeri silmek istediğinizden emin misiniz?"))) return;
    await yerSilService({familyId,yerId});
    setYerler(prev => prev.filter(y => String(y.id) !== String(yerId)));
  };
  /* ANDROID harici açılış yardımcısı (MADDE 3 + 8)
     Sırasıyla dener:
       1. CapacitorApp.openUrl  -> sistem hangi uygulamayı çözerse (navigasyon
          uygulaması, tarayıcı) onu açar. Manifest'teki <queries> beyanı
          olmadan bu çağrı çözülemiyordu; artık beyan edildi.
       2. window.open(_blank)   -> plugin kullanılamazsa yedek yol.
     Hiçbiri çalışmazsa false döner; çağıran taraf alternatif URL deneyebilir. */
  const androidHariciAc = async (url) => {
    if (!url) return false;
    try {
      if (CapacitorApp && CapacitorApp.openUrl) {
        await CapacitorApp.openUrl({ url });
        return true;
      }
    } catch (_) { /* çözülemedi -> yedek yola düş */ }
    try {
      window.open(url, "_blank", "noopener");
      return true;
    } catch (_) {
      return false;
    }
  };

  const hariciHaritaAc = (url) => {
    if (!url) return;
    hariciHaritaAcilisRef.current = true;

    // ANDROID (MADDE 3): window.location.assign harici linkte uygulamanın
    // WebView'ini o adrese götürüp uygulamayı fiilen "kaybettiriyordu".
    // Android'de harici harita/URL, Capacitor App.openUrl ile sistem
    // tarayıcısında / navigasyon uygulamasında açılır; uygulama arka planda
    // ayakta kalır ve geri tuşuyla dönülebilir.
    //
    // NOT: Önceki sürümde plugin'e window.Capacitor.Plugins.App üzerinden
    // erişiliyordu; bu referans her zaman hazır olmadığı için çağrı sessizce
    // düşüyor ve butona basınca HİÇBİR ŞEY OLMUYORDU. Artık plugin doğrudan
    // "@capacitor/app" modülünden import ediliyor (CapacitorApp).
    if (isAndroidPlatform()) {
      androidHariciAc(url);
      return;
    }

    // PWA/iOS içinde window.open(_blank) bazı cihazlarda uygulamaya dönünce
    // beyaz ara ekran + sol üstte X bırakabiliyor. Bu yüzden iOS'ta harici harita
    // aynı webview'i yeni boş pencereye çevirmeden, gerçek uygulama/link
    // yönlendirmesi olarak açılıyor (mevcut davranış korunur).
    window.location.assign(url);
  };

  const yolTarifi = (yer) => {
    if (yer.lat&&yer.lng) yolTarifiAc(yer.lat, yer.lng, yer.ad || yer.adres || mt(lang, "Hedef"));
    else if(yer.adres) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(yer.adres)}`;
      hariciHaritaAc(url);
    }
  };

  /* Yüklü navigasyon uygulamalarını tespit et */
  const yuklUygulamalariGetir = async () => {
    // iOS'ta canOpenUrl için Info.plist'te LSApplicationQueriesSchemes zorunlu.
    // Ekli değilse her zaman false döner. Bu yüzden native'de her zaman
    // Google Maps + Apple Haritalar gösteriyoruz (her ikisi de standart iOS'ta açılır).
    // Açma anında iOS, uygulama yüklü değilse App Store'a yönlendiriyor.

    if (!isNativeLocationAvailable()) {
      // Web — sadece Google Maps web
      return ["google"];
    }

    // Android'de Apple Haritalar YOKTUR. Yalnızca Google Haritalar sunulur;
    // cihazda Google Maps yüklü değilse yönlendirme yine de tarayıcı üzerinden
    // (https://www.google.com/maps) açılır. Bu yüzden Android'de doğrudan tek
    // seçenek olarak Google gösterip navigasyon modalını atlıyoruz.
    if (isAndroidPlatform()) {
      return ["google"];
    }

    // Native iOS — Google Maps + Apple Haritalar standart göster
    // İleride LSApplicationQueriesSchemes eklenirse canOpenUrl aktif edilebilir
    return ["google", "apple"];
  };

  const navUygulamasiAc = (app, lat, lng, label = "") => {
    const hedef = `${lat},${lng}`;
    const enc   = encodeURIComponent(hedef);
    let url;

    // ANDROID (MADDE 3): iOS'a özel şemalar (comgooglemaps://, maps://)
    // Android'de çalışmaz. Android'de iki aşamalı, "cihazda hangi navigasyon
    // uygulaması varsa onu aç" stratejisi uygulanır:
    //
    //   1. geo: URI  ->  Android'in standart harita/navigasyon şemasıdır.
    //      Sistem, cihazda kurulu TÜM navigasyon uygulamalarını (Google Maps,
    //      Yandex Navi, Waze, Sygic vb.) çözüp kullanıcıya seçtirir veya
    //      varsayılan olanı doğrudan açar.
    //   2. Hiçbir navigasyon uygulaması yoksa geo: çözülemez; bu durumda
    //      evrensel Google Maps HTTPS linkiyle TARAYICIDA açılır.
    //
    // Not: Bu çağrıların çalışabilmesi için AndroidManifest.xml içinde
    // <queries> altında geo: ve https: şemaları beyan edilmiştir. O beyan
    // olmadan (önceki durum) sistem hiçbir uygulamayı çözemiyor ve butona
    // basıldığında hiçbir şey olmuyordu.
    if (isAndroidPlatform()) {
      setNavModal(null);
      hariciHaritaAcilisRef.current = true;

      const etiket = encodeURIComponent(label || navModal?.label || "");
      const geoUrl = etiket
        ? `geo:${hedef}?q=${hedef}(${etiket})`
        : `geo:${hedef}?q=${hedef}`;
      const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`;

      (async () => {
        try {
          if (CapacitorApp && CapacitorApp.openUrl) {
            // 1) Kurulu navigasyon uygulamasını dene
            await CapacitorApp.openUrl({ url: geoUrl });
            return;
          }
        } catch (_) {
          /* navigasyon uygulaması yok/çözülemedi -> web haritaya düş */
        }
        // 2) Yedek: tarayıcıda Google Maps
        await androidHariciAc(webUrl);
      })();
      return;
    }

    switch (app) {
      case "google":
        url = `comgooglemaps://?daddr=${hedef}&directionsmode=driving`;
        // Google Maps yüklü değilse web fallback
        if (!window?.Capacitor?.Plugins?.App) {
          url = `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`;
        }
        break;
      case "apple":
        url = `maps://?daddr=${hedef}&dirflg=d`;
        break;
      case "yandex":
        url = `yandexnavi://build_route_on_map?lat_to=${lat}&lon_to=${lng}`;
        break;
      case "waze":
        url = `waze://?ll=${hedef}&navigate=yes`;
        break;
      default:
        url = `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`;
    }
    setNavModal(null);
    hariciHaritaAc(url);
  };

  const yolTarifiAc = async (lat, lng, label=mt(lang, "Hedef")) => {
    if (!lat || !lng) return;
    const uygulamalar = await yuklUygulamalariGetir();

    if (uygulamalar.length === 1) {
      // Tek uygulama — direkt aç
      navUygulamasiAc(uygulamalar[0], Number(lat), Number(lng), label);
    } else {
      // Birden fazla — seçtir
      setNavModal({ lat: Number(lat), lng: Number(lng), label, uygulamalar });
    }
  };

  const haritaKartiniOrtayaKaydir = () => {
    requestAnimationFrame(() => {
      try {
        haritaKartRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } catch (_) {}
    });
  };

  /* Yer haritada göster */
  const yerHaritadaGoster = (yer, sayfayiHaritayaKaydir=false) => {
    if (!yer.lat||!yer.lng||!mapRef.current) return;
    mapRef.current.flyTo([Number(yer.lat),Number(yer.lng)],16,{duration:1.0});
    if (sayfayiHaritayaKaydir) haritaKartiniOrtayaKaydir();
  };

  const koordinataOdaklan = (lat, lng, zoom=16, sayfayiHaritayaKaydir=false) => {
    if (!lat || !lng || !mapRef.current) return;
    mapRef.current.flyTo([Number(lat), Number(lng)], zoom, { duration: 1.1 });
    if (sayfayiHaritayaKaydir) haritaKartiniOrtayaKaydir();
  };

  const aktifKonumMerkezi = () => {
    let region;
    try { region = mapRef.current?.getRegion?.() || undefined; } catch (_) {}
    const benim = konumlar?.[user?.uid];
    if (benim?.lat && benim?.lng) return { lat: Number(benim.lat), lng: Number(benim.lng), region };
    try {
      const c = mapRef.current?.getCenter?.();
      if (c?.lat && c?.lng) return { lat: Number(c.lat), lng: Number(c.lng), region };
    } catch (_) {}
    return { region };
  };

  const sonAramayiKaydet = (yer) => {
    if (!yer?.lat || !yer?.lng) return;
    const kayit = { name: yer.name, subtitle: yer.subtitle, lat: yer.lat, lng: yer.lng, source: yer.source || "recent" };
    setSonHaritaAramalari((prev) => {
      const next = [kayit, ...prev.filter((x) => placeKey(x.lat, x.lng, x.name) !== placeKey(kayit.lat, kayit.lng, kayit.name))].slice(0, 3);
      try { localStorage.setItem("aileHaritaSonAramalar", JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  const haritaSonucSec = async (yer) => {
    let secim = yer;
    if (yer?.source === "apple-autocomplete") {
      try {
        setHaritaAraniyor(true);
        const merkez = aktifKonumMerkezi();
        const detaylar = await searchMapKitPlaces(yer, { lang, ...merkez, limit: 1 });
        if (detaylar[0]) secim = detaylar[0];
      } catch (_) {}
    }
    if (!secim?.lat || !secim?.lng) {
      try {
        const merkez = aktifKonumMerkezi();
        const alternatifler = await haritaYerAraCoklu(secim?.name || haritaArama, { lang, ...merkez, limit: 1 });
        if (alternatifler[0]) secim = alternatifler[0];
      } catch (_) {}
    }
    setHaritaAraniyor(false);
    if (!secim?.lat || !secim?.lng) return;
    setHaritaArama(secim.name || "");
    setHaritaOnerileri([]);
    setHaritaAramaOdak(false);
    sonAramayiKaydet(secim);
    koordinataOdaklan(secim.lat, secim.lng, 16, true);
  };

  useEffect(() => {
    const kapat = (event) => {
      const target = event.target;
      const aramaEtkilesimi = target instanceof Element && target.closest(".haritaAramaForm, .haritaAramaOneriKutusu");
      if (!aramaEtkilesimi || !haritaAramaAlanRef.current?.contains(target)) setHaritaAramaOdak(false);
    };
    document.addEventListener("pointerdown", kapat);
    return () => document.removeEventListener("pointerdown", kapat);
  }, []);

  useEffect(() => {
    const q = haritaArama.trim();
    let iptal = false;
    if (q.length < 2) {
      setHaritaOnerileri([]);
      setHaritaOneriYukleniyor(false);
      return;
    }
    setHaritaOneriYukleniyor(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const merkez = aktifKonumMerkezi();
        let liste;
        if (mapRef.current?._renderer === "mapkit") {
          try {
            liste = await autocompleteMapKitPlaces(q, { lang, ...merkez, limit: 8, signal: controller.signal });
          } catch (error) {
            if (error?.name === "AbortError") return;
          }
        }
        if (!liste) liste = await haritaYerAraCoklu(q, { lang, ...merkez, limit: 8 });
        if (!iptal) setHaritaOnerileri(liste);
      } catch (_) {
        if (!iptal) setHaritaOnerileri([]);
      } finally {
        if (!iptal) setHaritaOneriYukleniyor(false);
      }
    }, 320);
    return () => { iptal = true; controller.abort(); clearTimeout(timer); };
  }, [haritaArama, lang]); // eslint-disable-line

  const haritadaAdresAra = async (e) => {
    e?.preventDefault?.();
    const q = haritaArama.trim();
    if (!q) return;

    try {
      setHaritaAraniyor(true);
      const merkez = aktifKonumMerkezi();
      let liste = haritaOnerileri.length > 0 ? haritaOnerileri : null;
      if (!liste && mapRef.current?._renderer === "mapkit") {
        try { liste = await searchMapKitPlaces(q, { lang, ...merkez, limit: 8 }); } catch (_) {}
      }
      if (!liste) liste = await haritaYerAraCoklu(q, { lang, ...merkez, limit: 8 });
      const ilk = liste[0];

      if (!ilk?.lat || !ilk?.lng) {
        if (bildirimGoster) bildirimGoster(`📍 ${mt(lang, "Konum bulunamadı.")}`, mt(lang, "Aradığınız cadde/sokak için sonuç bulunamadı."), "location");
        else appAlert(mt(lang, "Konum bulunamadı."));
        return;
      }

      await haritaSonucSec(ilk);
    } catch (err) {
      console.error("Harita araması başarısız:", err);
      appAlert(mt(lang, "Harita araması yapılamadı. İnternet bağlantınızı kontrol edin."));
    } finally {
      setHaritaAraniyor(false);
    }
  };

  /* Üyeye odaklan */
  const uyeyeOdaklan = (uid) => {
    const lok=konumlar[uid];
    if (!lok?.lat||!mapRef.current) return;
    mapRef.current.flyTo([lok.lat,lok.lng],16,{duration:1.2});
    setSeciliUyeUid(uid);
    haritaKartiniOrtayaKaydir();
  };

  /* Geofence */
  const geofenceEkle = async (data) => {
    try {
      const yeni = await geofenceEkleService({ familyId, geofence: { ...data, ekleyenUid: user.uid } });
      if (yeni) setGeofencelar(prev => [...prev, yeni]);
      setGeofenceModal(false);
    }
    catch (e) { console.error("Geofence kaydedilemedi:", e); throw e; }
    // Oluşturma bildirimi artık backend tarafından yeni AI/TR-EN yapısıyla gönderiliyor.
    // Buradaki eski frontend broadcast kapalı tutulur; aksi halde çift bildirim oluşur.
    if (bildirimGoster) bildirimGoster(`🔔 ${mt(lang, "Coğrafi Sınır Eklendi")}`,`"${data.ad}" ${mt(lang, "oluşturuldu")}`,"location");
    return true;
  };
  const geofenceSil = async (id) => {
    try {
      await geofenceSilService({ familyId, geofenceId: id });
      setGeofencelar(prev => prev.filter(g => String(g.id) !== String(id)));
    }
    catch (e) { console.error("Geofence silinemedi:", e); }
  };

  /* SOS / Acil */
  const sosBaslat = () => {
    setSosAktif(true);
    setTimeout(async () => {
      try {
        const pos = await getAppLocationOnce({ requestPermission:true, timeout:10000, maximumAge:5000 });
        if (!pos?.lat || !pos?.lng) throw new Error("LOCATION_EMPTY");
        try {
          await sosGonderService({familyId,uid:user.uid,lat:pos.lat,lng:pos.lng,tamAd:tamAd||""});
          if (bildirimHerkeseYazService && uyelerRef) {
            bildirimHerkeseYazService({
              familyId,uyeler:uyelerRef.current,ekleyenUid:user.uid,
              baslik:`🆘 ${mt(lang, "ACİL YARDIM!")}`,
              aciklama: `${tamAd||mt(lang, "Bir aile üyesi")} ${mt(lang, "yardım istiyor! Konumu paylaşıldı.")}`,
              baslikKey:"auto.push.sos.title",
              aciklamaKey:"auto.push.sos.body",
              aciklamaVars:{ member: tamAd||mt(lang, "Bir aile üyesi") },
              type:"location",kaynak:"sos",
              kaynakId:`sos_${user.uid}_${Date.now()}`,
              gonderenUid:user.uid,
            }).catch(()=>{});
          }
          if (mapRef.current) mapRef.current.flyTo([pos.lat,pos.lng],15,{duration:1.0});
        } catch(e) { console.error(e); }
      } catch (err) {
        setSosAktif(false);
        appAlert(formatLocationError(err, lang));
      }
    }, 0);
  };
  const sosBitir = () => {
    setSosAktif(false);
    setTimeout(() => {
      sosBitirService({familyId,uid:user.uid}).catch(e => console.error(e));
    }, 0);
  };

  /* Konum geçmişi */
  /* ── Rota renk paleti — üye index'ine göre ── */
  const rotaRenkleri = [
    "#6366f1", // indigo
    "#10b981", // emerald
    "#f59e0b", // amber
    "#ef4444", // red
    "#3b82f6", // blue
    "#8b5cf6", // violet
    "#ec4899", // pink
    "#14b8a6", // teal
  ];

  /* ── Seçili üyenin rota rengini al — avatar rengiyle eşleş ── */
  const gecmisRotaRengi = (uid) => {
    const idx = siraUyeler.findIndex(u => (u.uid||u.id) === uid);
    if (idx < 0) return rotaRenkleri[0];
    return uyeRenk(siraUyeler[idx], idx); // kullanıcının avatar rengi
  };

  /* ── Haritada renkli rota çiz ── */
  const rotaCiz = (noktalar, renk) => {
    if (!mapRef.current || noktalar.length === 0) return;

    // Önceki katmanı temizle
    if (gecmisLayerRef.current) {
      gecmisLayerRef.current.remove();
      gecmisLayerRef.current = null;
    }

    if (mapRef.current._renderer === "mapkit") {
      mapRef.current.setHistoryRoute?.(noktalar, renk);
      gecmisLayerRef.current = {
        remove: () => mapRef.current?.clearHistoryRoute?.(),
      };
      return;
    }

    if (!window.L) return;
    const L = window.L;

    const latlngs = noktalar.map(n => [Number(n.lat), Number(n.lng)]);

    // Grup: polyline + noktalar
    const katman = L.layerGroup();

    // Ana çizgi — gradient görünümü için iki katman
    L.polyline(latlngs, {
      color  : renk,
      weight : 4,
      opacity: 0.25,
    }).addTo(katman);

    L.polyline(latlngs, {
      color    : renk,
      weight   : 3,
      opacity  : 0.85,
      dashArray: null,
    }).addTo(katman);

    // Başlangıç noktası (yeşil)
    if (latlngs.length > 0) {
      L.circleMarker(latlngs[0], {
        radius     : 6,
        fillColor  : "#10b981",
        color      : "#fff",
        weight     : 2,
        opacity    : 1,
        fillOpacity: 1,
      }).bindTooltip(lang === "en" ? "Start" : "Başlangıç", { permanent: false, direction: "top" })
        .addTo(katman);
    }

    // Bitiş noktası (kırmızı)
    if (latlngs.length > 1) {
      L.circleMarker(latlngs[latlngs.length - 1], {
        radius     : 7,
        fillColor  : renk,
        color      : "#fff",
        weight     : 2,
        opacity    : 1,
        fillOpacity: 1,
      }).bindTooltip(lang === "en" ? "Last position" : "Son konum", { permanent: false, direction: "top" })
        .addTo(katman);
    }

    katman.addTo(mapRef.current);
    gecmisLayerRef.current = katman;

    // Haritayı rotaya sığdır
    try {
      mapRef.current.fitBounds(L.latLngBounds(latlngs), { padding: [32, 32], maxZoom: 16 });
    } catch (_) {
      mapRef.current.flyTo(latlngs[latlngs.length - 1], 14, { duration: 1.0 });
    }
  };

  /* ── Geçmiş yükle (uid + tarih) ── */
  const gecmisYukle = (uid, tarih) => {
    const hedefTarih = tarih || gecmisTarih;
    if (gecmisUnsubRef.current) { gecmisUnsubRef.current(); gecmisUnsubRef.current = null; }
    setGecmisUyeUid(uid);
    setGecmisYukleniyor(true);
    setKonumGecmisi([]);
    setSeciliRotaIdx(null);
    const renk = gecmisRotaRengi(uid);
    gecmisUnsubRef.current = konumGecmisiniDinleService(familyId, uid, (noktalar) => {
      setKonumGecmisi(noktalar);
      setGecmisYukleniyor(false);
      try { rotaCiz(noktalar, renk); } catch(e) { console.warn("[Harita] rotaCiz hatası:", e?.message); }
    }, hedefTarih);
  };

  /* ── Geçmiş kapat ── */
  const gecmisKapat = () => {
    // Seçim markerını temizle
    if (window._gecmisSecimMarker) {
      window._gecmisSecimMarker.remove();
      window._gecmisSecimMarker = null;
    }
    if (gecmisUnsubRef.current) { gecmisUnsubRef.current(); gecmisUnsubRef.current = null; }
    if (gecmisLayerRef.current) { gecmisLayerRef.current.remove(); gecmisLayerRef.current = null; }
    setGecmisUyeUid(null);
    setKonumGecmisi([]);
    setSeciliRotaIdx(null);
  };

  /* ── Tarih değişince mevcut seçili üyeyi yeniden yükle ── */
  const gecmisTarihDegistir = (yeniTarih) => {
    setGecmisTarih(yeniTarih);
    if (gecmisUyeUid) {
      gecmisYukle(gecmisUyeUid, yeniTarih);
    }
  };

  /* Güvenli alan */
  const guvenliAlanEkle = async (data) => {
    try {
      const yeni = await guvenliAlanEkleService({familyId,alan:{...data,ekleyenUid:user.uid}});
      // Listeyi hemen güncelle — 30sn polling bekleme
      if (yeni) setGuvenliAlanlar(prev => [...prev, yeni]);
      setGuvenliAlanModal(false);
    }
    catch(e) { console.error(e); throw e; }
    // Oluşturma bildirimi artık backend tarafından yeni AI/TR-EN yapısıyla gönderiliyor.
    // Buradaki eski frontend broadcast kapalı tutulur; aksi halde çift bildirim oluşur.
    if(bildirimGoster) bildirimGoster(`🟢 ${mt(lang, "Güvenli Alan Eklendi")}`,`"${data.ad}" ${mt(lang, "tanımlandı")}`,"location");
    return true;
  };
  const guvenliAlanSil = async (id) => {
    try {
      await guvenliAlanSilService({familyId,alanId:id});
      setGuvenliAlanlar(prev => prev.filter(a => String(a.id) !== String(id)));
    } catch(e) { console.error(e); }
  };

  /* Hareket analizi */
  const analizYukle = async (uid) => {
    setAnalizUyeUid(uid);
    setAnalizYukleniyor(true);
    try {
      const veri = await hareketAnaliziGetirService({familyId,uid,gun:7});
      setHareketAnalizi(veri);
    } catch(e) { console.error(e); }
    finally { setAnalizYukleniyor(false); }
  };

  /* Buluşma */
  const bulusmaEkle = async (data) => {
    setHaritaSecimModal(null);
    const geciciId = `local_meeting_${Date.now()}`;
    const yeniBulusma = { ...data, id: data.id || geciciId };
    const oncekiBulusma = bulusmaNoktasi;

    // Backend listener beklenmeden listeyi anında güncelle.
    setBulusmaNoktasi(prev => {
      const liste = Array.isArray(prev) ? prev : (prev ? [prev] : []);
      return [yeniBulusma, ...liste.filter(item => item.id !== yeniBulusma.id)];
    });

    try {
      const kayit = await bulusmaNoktasiKaydetService({ familyId, bulusma: data });
      if (kayit?.id && kayit.id !== geciciId) {
        setBulusmaNoktasi(prev => {
          const liste = Array.isArray(prev) ? prev : (prev ? [prev] : []);
          return liste.map(item => item.id === geciciId ? { ...item, ...kayit } : item);
        });
      }
    }
    catch (e) {
      console.error("Bulusma kaydedilemedi:", e);
      setBulusmaNoktasi(oncekiBulusma);
      throw e;
    }
    if (mapRef.current) mapRef.current.flyTo([data.lat,data.lng],15,{duration:1.2});
    // Tüm aileye bildirim gönder
    if (bildirimHerkeseYazService && uyelerRef) {
      bildirimHerkeseYazService({
        familyId, uyeler:uyelerRef.current, ekleyenUid:user.uid,
        baslik:`🤝 ${mt(lang, "Buluşma Noktası Belirlendi")}`,
        aciklama: lang === "en" ? `${tamAd||mt(lang, "Aile yöneticisi")} set "${data.ad}" as the meeting point` : `${tamAd||mt(lang, "Aile yöneticisi")} buluşma noktası olarak "${data.ad}" belirledi`,
        baslikKey:"auto.push.meeting.title",
        aciklamaKey:"auto.push.meeting.body",
        aciklamaVars:{ member: tamAd||mt(lang, "Aile yöneticisi"), place: data.ad },
        type:"location", kaynak:"bulusma",
        kaynakId:`bulusma_${user.uid}_${Date.now()}`,
        gonderenUid:user.uid,
      }).catch(()=>{});
    }
    if (bildirimGoster) bildirimGoster(`🤝 ${mt(lang, "Buluşma Noktası")}`, `"${data.ad}" ${mt(lang, "buluşma noktası olarak belirlendi")}`, "location");
    setBulusmaModal(false);
    return true;
  };

  const bulusmaSil = async (bn) => {
    const oncekiBulusma = bulusmaNoktasi;

    // Silme işlemi kullanıcıya anında yansısın; backend hata verirse geri al.
    setBulusmaNoktasi(prev => {
      const liste = Array.isArray(prev) ? prev : (prev ? [prev] : []);
      const kalanlar = liste.filter(item => item.id !== bn.id);
      return Array.isArray(prev) ? kalanlar : (kalanlar[0] || null);
    });

    try {
      await bulusmaNoktasiSilService({ familyId, id: bn.id });
    } catch (e) {
      console.error("Bulusma silinemedi:", e);
      setBulusmaNoktasi(oncekiBulusma);
    }
  };

  const gosterilecekYerler = yerler;
  const bulusmaNoktalari = Array.isArray(bulusmaNoktasi) ? bulusmaNoktasi : (bulusmaNoktasi ? [bulusmaNoktasi] : []);
  const ucKayitListeStyle = {
    maxHeight: 172,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 4,
    paddingBottom: 8,
    WebkitOverflowScrolling: "touch",
  };
  const ortakYerListeStyle = {
    maxHeight: 250,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 4,
    paddingBottom: 10,
    WebkitOverflowScrolling: "touch",
  };
  const yerEkleyebilir = yoneticiMi || permissions?.actions?.addMapPlace !== false;
  const kendiYeriniSilebilir = permissions?.actions?.deleteOwnMapPlace === true;
  const tumYerleriSilebilir = yoneticiMi || permissions?.actions?.deleteAllMapPlaces === true;

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <div className="pageContent haritaPage" style={{padding:"0 0 16px"}}>

      {/* ── Navigasyon Uygulama Seçici ── */}
      {navModal && (
        <div className="haritaNavModalOverlay" onClick={() => setNavModal(null)}>
          <div className="haritaNavModal" onClick={e => e.stopPropagation()}>
            <div className="haritaNavModalBaslik">
              🧭 {mt(lang, "Navigasyon Uygulaması Seç")}
            </div>
            {navModal.label && (
              <div className="haritaNavModalHedef">📍 {navModal.label}</div>
            )}
            <div className="haritaNavModalBtnlar">
              {(navModal.uygulamalar || []).map(app => {
                const appInfo = {
                  google: {
                    label: "Google Maps",
                    logo : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335"/><path d="M12 11.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#fff"/></svg>
                  },
                  apple : {
                    label: mt(lang, "Harita"),
                    logo : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="6" fill="#34C759"/><path d="M12 4L6 9.5V20h12V9.5L12 4z" fill="white" opacity="0.9"/><path d="M9 20v-6h6v6" fill="#34C759"/><path d="M12 4l6 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  },
                  yandex: {
                    label: "Yandex",
                    logo : <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#FC3F1D"/><text x="12" y="17" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">Y</text></svg>
                  },
                  waze  : {
                    label: "Waze",
                    logo : <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#33CCFF"/><text x="12" y="17" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">W</text></svg>
                  },
                };
                const info = appInfo[app];
                if (!info) return null;
                return (
                  <button key={app} className="haritaNavBtn"
                    onClick={() => navUygulamasiAc(app, navModal.lat, navModal.lng)}>
                    <span className="haritaNavBtnLogo">{info.logo}</span>
                    <span className="haritaNavBtnAd">{info.label}</span>
                    <span className="haritaNavBtnOk">›</span>
                  </button>
                );
              })}
            </div>
            <button className="haritaNavModalIptal" onClick={() => setNavModal(null)}>
              {mt(lang, "İptal")}
            </button>
          </div>
        </div>
      )}

      {/* ── Arka plan konum aktif banner — premium ── */}
      {konumBgBanner && paylasimAcik && (
        <div className="haritaTakipBildirimi haritaKonumBgBanner" role="status" aria-live="polite">
          <div className="haritaKonumBgBannerSol">
            <span className="haritaKonumBgBannerIkon">
              <HaritaUiIcon name="share-location" size={22} />
            </span>
            <div style={{flex:1,minWidth:0}}>
              <div className="haritaKonumBgBannerBaslik">{mt(lang, "Konum paylaşımı aktif")}</div>
              <div className="haritaKonumBgBannerAlt">{mt(lang, "Kesintisiz takip için uygulamayı kapatmayın")}</div>
              <div className="haritaKonumBgBannerPulse">
                <div className="haritaKonumBgBannerPulseDot" />
                <div className="haritaKonumBgBannerPulseDot" />
                <div className="haritaKonumBgBannerPulseDot" />
              </div>
            </div>
          </div>
          <button className="haritaKonumBgBannerKapat" onClick={() => setKonumBgBanner(false)}>
            {mt(lang, "Anladım")}
          </button>
        </div>
      )}

      {/* DÜZELTME 2 & 3: "Her Zaman İzin Ver" banner — iOS WhenInUse varsa göster */}
      {konumIzinBanner && (
        <div className="haritaIzinBanner">
          <div className="haritaIzinBannerIcerik">
            <span className="haritaIzinBannerIkon">📍</span>
            <div className="haritaIzinBannerMetin">
              <div className="haritaIzinBannerBaslik">{mt(lang, "Arka Plan Konum İzni")}</div>
              <div className="haritaIzinBannerAlt">{mt(lang, "Kesintisiz konum takibi için Ayarlar'dan konum iznini 'Her Zaman' olarak seçin.")}</div>
            </div>
          </div>
          <div className="haritaIzinBannerBtnlar">
            <button
              className="haritaIzinBannerAyarBtn"
              onClick={async () => {
                setKonumIzinBanner(false);
                try {
                  if (isIOSPlatform() && window?.webkit?.messageHandlers?.aileNativeBridge) {
                    window.webkit.messageHandlers.aileNativeBridge.postMessage({
                      action: "openLocationSettings",
                    });
                  } else if (window?.Capacitor?.Plugins?.App?.openUrl) {
                    await window.Capacitor.Plugins.App.openUrl({ url: "app-settings:" });
                  }
                } catch (_) {}
              }}
            >
              {mt(lang, "Ayarlar'ı Aç")}
            </button>
            <button
              className="haritaIzinBannerKapatBtn"
              onClick={() => setKonumIzinBanner(false)}
            >
              {mt(lang, "Şimdi Değil")}
            </button>
          </div>
        </div>
      )}

      {/* Yakınlık bildirimi — sayfaya girince bir kez gösterilir, 10sn sonra kendiliğinden kapanır */}
      {yakinlikBannerAcik&&yakinlar.length>0&&(
        <div className="haritaYakinlikBar">
          {yakinlar.map(b=>(
            <div key={b.uid} className="haritaYakinlikItem">
              <span>🤝</span>
              <span><b>{b.ad}</b> {lang === "en" ? "is" : "sana"} {b.km<0.1?mt(lang, "çok yakın!"):`${(b.km*1000).toFixed(0)} ${lang === "en" ? "m away" : "m uzakta"}`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Toggle */}
      <div className="haritaToggleBar">
        <span className="haritaToggleBarIkon">
          <HaritaUiIcon name="share-location" size={24} />
        </span>
        <div className="haritaToggleBarMetin">
          <div className="haritaToggleBarBaslik">{mt(lang, "Konumumu Paylaş")}</div>
          <div className="haritaToggleBarSub">
            {paylasimBaslatiliyor?mt(lang, "Konum alınıyor…"):paylasimAcik?konumSuresiDk>0?`${mt(lang, "Süreli")} — ${konumSuresiDk} ${mt(lang, "dk")}`:mt(lang, "Paylaşılıyor"):mt(lang, "Gizli")}
          </div>
        </div>
        <button className={`haritaToggle ${paylasimAcik?"acik":"kapali"}`} onClick={togglePaylasim} disabled={paylasimIslemiSuruyor}>
          <div className="haritaToggleKnob"/>
        </button>
      </div>

      {/* Süreli paylaşım — yatay scroll, alt satıra inmez */}
      <div className="haritaSureliPaylasimWrap">
        <span className="haritaSureliLabel">{mt(lang, "Süreli Paylaş:")}</span>
        <div className="haritaSureliScroll">
          {[30, 60, 120, 360, 720, 1440].map(dk => {
            const aktif = konumSuresiDk === dk && paylasimAcik;
            const label = dk < 60
              ? `${dk} ${mt(lang, "dk")}`
              : dk < 1440
                ? `${dk/60} ${mt(lang, "sa")}`
                : `24 ${mt(lang, "sa")}`;
            return (
              <button key={dk}
                className={`haritaSureliBtn${aktif ? " aktif" : ""}`}
                disabled={paylasimIslemiSuruyor}
                onClick={() => sureligBaslat(dk)}>
                {label}
              </button>
            );
          })}
          {konumSuresiDk > 0 && paylasimAcik && (
            <button className="haritaSureliBtn iptal"
              disabled={paylasimIslemiSuruyor}
              onClick={() => paylasimGecisiniCalistir(() => paylasimiKapat()).catch(() => {
                konumHatasiYaz(mt(lang, "Konum paylaşımı güncellenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin."));
              })}>
              {mt(lang, "İptal")}
            </button>
          )}
        </div>
      </div>

      {/* Hata mesajı */}
      {konumHata&&(
        <div style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",marginBottom:12,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:12,fontSize:12,color:"#ef4444",lineHeight:1.5}}>
          <span>⚠️</span>
          <div><strong style={{display:"block",marginBottom:2}}>{konumHataBaslik}</strong>{konumHata}</div>
          <button onClick={()=>setKonumHata("")} style={{marginLeft:"auto",background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:16}}>×</button>
        </div>
      )}

      {/* Üyeler */}
      <div className="haritaUyelerScroll noSwipeBack" data-no-swipe-back="true">
        {siraUyeler.map((uye,i)=>{
          const uid=uye.uid||uye.id, lok=konumlar[uid], payVar=lok?.paylasimAcik===true, renk=uyeRenk(uye,i);
          const hizRozeti = payVar ? haritaSpeedBadgeValue(lok?.speed) : null;
          return (
            <div key={uid} className={`haritaUyeKart${seciliUyeUid===uid?" secili":""}`} onClick={()=>{ if(seciliUyeUid===uid){ setSeciliUyeUid(null); setTakipModu(false); return; } if(lok?.paylasimAcik&&lok?.lat){ uyeyeOdaklan(uid); } else { setSeciliUyeUid(uid); } }}>
              <div className={`haritaOnlineDot${payVar?"":" kapali"}`}/>
              {hizRozeti != null && (
                <div
                  className="haritaSpeedBadge"
                  aria-label={lang === "en" ? `${hizRozeti} km/h` : `${hizRozeti} km/s`}
                  title={lang === "en" ? `${hizRozeti} km/h` : `${hizRozeti} km/s`}
                >
                  {hizRozeti}
                </div>
              )}
              <AvatarImg
                avatar={uye.avatar || null}
                renk={renk}
                initials={initials(uye)}
                size={36}
                glow={false}
                radius="50%"
                className="haritaUyeAvatar"
              
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
              <div className="haritaUyeAd">{uye.ad}</div>
              <div className={`haritaKonumChip ${payVar?"acik":"kapali"}`}><div className="haritaKonumChipDot"/>{payVar?mt(lang, "Paylaşıyor"):mt(lang, "Gizli")}</div>
              <div className="haritaSonKonum">{payVar&&lok?.sonGuncelleme?zaman(lok.sonGuncelleme, lang):uid===user?.uid?mt(lang, "Kendiniz"):"—"}</div>
            </div>
          );
        })}
      </div>

      {/* Seçili üye panel */}
      {seciliUyeUid&&(()=>{
        const uye=uyeler.find(u=>(u.uid||u.id)===seciliUyeUid), lok=konumlar[seciliUyeUid];
        if (!uye) return null;
        const i=siraUyeler.findIndex(u=>(u.uid||u.id)===seciliUyeUid);
        return (
          <div className="haritaUyePanel">
            <AvatarImg
              avatar={uye.avatar || null}
              fileKey={uye.profilePictureFileKey || uye.profile_picture_file_key}
              renk={uyeRenk(uye,i)}
              initials={initials(uye)}
              size={42}
              glow={false}
              radius="50%"
              className="haritaUyeAvatar"
            
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
            <div className="haritaUyePanelBilgi">
              <div className="haritaUyePanelAd">{uye.ad} {uye.soyad} · {uye.rol}</div>
              <div className="haritaUyePanelKonum">
                {lok?.paylasimAcik&&lok?.lat
                  ?`📍 ${(()=>{const b=konumlar[user?.uid];if(b?.lat&&lok?.lat){const km=mesafe(b.lat,b.lng,lok.lat,lok.lng);return km<1?`${(km*1000).toFixed(0)} ${mt(lang, "m uzakta")}`:`${km.toFixed(1)} ${mt(lang, "km uzakta")}`;}return lok.adres||`${Number(lok.lat).toFixed(4)}, ${Number(lok.lng).toFixed(4)}`;})()} · ${zaman(lok.sonGuncelleme, lang)}`
                  :mt(lang, "Konum paylaşımı kapalı")}
              </div>
            </div>
            {lok?.paylasimAcik&&lok?.lat&&(
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button
                  className={"haritaOdaklanBtn" + (takipModu ? " takip-aktif" : "")}
                  onClick={() => { setTakipModu(t => !t); haritaKartiniOrtayaKaydir(); }}
                  title={takipModu ? mt(lang, "Takibi Durdur") : mt(lang, "Takip Et")}>
                  {takipModu ? "📡" : "🎯"} {takipModu ? mt(lang, "Takip Ediliyor") : mt(lang, "Takip Et")}
                </button>
                <button className="haritaOdaklanBtn" onClick={()=>yolTarifiAc(lok.lat,lok.lng,`${uye.ad} ${uye.soyad}`)}>🧭 {mt(lang, "Yol Tarifi")}</button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tam genişlikte, native harita yüzeyi */}
      <div className="haritaKart" ref={haritaKartRef}>
        <div className="haritaMapViewport noSwipeBack" data-no-swipe-back="true">
          <ResilientFamilyMap
            onLeafletFallback={leafletFallbackVarsayilaniUygula}
            onZoomChange={(z) => { userZoomRef.current = z; }}
            konumlar={konumlar} yerler={yerler} uyeler={mapUyeler}
            seciliUyeUid={seciliUyeUid} takipModu={takipModu} haritaTuru={haritaTuru}
            geofencelar={geofencelar} guvenliAlanlar={guvenliAlanlar} bulusmaNoktasi={bulusmaNoktasi}
            sosAlertler={sosAlertler}
            onYerTikla={yerHaritadaGoster} mapRef={mapRef}
            currentUserUid={user?.uid}
            resolveMemberAvatar={haritaMapKitAvatarSrc}
            resolvePlaceStyle={haritaMapKitYerStili}
            lang={lang}
          />

          <div className="haritaMapTopControls" ref={haritaAramaAlanRef}>
            <form className="haritaAramaForm" onSubmit={haritadaAdresAra}>
              <span className="haritaAramaIkon" aria-hidden="true">🔎</span>
              <input
                className="haritaAramaInput"
                value={haritaArama}
                onChange={(e)=>{
                  setHaritaArama(e.target.value);
                  setHaritaAramaOdak(true);
                }}
                onFocus={() => setHaritaAramaOdak(true)}
                placeholder={mt(lang, "Cadde, sokak veya yer ara...")}
              />
              <button className="haritaAramaBtn" type="submit" disabled={haritaAraniyor}>
                {haritaAraniyor ? "..." : mt(lang, "Ara")}
              </button>
            </form>

            <div className="haritaMapQuickControls">
              <div className="haritaCanliBadge">{lang === "en" ? "Live" : "Canlı"}</div>
              <select
                className="haritaKartBtn"
                value={haritaTuru}
                onChange={(e)=>haritaTuruDegistir(e.target.value)}
                aria-label={mt(lang, "Harita görünümü")}
              >
                <option value="sokak">🗺 {mt(lang, "Standart")}</option>
                <option value="koyu">🌙 {mt(lang, "Koyu")}</option>
                <option value="uydu">🛰 {mt(lang, "Uydu")}</option>
                <option value="terrain">🏔 {mt(lang, "Arazi")}</option>
              </select>
            </div>

            {(haritaAramaOdak && (haritaArama.trim().length >= 2 || sonHaritaAramalari.length > 0)) && (
              <div className="haritaAramaOneriKutusu noSwipeBack" data-no-swipe-back="true">
                <div className="haritaAramaOneriBaslik">
                  <span>{haritaArama.trim().length >= 2 ? mt(lang, "Arama önerileri") : mt(lang, "Son aramalar")}</span>
                  {haritaOneriYukleniyor && <small>{haritaAraniyor ? "" : "…"}</small>}
                </div>
                {(haritaArama.trim().length >= 2 ? haritaOnerileri : sonHaritaAramalari).slice(0, haritaArama.trim().length >= 2 ? 8 : 3).map((yer, idx) => (
                  <button
                    key={`${yer.source || "recent"}-${idx}-${yer.lat}-${yer.lng}`}
                    type="button"
                    className="haritaAramaOneriSatir"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { void haritaSonucSec(yer); }}
                  >
                    <span className="haritaAramaOneriIkon">📍</span>
                    <span className="haritaAramaOneriMetin">
                      <b>{yer.name}</b>
                      {yer.subtitle && <small>{yer.subtitle}</small>}
                    </span>
                  </button>
                ))}
                {haritaArama.trim().length >= 2 && !haritaOneriYukleniyor && haritaOnerileri.length === 0 && (
                  <div className="haritaAramaOneriBos">{mt(lang, "Sonuç bulunamadı")}</div>
                )}
              </div>
            )}
          </div>

          <button
            className="haritaKonumGosterBtn"
            type="button"
            onClick={()=>konumAl({sessiz:false, izinIste:true, flyYap:true})}
            disabled={manuelKonumAliniyor}
            aria-label={manuelKonumAliniyor ? mt(lang, "Konum alınıyor…") : mt(lang, "Konumumu Göster")}
            title={manuelKonumAliniyor ? mt(lang, "Konum alınıyor…") : mt(lang, "Konumumu Göster")}
          >
            {manuelKonumAliniyor ? <span className="haritaKonumSpinner" aria-hidden="true" /> : <HaritaUiIcon name="locate" size={22} />}
          </button>

          <div className="haritaLegend noSwipeBack" data-no-swipe-back="true">
            <div className="haritaLegendItem"><div className="haritaLegendDot" style={{background:"#6366f1"}}/>{mt(lang, "Üyeler")}</div>
            {[["#3b82f6","Ev"],["#f59e0b","Okul"],["#8b5cf6","İş"],["#ef4444","Hastane"],["#10b981","Market"],["#94a3b8","Diğer"]].map(([r,l])=>(
              <div key={l} className="haritaLegendItem"><div className="haritaLegendDot" style={{background:r}}/>{mt(lang, l)}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Premium araç çubuğu - kaydırılabilir */}
      <div className="haritaPremiumBar noSwipeBack" data-no-swipe-back="true">
        {[["geofence","🔔",mt(lang, "Sınır")],["guvenli","🟢",mt(lang, "Güvenli")],["bulusma","🤝",mt(lang, "Buluşma")],["yakinlik","📡",mt(lang, "Yakınlık")],
          ["sos","🆘","SOS"],["gecmis","🕐",mt(lang, "Geçmiş")],["analiz","📊",mt(lang, "Analiz")]
        ].map(([k,ikon,label])=>{
          const lockedMap = { geofence: "geofence", gecmis: "konumGecmisi" };
          const lockKey = lockedMap[k];
          const isLocked = !isPremium && lockKey && premiumConfig?.lockedFeatures?.[lockKey];
          return (
            <button key={k}
              className={`haritaPremiumBtn${aktifPanel===k?" aktif":""}${k==="sos"&&sosAktif?" sos-aktif":""}`}
              onClick={()=>{
                if (isLocked) {
                  onPremiumLock && onPremiumLock(
                    k === "geofence" ? mt(lang, "Coğrafi sınır (Geofence)") : mt(lang, "Konum geçmişi"),
                    k === "geofence" ? mt(lang, "Belirli bölgelere girip çıkınca bildirim al") : mt(lang, "Aile üyelerinin konum geçmişini görüntüle"),
                    [mt(lang, "Canlı konum paylaşımı"), mt(lang, "Coğrafi sınır bildirimleri"), mt(lang, "Konum geçmişi"), mt(lang, "Buluşma noktası belirleme")]
                  );
                  return;
                }
                setAktifPanel(p=>p===k?null:k);
              }}>
              <span className="haritaPremiumBtnIkon">{ikon}</span><span>{label}{isLocked ? " 🔒" : ""}</span>
            </button>
          );
        })}
      </div>

      {/* Geofence paneli */}
      {aktifPanel==="geofence"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik">
            <span>🔔 {mt(lang, "Coğrafi Sınırlar")}</span>
            <button className="haritaYerEkleBtn" onClick={()=>setGeofenceModal(true)}>{lang === "en" ? "+ Add" : "+ Ekle"}</button>
          </div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 12px",lineHeight:1.5}}>{mt(lang, "Belirlenen bölgeye giriş/çıkışta bildirim alın. Sınırlar haritada daire olarak görünür.")}</p>
          {geofencelar.length===0?(
            <div style={{textAlign:"center",padding:"20px 0",color:"var(--text-muted)",fontSize:13}}><div style={{fontSize:28,marginBottom:6}}>🔔</div>{mt(lang, "Henüz coğrafi sınır eklenmemiş.")}</div>
          ):(
            <div style={geofencelar.length > 3 ? ucKayitListeStyle : undefined}>{geofencelar.map(gf=>(
              <div key={gf.id} className="haritaGeofenceRow haritaTappableRow"
                onClick={()=>koordinataOdaklan(gf.lat,gf.lng,14,true)}>
                <div className="haritaGeofenceDot" style={{background:gf.renk}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{gf.ad}</div>
                  <div style={{fontSize:11,color:"var(--text-secondary)"}}>{gf.yaricap>=1000?`${gf.yaricap/1000} km`:`${gf.yaricap} m`} · {gf.tur==="giris"?mt(lang, "Giriş"):gf.tur==="cikis"?mt(lang, "Çıkış"):mt(lang, "Her İkisi")} · {bolgeHedefMetni(gf, uyeler, lang)} · {mt(lang, "Haritada göster")}</div>
                </div>
                <button onClick={(e)=>{e.stopPropagation();geofenceSil(gf.id);}} className="haritaYerAkBtn sil">🗑️</button>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {/* Buluşma paneli */}
      {aktifPanel==="bulusma"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik">
            <span>🤝 {mt(lang, "Buluşma Noktası")}</span>
            <button className="haritaYerEkleBtn" onClick={()=>setBulusmaModal(true)}>{lang === "en" ? "Set" : "Belirle"}</button>
          </div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 12px",lineHeight:1.5}}>{mt(lang, "Tüm aile üyelerine mevcut navigasyon uygulamanız üzerinden yol tarifi verir.")}</p>
          {bulusmaNoktalari.length>0?(
            <div style={bulusmaNoktalari.length > 3 ? ucKayitListeStyle : undefined}>
              {bulusmaNoktalari.map((bn, idx)=>(
                <div key={bn.id || idx} className="haritaGeofenceRow haritaTappableRow"
                  onClick={()=>koordinataOdaklan(bn.lat,bn.lng,16,true)}>
                  <span style={{fontSize:22}}>🤝</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{bn.ad}</div>
                    <div style={{fontSize:11,color:"var(--text-secondary)"}}>{Number(bn.lat).toFixed(4)}, {Number(bn.lng).toFixed(4)} · {mt(lang, "Haritada göster")}</div>
                  </div>
                  <button className="haritaYerAkBtn" title={mt(lang, "Yol Tarifi")} onClick={(e)=>{e.stopPropagation();yolTarifiAc(bn.lat,bn.lng,bn.ad|| mt(lang, "Buluşma Noktası"));}}>🧭</button>
                  <button className="haritaYerAkBtn sil" onClick={(e)=>{e.stopPropagation();bulusmaSil(bn);}}>🗑️</button>
                </div>
              ))}
            </div>
          ):(
            <div style={{textAlign:"center",padding:"16px 0",color:"var(--text-muted)",fontSize:13}}><div style={{fontSize:28,marginBottom:6}}>🤝</div>{mt(lang, "Buluşma noktası belirlenmemiş.")}</div>
          )}
        </div>
      )}

      {/* Yakınlık paneli */}
      {aktifPanel==="yakinlik"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik"><span>📡 {mt(lang, "Yakınlık Durumu")}</span></div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 12px",lineHeight:1.5}}>{mt(lang, "Konumunu paylaşan aile üyelerine olan mesafen gösterilir.")}</p>
          {(()=>{
            const ben=konumlar[user?.uid];
            if (!ben?.lat||!ben?.paylasimAcik) return <div style={{textAlign:"center",padding:"16px 0",color:"var(--text-muted)",fontSize:13}}>{mt(lang, "Konumunuzu paylaşarak başlatın.")}</div>;
            const aktif=siraUyeler.filter(uye=>{const uid=uye.uid||uye.id;if(uid===user?.uid) return false;const l=konumlar[uid];return l?.lat&&l?.paylasimAcik;});
            if (aktif.length===0) return <div style={{textAlign:"center",padding:"16px 0",color:"var(--text-muted)",fontSize:13}}>{mt(lang, "Konum paylaşan başka üye yok.")}</div>;
            return aktif.map((uye,i)=>{
              const uid=uye.uid||uye.id, lok=konumlar[uid];
              const km=mesafe(ben.lat,ben.lng,lok.lat,lok.lng);
              const mesajTxt = km < 0.1
                ? mt(lang, "Çok yakın! 🎉")
                : km < 1
                  ? `${(km * 1000).toFixed(0)} ${mt(lang, "m uzakta")}`
                  : `${km.toFixed(1)} ${mt(lang, "km uzakta")}`;
              const renk=km<0.5?"#10b981":km<2?"#f59e0b":"#94a3b8";
              return (
                <div key={uid} className="haritaGeofenceRow">
                  <AvatarImg
                    avatar={uye.avatar || null}
                    renk={uyeRenk(uye,i)}
                    initials={initials(uye)}
                    size={34}
                    glow={false}
                    radius="50%"
                    className="haritaUyeAvatar"
                  
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{uye.ad} {uye.soyad}</div>
                    <div style={{fontSize:11,color:renk,fontWeight:600}}>{mesajTxt}</div>
                  </div>
                  <div className="haritaMiniActionGroup">
                    <button className="haritaOdaklanBtn" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>uyeyeOdaklan(uid)}>{mt(lang, "Odaklan")}</button>
                    <button className="haritaOdaklanBtn" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>yolTarifiAc(lok.lat,lok.lng,`${uye.ad} ${uye.soyad}`)}>{mt(lang, "Yol Tarifi")}</button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}


      {/* SOS Paneli */}
      {aktifPanel==="sos"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik"><span>🆘 {lang === "en" ? "SOS / Emergency Location" : "SOS / Acil Konum"}</span></div>
          {sosAlertler.length>0&&(
            <div style={{marginBottom:12}}>
              {sosAlertler.map(s=>(
                <div key={s.id} className="haritaSosAlertRow">
                  <span style={{fontSize:22,animation:"sos-pulse 1s infinite"}}>🆘</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#ef4444"}}>{s.tamAd} {mt(lang, "ACİL YARDIM İSTİYOR!")}</div>
                    <div style={{fontSize:11,color:"var(--text-secondary)"}}>
                      {s.lat&&s.lng&&<button style={{background:"none",border:"none",color:"var(--brand-1)",cursor:"pointer",fontSize:11,padding:0}} onClick={()=>mapRef.current?.flyTo([s.lat,s.lng],16,{duration:1.0})}>{mt(lang, "Haritada göster")}</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 14px",lineHeight:1.5}}>
            {mt(lang, "Acil durumda tüm aile üyelerine anlık konum bildirimi gönderir. Haritada konumun yanıp söner.")}
          </p>
          {sosAktif?(
            <button className="haritaSosKapat" onClick={sosBitir}>{mt(lang, "SOS İptal Et — Güvendeyim ✓")}</button>
          ):(
            <button className="haritaSosBtn" onClick={sosBaslat}>🆘 {mt(lang, "ACİL YARDIM GÖNDER")}</button>
          )}
        </div>
      )}

      {/* Geçmiş Paneli */}
      {aktifPanel==="gecmis"&&(
        <div className="haritaPremiumPanel">

          {/* Başlık */}
          <div className="haritaPremiumPanelBaslik">
            <span>🕐 {mt(lang, "Konum Geçmişi")}</span>
            {gecmisUyeUid && (
              <button className="haritaGecmisSifirlaBtn" onClick={gecmisKapat}>✕</button>
            )}
          </div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 10px",lineHeight:1.5}}>
            {mt(lang, "Kimin nerede olduğunu görün. Haritada renkli rota olarak çizilir.")}
          </p>

          {/* ── Tarih Seçici ── */}
          <div className="haritaGecmisTarihSatir">
            <button
              className={"haritaGecmisTarihBtn" + (gecmisTarih === bugununTarihi() ? " aktif" : "")}
              onClick={() => gecmisTarihDegistir(bugununTarihi())}>
              {mt(lang, "Bugün")}
            </button>
            <button
              className={"haritaGecmisTarihBtn" + (gecmisTarih === dunununTarihi() ? " aktif" : "")}
              onClick={() => gecmisTarihDegistir(dunununTarihi())}>
              {mt(lang, "Dün")}
            </button>
            <input
              type="date"
              className="haritaGecmisTarihInput"
              value={gecmisTarih}
              max={bugununTarihi()}
              onChange={e => e.target.value && gecmisTarihDegistir(e.target.value)}
            />
          </div>

          {/* ── Üye seçici butonlar ── */}
          <div className="haritaGecmisUyeSatir">
            {gecmisGorunurUyeler.map((uye, i) => {
              const uid   = uye.uid || uye.id;
              const renk  = uyeRenk(uye, i); // avatar rengi kullan
              const aktif = gecmisUyeUid === uid;
              return (
                <button key={uid}
                  className={"haritaGecmisUyeBtn" + (aktif ? " aktif" : "")}
                  style={{ "--rota-renk": renk }}
                  onClick={() => aktif ? gecmisKapat() : gecmisYukle(uid)}>
                  <AvatarImg
                    avatar={uye.avatar || null}
                    renk={renk}
                    initials={initials(uye)}
                    size={24}
                    className="haritaGecmisUyeAvatarImg"
                  
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
                  <span>{uye.ad}</span>
                  {aktif && <span className="haritaGecmisUyeAktifDot" />}
                </button>
              );
            })}
          </div>

          {/* ── İçerik ── */}
          {gecmisUyeUid && (
            gecmisYukleniyor ? (
              <div className="haritaGecmisYukleniyor">
                <div className="haritaGecmisSpinner" />
                {mt(lang, "Yükleniyor")}
              </div>
            ) : konumGecmisi.length === 0 ? (
              <div className="haritaGecmisEmpty">
                <div style={{fontSize:28,marginBottom:6}}>🗺️</div>
                {mt(lang, "Bu tarih için konum kaydı yok. Konum paylaşımı açıkken ve uygulama arka planda iken hareket olduğunda otomatik kaydedilir. Son 15 günlük hareketleriniz kaydedilir.")}
              </div>
            ) : (
              <div className="haritaGecmisListeWrap">

                {/* Özet satırı */}
                <div className="haritaGecmisOzet">
                  <span className="haritaGecmisOzetDot" style={{background: gecmisRotaRengi(gecmisUyeUid)}} />
                  <span>{konumGecmisi.length} {mt(lang, "kayıt")} · {mt(lang, "Rota haritada gösterildi")}</span>
                </div>

                {/* Kayıt listesi */}
                <div className="haritaGecmisListe">
                  {[...konumGecmisi].reverse().map((n, i) => {
                    // Backend ISO string desteklenir (Firestore Timestamp artık kullanılmıyor)
                    const ts  = n.zaman?.toDate ? n.zaman.toDate()
                              : n.zaman ? new Date(n.zaman)
                              : new Date((n.zaman?.seconds || 0) * 1000 || Date.now());
                    const renk       = gecmisRotaRengi(gecmisUyeUid);
                    const gercekIdx  = konumGecmisi.length - 1 - i;
                    const aktif      = seciliRotaIdx === gercekIdx;
                    const ilk        = gercekIdx === 0;
                    const son        = gercekIdx === konumGecmisi.length - 1;
                    return (
                      <div
                        key={n.id || i}
                        className={"haritaGecmisItem" + (aktif ? " aktif" : "")}
                        style={{ "--rota-renk": renk }}
                        onClick={() => {
                          setSeciliRotaIdx(gercekIdx);
                          const lat = Number(n.lat), lng = Number(n.lng);
                          const seciliZoom = mapRef.current?.getZoom?.() || userZoomRef.current || 16;
                          userZoomRef.current = seciliZoom;
                          mapRef.current?.flyTo([lat, lng], seciliZoom, { duration: 0.7 });
                          haritaKartiniOrtayaKaydir();
                          // Seçili noktayı haritada küçük marker ile göster
                          if (mapRef.current?._renderer === "mapkit") {
                            mapRef.current.setHistorySelection?.(lat, lng, gecmisRotaRengi(gecmisUyeUid));
                          } else if (mapRef.current && window.L) {
                            const L = window.L;
                            const renk2 = gecmisRotaRengi(gecmisUyeUid);
                            // Önceki seçim markerını temizle
                            if (window._gecmisSecimMarker) {
                              window._gecmisSecimMarker.remove();
                              window._gecmisSecimMarker = null;
                            }
                            window._gecmisSecimMarker = L.circleMarker([lat, lng], {
                              radius: 7,
                              fillColor: renk2,
                              color: "#fff",
                              weight: 2.5,
                              opacity: 1,
                              fillOpacity: 1,
                            }).addTo(mapRef.current);
                          }
                        }}>
                        {/* Zaman çizelgesi çizgisi */}
                        <div className="haritaGecmisItemTimeline">
                          <div className={"haritaGecmisItemDot" + (ilk ? " ilk" : son ? " son" : "")}
                            style={{background: ilk ? "#10b981" : son ? renk : renk, opacity: ilk||son ? 1 : 0.6}} />
                          {gercekIdx < konumGecmisi.length - 1 && (
                            <div className="haritaGecmisItemCizgi" style={{background: renk}} />
                          )}
                        </div>

                        {/* İçerik */}
                        <div className="haritaGecmisItemIcerik">
                          <div className="haritaGecmisItemSaat">
                            {ts.toLocaleTimeString(lang === "en" ? "en-US" : "tr-TR", {hour:"2-digit", minute:"2-digit"})}
                            {ilk && <span className="haritaGecmisItemEtiket baslangic">{lang === "en" ? "Start" : "Başlangıç"}</span>}
                            {son && <span className="haritaGecmisItemEtiket son">{lang === "en" ? "Last" : "Son"}</span>}
                          </div>
                          <div className="haritaGecmisItemKoord">
                            {Number(n.lat).toFixed(5)}, {Number(n.lng).toFixed(5)}
                          </div>
                        </div>

                        {/* Ok ikonu */}
                        <div className="haritaGecmisItemOk">›</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Güvenli Alan Paneli */}
      {aktifPanel==="guvenli"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik">
            <span>🟢 {mt(lang, "Güvenli Alanlar")}</span>
            <button className="haritaYerEkleBtn" onClick={()=>setGuvenliAlanModal(true)}>{lang === "en" ? "+ Add" : "+ Ekle"}</button>
          </div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 12px",lineHeight:1.5}}>{mt(lang, "Belirlenen bölge dışına çıkınca anlık bildirim alın. Tüm aile üyelerini izler.")}</p>
          {guvenliAlanlar.length===0?(
            <div style={{textAlign:"center",padding:"20px 0",color:"var(--text-muted)",fontSize:13}}><div style={{fontSize:28,marginBottom:6}}>🟢</div>{mt(lang, "Henüz güvenli alan eklenmemiş.")}</div>
          ):(
            <div style={guvenliAlanlar.length > 3 ? ucKayitListeStyle : undefined}>{guvenliAlanlar.map(alan=>(
            <div key={alan.id} className="haritaGeofenceRow haritaTappableRow"
              onClick={()=>koordinataOdaklan(alan.lat,alan.lng,14,true)}>
              <div style={{width:12,height:12,borderRadius:"50%",background:"#10b981",flexShrink:0,border:"2px solid rgba(16,185,129,0.3)"}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{alan.ad}</div>
                <div style={{fontSize:11,color:"var(--text-secondary)"}}>
                  {alan.yaricap>=1000?`${alan.yaricap/1000} km`:`${alan.yaricap} m`} {mt(lang, "yarıçap")}
                   · {bolgeHedefMetni(alan, uyeler, lang)}
                </div>
              </div>
              <button onClick={(e)=>{e.stopPropagation();guvenliAlanSil(alan.id);}} className="haritaYerAkBtn sil">🗑️</button>
            </div>
          ))}
            </div>
          )}
        </div>
      )}

      {/* Hareket Analizi Paneli — geçmiş paneli ile uyumlu premium tasarım */}
      {aktifPanel==="analiz"&&(
        <div className="haritaPremiumPanel">
          <div className="haritaPremiumPanelBaslik">
            <span>📊 {mt(lang, "Hareket Analizi")}</span>
          </div>
          <p style={{fontSize:11,color:"var(--text-muted)",margin:"0 0 10px",lineHeight:1.5}}>
            {mt(lang, "Son 7 günlük hareket verisi. Üyeyi seçerek analizi görüntüleyin.")}
          </p>

          {/* Üye seçici — geçmiş paneli ile aynı tasarım */}
          <div className="haritaGecmisUyeSatir">
            {gecmisGorunurUyeler.map((uye, i) => {
              const uid   = uye.uid || uye.id;
              const renk  = uyeRenk(uye, i);
              const aktif = analizUyeUid === uid;
              return (
                <button key={uid}
                  className={"haritaGecmisUyeBtn" + (aktif ? " aktif" : "")}
                  style={{ "--rota-renk": renk }}
                  onClick={() => analizYukle(uid)}>
                  <AvatarImg
                    avatar={uye.avatar || null}
                    renk={renk}
                    initials={initials(uye)}
                    size={24}
                    className="haritaGecmisUyeAvatarImg"
                  
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
                  <span>{uye.ad}</span>
                  {aktif && <span className="haritaGecmisUyeAktifDot" />}
                </button>
              );
            })}
          </div>

          {/* İçerik */}
          {analizYukleniyor ? (
            <div className="haritaGecmisYukleniyor">
              <div className="haritaGecmisSpinner" />
              {mt(lang, "Analiz yükleniyor…")}
            </div>
          ) : analizUyeUid && hareketAnalizi.length > 0 ? (() => {
            const secilenUye   = gecmisGorunurUyeler.find(u => (u.uid||u.id) === analizUyeUid);
            const uyeIdx       = gecmisGorunurUyeler.findIndex(u => (u.uid||u.id) === analizUyeUid);
            const renk         = uyeRenk(secilenUye, uyeIdx);
            const toplamMesafe = hareketAnalizi.reduce((s, g) => s + g.toplamMesafe, 0);
            const aktifGun     = hareketAnalizi.filter(g => g.snapshotSayisi > 0).length;
            const toplamKayit  = hareketAnalizi.reduce((s, g) => s + g.snapshotSayisi, 0);
            const maxMesafe    = Math.max(...hareketAnalizi.map(g => g.toplamMesafe), 0.1);
            return (
              <div className="haritaAnalizIcerik">
                {/* Özet kartları */}
                <div className="haritaAnalizOzetSatir">
                  <div className="haritaAnalizOzetKart" style={{"--ozet-renk":"#6366f1"}}>
                    <div className="haritaAnalizOzetDeger">{toplamMesafe.toFixed(1)}</div>
                    <div className="haritaAnalizOzetBirim">km</div>
                    <div className="haritaAnalizOzetEtiket">{mt(lang, "km toplam")}</div>
                  </div>
                  <div className="haritaAnalizOzetKart" style={{"--ozet-renk":"#10b981"}}>
                    <div className="haritaAnalizOzetDeger">{aktifGun}</div>
                    <div className="haritaAnalizOzetBirim">{lang==="en"?"days":"gün"}</div>
                    <div className="haritaAnalizOzetEtiket">{mt(lang, "aktif gün")}</div>
                  </div>
                  <div className="haritaAnalizOzetKart" style={{"--ozet-renk":"#f59e0b"}}>
                    <div className="haritaAnalizOzetDeger">{toplamKayit}</div>
                    <div className="haritaAnalizOzetBirim">{lang==="en"?"pts":"kay."}</div>
                    <div className="haritaAnalizOzetEtiket">{mt(lang, "konum kaydı")}</div>
                  </div>
                </div>
                {/* Bar grafik */}
                <div className="haritaAnalizGrafik">
                  {hareketAnalizi.map((gun) => {
                    const barW    = maxMesafe > 0 ? (gun.toplamMesafe / maxMesafe * 100) : 0;
                    const tarihObj = new Date(gun.tarih + "T12:00:00");
                    const gunAdi   = tarihObj.toLocaleDateString(
                      lang==="en" ? "en-US" : "tr-TR",
                      { weekday:"short", day:"numeric", month:"short" }
                    );
                    const barRenk = barW > 60 ? renk : barW > 30 ? "#10b981" : "#94a3b8";
                    return (
                      <div key={gun.tarih} className="haritaAnalizGunSatir">
                        <div className="haritaAnalizGunAd">{gunAdi}</div>
                        <div className="haritaAnalizBarWrap">
                          <div className="haritaAnalizBar" style={{width:`${barW}%`,background:barRenk}} />
                          {gun.snapshotSayisi > 0 && (
                            <span className="haritaAnalizBarKayit">{gun.snapshotSayisi}</span>
                          )}
                        </div>
                        <div className="haritaAnalizGunMesafe">
                          {gun.toplamMesafe > 0 ? `${gun.toplamMesafe}km` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })() : analizUyeUid ? (
            <div className="haritaGecmisEmpty">
              <div style={{fontSize:28,marginBottom:6}}>📊</div>
              {lang==="en" ? "No movement data found for this member." : "Bu üye için hareket verisi bulunamadı."}
            </div>
          ) : null}
        </div>
      )}

      {/* Ortak Yerler */}
      <div className="haritaBolumRow" style={{marginTop:4}}>
        <span className="haritaBolumBaslik">{mt(lang, "Ortak Yerler")}</span>
        {yerEkleyebilir&&<button className="haritaYerEkleBtn" onClick={yeniYerModalAc}>+ {mt(lang, "Yeni Yer Ekle")}</button>}
      </div>

      {yerler.length===0?(
        <div className="emptyState" style={{padding:"32px 16px",textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>📍</div>
          <div>{mt(lang, "Henüz ortak yer eklenmemiş.")}</div>
          {yerEkleyebilir&&<button className="primaryButton" style={{marginTop:14}} onClick={yeniYerModalAc}>{mt(lang, "İlk Yeri Ekle")}</button>}
        </div>
      ):(
        <div className="haritaYerlerKart" style={yerler.length > 3 ? ortakYerListeStyle : undefined}>
          {gosterilecekYerler.map((yer)=>{
            const kat=kategoriData(yer.kategori), silebilir=tumYerleriSilebilir||(kendiYeriniSilebilir&&yer.ekleyenUid===user?.uid), koordinatVar=!!yer.lat&&!!yer.lng;
            return (
              <div key={yer.id} className={`haritaYerRow${koordinatVar?" tiklanabilir":""}`}
                onClick={koordinatVar?()=>yerHaritadaGoster(yer, true):undefined}
                style={koordinatVar?{cursor:"pointer"}:{}}>
                <div className="haritaYerIkon" style={{background:kat.bg}}>{kat.ikon}</div>
                <div className="haritaYerBilgi">
                  <div className="haritaYerAd">{yer.ad}{koordinatVar&&<span style={{fontSize:9,marginLeft:5,padding:"1px 5px",background:"rgba(99,102,241,0.12)",color:"var(--brand-1)",borderRadius:4,fontWeight:700}}>{mt(lang, "HARİTADA GÖR")}</span>}</div>
                  <div className="haritaYerAdres">{yer.adres||mt(lang, kat.label)}</div>
                  <div className="haritaYerEkleyen">{mt(lang, "Ekleyen:")} {yer.ekleyenAd||"—"}{yer.createdAt?.toDate?` • ${yer.createdAt.toDate().toLocaleDateString(lang === "en" ? "en-US" : "tr-TR")}`:""}</div>
                </div>
                <div className="haritaYerAksiyonlar" onClick={e=>e.stopPropagation()}>
                  {koordinatVar&&<button className="haritaYerAkBtn" title={mt(lang, "Haritada Göster")} onClick={()=>yerHaritadaGoster(yer, true)}>📍</button>}
                  <button className="haritaYerAkBtn" title={mt(lang, "Yol Tarifi")} onClick={()=>yolTarifi(yer)}>🗺️</button>
                  {silebilir&&<>
                    <button className="haritaYerAkBtn" title={mt(lang, "Düzenle")} onClick={()=>{setDuzenleYer(yer);setModalAcik(true);}}>✏️</button>
                    <button className="haritaYerAkBtn sil" title={mt(lang, "Sil")} onClick={()=>yerSil(yer.id)}>🗑️</button>
                  </>}
                </div>
              </div>
            );
          })}

        </div>
      )}

      {/* Modallar */}
      {modalAcik&&<YerModal lang={lang} mevcut={duzenleYer} onKaydet={yerKaydet} onHaritadanSec={setHaritaSecimModal} mevcutKonum={gecerliKonumSec(konumlar[user?.uid], konumlar[user?.uid]?.paylasimAcik !== false ? konumlar[user?.uid] : null)} onKapat={()=>{setModalAcik(false);setDuzenleYer(null);}} uyeler={uyeler}/>}
      {guvenliAlanModal&&<GuvenliAlanModal lang={lang} onKaydet={guvenliAlanEkle} onHaritadanSec={setHaritaSecimModal} onKapat={()=>setGuvenliAlanModal(false)} mevcutKonum={konumlar[user?.uid]} uyeler={uyeler}/>}
      {geofenceModal&&<GeofenceModal lang={lang} onKaydet={geofenceEkle} onHaritadanSec={setHaritaSecimModal} onKapat={()=>setGeofenceModal(false)} mevcutKonum={konumlar[user?.uid]} uyeler={uyeler}/>}
      {bulusmaModal&&<BulusmaModal lang={lang} onKaydet={bulusmaEkle} onHaritadanSec={setHaritaSecimModal} mevcutKonum={konumlar[user?.uid]} onKapat={()=>setBulusmaModal(false)} yerler={yerler.filter(y=>y.lat&&y.lng)}/>}
      {haritaSecimModal&&<HaritadanKonumSecModal lang={lang} {...haritaSecimModal} onKapat={()=>setHaritaSecimModal(null)} />}
    </div>
  );
}

export default HaritaPage;
